<template>
  <v-dialog
    :value="value"
    width="1350"
    max-width="95vw"
    scrollable
    :fullscreen="isDialogFullscreen"
    @input="handleInput"
    :content-class="dialogContentClass">
    <v-card
      class="glass-card-theme elevation-24 d-flex flex-column overflow-hidden"
      :class="{
        'cockpit-fullscreen-card': isDialogFullscreen,
        'cockpit-finder-expanded-card': isFinderExpanded,
      }"
      :style="cardStyle">
      <div
        v-if="!showFinder"
        class="appointment-header relative flex-shrink-0 header-transition"
        :class="{ 'appointment-header--customer': activeCustomer, 'appointment-header--simple': !activeCustomer }"
        :style="headerStyle">
        <div class="appointment-header-main relative z-index-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center flex-grow-1" style="min-width: 0">
            <v-menu
              v-if="activeCustomer"
              v-model="showCustomerImagePreview"
              :open-on-hover="$vuetify.breakpoint.mdAndUp"
              :disabled="!mergedCustomer || !hasRealCustomerPicture(mergedCustomer)"
              :close-on-content-click="false"
              offset-y
              :nudge-bottom="8"
              transition="scale-transition"
              content-class="customer-image-preview-menu">
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="customer-avatar-trigger mr-3 flex-shrink-0"
                  :class="{ 'customer-avatar-trigger--with-picture': mergedCustomer && hasRealCustomerPicture(mergedCustomer) }"
                  :aria-label="mergedCustomer && hasRealCustomerPicture(mergedCustomer) ? 'Profilbild vergrössern' : 'Kundenprofil anzeigen'"
                  v-bind="attrs"
                  v-on="$vuetify.breakpoint.mdAndUp ? on : {}"
                  @click.stop.prevent="openCustomerImagePreview">
                  <v-avatar :size="$vuetify.breakpoint.xsOnly ? 46 : 54" class="customer-header-avatar">
                    <img
                      v-if="mergedCustomer && hasRealCustomerPicture(mergedCustomer)"
                      :src="getPicture(mergedCustomer)"
                      :alt="`${activeCustomer.vorname || ''} ${activeCustomer.nachname || ''}`.trim()"
                      class="customer-header-avatar-image" />
                    <div
                      v-else
                      class="customer-header-initials d-flex align-center justify-center white--text font-weight-black"
                      :style="`background: linear-gradient(135deg, ${currentTheme.color || '#4CAF50'} 0%, ${
                        currentTheme.color ? currentTheme.color + 'aa' : '#81C784'
                      } 100%);`">
                      {{ getCustomerInitials(activeCustomer, activeNoAccountData) }}
                    </div>
                  </v-avatar>
                  <span class="customer-avatar-type-badge" :style="`background: ${currentTheme.color}`">
                    <v-icon color="white" size="13">{{ appointmentTypeMeta.icon }}</v-icon>
                  </span>
                  <span v-if="mergedCustomer && hasRealCustomerPicture(mergedCustomer)" class="customer-avatar-zoom-badge" aria-hidden="true">
                    <v-icon color="white" size="11">mdi-arrow-expand</v-icon>
                  </span>
                </button>
              </template>

              <v-card
                class="customer-image-preview-card overflow-hidden"
                :style="[
                  themeVars,
                  {
                    background: isDarkMode ? '#222225' : '#ffffff',
                    color: isDarkMode ? '#ffffff' : '#121212',
                    borderColor: hexToRgba(currentTheme.color, 0.35),
                  },
                ]">
                <v-img
                  v-if="mergedCustomer && hasRealCustomerPicture(mergedCustomer)"
                  :src="getPicture(mergedCustomer)"
                  :alt="`${activeCustomer.vorname || ''} ${activeCustomer.nachname || ''}`.trim()"
                  :width="$vuetify.breakpoint.xsOnly ? 244 : 284"
                  :height="$vuetify.breakpoint.xsOnly ? 244 : 284"
                  aspect-ratio="1"
                  cover>
                  <template v-slot:placeholder>
                    <div class="fill-height d-flex align-center justify-center">
                      <v-progress-circular indeterminate :color="currentTheme.color" size="28"></v-progress-circular>
                    </div>
                  </template>
                </v-img>
                <div
                  v-else
                  class="customer-image-preview-empty d-flex flex-column align-center justify-center"
                  :style="`background: linear-gradient(145deg, ${hexToRgba(currentTheme.color, 0.2)}, ${hexToRgba(currentTheme.color, 0.05)})`">
                  <div class="customer-image-preview-initials" :style="`background: ${currentTheme.color}`">
                    {{ getCustomerInitials(activeCustomer, activeNoAccountData) }}
                  </div>
                  <div class="mt-3 font-weight-bold">Kein Profilbild hinterlegt</div>
                </div>
                <div class="customer-image-preview-caption">
                  <div class="font-weight-black text-truncate">{{ activeCustomer.vorname }} {{ activeCustomer.nachname }}</div>
                  <div class="customer-image-preview-hint">
                    {{ mergedCustomer && hasRealCustomerPicture(mergedCustomer) ? "Profilbild" : "Kundenprofil" }}
                  </div>
                </div>
              </v-card>
            </v-menu>

            <v-sheet
              v-else
              :height="$vuetify.breakpoint.xsOnly ? 42 : 48"
              :width="$vuetify.breakpoint.xsOnly ? 42 : 48"
              class="appointment-header-icon d-flex align-center justify-center mr-3 flex-shrink-0"
              :style="`background: ${hexToRgba(currentTheme.color, isDarkMode ? 0.2 : 0.11)}; border-color: ${hexToRgba(currentTheme.color, 0.34)};`">
              <v-icon :size="$vuetify.breakpoint.xsOnly ? 22 : 25" :color="currentTheme.color">{{ appointmentTypeMeta.icon }}</v-icon>
            </v-sheet>

            <div class="appointment-header-copy">
              <div class="appointment-header-kicker" :style="`color: ${currentTheme.color}`">
                {{ appointmentTypeMeta.category }}
              </div>

              <div class="appointment-header-title-row" v-if="activeCustomer">
                <h2 class="appointment-header-title dynamic-text text-truncate">{{ activeCustomer.vorname }} {{ activeCustomer.nachname }}</h2>
              </div>
              <div class="appointment-header-title-row" v-else-if="activeNoAccountData && activeNoAccountData.firstName">
                <h2 class="appointment-header-title dynamic-text text-truncate">
                  {{ activeNoAccountData.firstName }} {{ activeNoAccountData.lastName }}
                </h2>
                <span class="appointment-header-badge">Ohne Account</span>
              </div>
              <h2 v-else class="appointment-header-title dynamic-text text-truncate">
                {{ localData.isPause ? "Pause bearbeiten" : localData.title || "Neuer Termin" }}
              </h2>

              <div class="appointment-header-meta">
                <span class="appointment-header-meta-item">
                  <v-icon x-small>mdi-clock-outline</v-icon>
                  {{ appointmentTimeSummary }}
                </span>
                <span v-if="appointmentEmployeeLabel" class="appointment-header-meta-item">
                  <v-icon x-small>mdi-account-outline</v-icon>
                  {{ appointmentEmployeeLabel }}
                </span>
                <span
                  v-if="tacBillingMeta"
                  class="appointment-header-meta-item"
                  :style="{ color: tacBillingMeta.color }"
                  :title="tacBillingMeta.title">
                  <v-icon x-small :color="tacBillingMeta.color">mdi-ticket-confirmation-outline</v-icon>
                  {{ tacBillingMeta.text }}
                </span>
                <span v-if="activeCustomerRating" class="appointment-header-meta-item appointment-rating-meta" :title="activeCustomerRatingTooltip">
                  <v-icon x-small color="amber">mdi-star</v-icon>
                  {{ activeCustomerRating.stars }}/5 Kundenbewertung
                </span>
              </div>
            </div>
          </div>

          <div class="appointment-header-tools d-flex align-center flex-shrink-0 ml-3">
            <div v-if="activeCustomer" class="appointment-header-quickfacts d-flex align-center mr-2">
              <v-tooltip v-if="anamneseInfo.exists" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="customer-quickfact customer-quickfact--status"
                    :style="`--quickfact-color: ${anamneseInfo.isNew ? '#00963f' : '#FF9800'}`"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon size="16" :color="anamneseInfo.isNew ? '#00963f' : '#FF9800'">mdi-clipboard-pulse</v-icon>
                    <span class="customer-quickfact-value">
                      {{ anamneseInfo.isNew ? "Check" : "Altbestand" }} · {{ formatDate(anamneseInfo.date) }}
                    </span>
                  </div>
                </template>
                <span>{{ anamneseInfo.isNew ? "Aktuelle Anamnese" : "Anamnese-Altbestand" }} vom {{ formatDate(anamneseInfo.date) }}</span>
              </v-tooltip>

              <v-tooltip v-if="customerDetails && customerDetails.phone" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="customer-quickfact"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="copyToClipboard(customerDetails.phone, 'Telefonnummer')">
                    <v-icon size="16">mdi-phone-outline</v-icon>
                    <span class="customer-quickfact-value customer-quickfact-value--phone">{{ customerDetails.phone }}</span>
                  </button>
                </template>
                <span>{{ customerDetails.phone }} · Klicken zum Kopieren</span>
              </v-tooltip>

              <v-tooltip v-if="activeCustomer.email" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="customer-quickfact"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="copyToClipboard(activeCustomer.email, 'E-Mail')">
                    <v-icon size="16">mdi-email-outline</v-icon>
                    <span class="customer-quickfact-value customer-quickfact-value--email">{{ activeCustomer.email }}</span>
                  </button>
                </template>
                <span>{{ activeCustomer.email }} · Klicken zum Kopieren</span>
              </v-tooltip>
            </div>

            <div class="appointment-header-actions d-flex align-center flex-shrink-0">
              <v-btn
                v-if="customerDetails"
                icon
                :color="isDarkMode ? 'white' : 'black'"
                class="glass-btn appointment-header-action mr-1"
                @click="showContactDialog = true"
                title="Kunden kontaktieren">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-message-badge-outline</v-icon>
              </v-btn>

              <v-btn
                v-if="activeCustomer"
                icon
                :color="isDarkMode ? 'white' : 'black'"
                class="glass-btn appointment-header-action appointment-header-action--desktop mr-1"
                @click="openCustomerDashboard"
                title="Kundenakte öffnen">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-parking</v-icon>
              </v-btn>
              <v-btn
                v-if="activeCustomer"
                icon
                :color="isDarkMode ? 'white' : 'black'"
                class="glass-btn appointment-header-action appointment-header-action--desktop mr-1"
                :loading="refreshingCustomer"
                title="Kundendaten aktualisieren"
                @click="refreshCustomer">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                v-if="activeCustomer && isRealAppointment"
                icon
                :color="customerAssignmentChanged ? currentTheme.color : isDarkMode ? 'white' : 'black'"
                class="glass-btn appointment-header-action mr-1"
                title="Kundenzuordnung ändern"
                @click="openCustomerChange">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-account-switch-outline</v-icon>
              </v-btn>
              <v-btn
                v-if="$vuetify.breakpoint.mdAndDown && activeCustomer"
                icon
                :color="showMobileDetails ? currentTheme.color || 'primary' : isDarkMode ? 'white' : 'black'"
                class="glass-btn appointment-header-action mr-1"
                @click="showMobileDetails = !showMobileDetails"
                title="Termindetails bearbeiten">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-clipboard-edit-outline</v-icon>
              </v-btn>
              <v-btn icon :color="isDarkMode ? 'white' : 'black'" class="glass-btn appointment-header-action" @click="closeDialog">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div class="appointment-header-tabs relative z-index-2" v-if="activeCustomer && !showFinder">
          <v-tabs v-model="activeTab" height="40" background-color="transparent" :dark="isDarkMode" :slider-color="currentTheme.color" show-arrows>
            <v-tab class="font-weight-bold text-body-2"><v-icon left small>mdi-lightning-bolt</v-icon> Übersicht</v-tab>
            <v-tab class="font-weight-bold text-body-2"><v-icon left small>mdi-folder-open</v-icon> Akte & Pläne</v-tab>
            <v-tab class="font-weight-bold text-body-2" @click="loadHistory"><v-icon left small>mdi-account-clock</v-icon> Historie</v-tab>
          </v-tabs>
        </div>

        <!-- Customer Tab-Switch für 2 Personen -->
        <div v-if="hasTwoCustomers && !showFinder" class="appointment-customer-switch relative z-index-2">
          <div
            class="d-flex rounded-lg"
            :style="`background: ${hexToRgba(currentTheme.color, 0.06)}; border: 1px solid ${hexToRgba(currentTheme.color, 0.12)}; overflow: hidden`">
            <div
              class="flex-grow-1 d-flex align-center justify-center pa-1 cursor-pointer"
              :style="activeCustomerIndex === 0 ? `background: ${hexToRgba(currentTheme.color, 0.15)}; font-weight: 700` : 'opacity: 0.5'"
              @click="switchCustomer(0)">
              <v-icon x-small :color="currentTheme.color" class="mr-1">mdi-account</v-icon>
              <span class="caption dynamic-text">{{ customer1Label }}</span>
            </div>
            <div style="width: 1px; background: rgba(var(--rgb-color), 0.1)"></div>
            <div
              class="flex-grow-1 d-flex align-center justify-center pa-1 cursor-pointer"
              :style="activeCustomerIndex === 1 ? `background: ${hexToRgba(currentTheme.color, 0.15)}; font-weight: 700` : 'opacity: 0.5'"
              @click="switchCustomer(1)">
              <v-icon x-small :color="currentTheme.color" class="mr-1">mdi-account</v-icon>
              <span class="caption dynamic-text">{{ customer2Label }}</span>
            </div>
          </div>
        </div>
      </div>

      <v-alert
        v-if="localData.orphanedEmployeeAssignment && !showFinder"
        dense
        text
        border="left"
        type="warning"
        icon="mdi-account-alert-outline"
        class="mx-3 mt-3 mb-0 flex-shrink-0">
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <div class="flex-grow-1" style="min-width: 220px">
            <div class="font-weight-black">Dieser Termin muss neu zugewiesen werden</div>
            <div class="text-body-2 mt-1">
              {{ reassignmentReasonText }} Der Termin wird deshalb vorübergehend unter MA-Info angezeigt.
            </div>
          </div>
          <v-btn small text color="warning" class="font-weight-bold text-none" @click="openAppointmentDateFinder">
            <v-icon left small>mdi-account-arrow-right-outline</v-icon>
            Jetzt neu zuweisen
          </v-btn>
        </div>
      </v-alert>

      <v-card-text
        class="pt-3 pt-md-4 pb-0 flex-grow-1"
        :class="{
          'overflow-hidden': $vuetify.breakpoint.mdAndUp,
          'overflow-y-auto px-4': $vuetify.breakpoint.smAndDown,
          'cockpit-finder-body': showFinder && isRealAppointment,
        }">
        <v-row class="fill-height ma-0" :class="{ 'cockpit-finder-row': showFinder && isRealAppointment }">
          <v-col
            v-if="showLeftColumn"
            cols="12"
            :md="showDetailsColumn ? 7 : 12"
            :lg="showDetailsColumn ? 7 : 12"
            class="order-1 order-md-1 d-flex flex-column"
            :class="{
              'custom-scrollbar pr-4 pb-4': $vuetify.breakpoint.mdAndUp,
              'px-0 pt-3 pb-8': $vuetify.breakpoint.smAndDown,
              'cockpit-finder-column': showFinder && isRealAppointment,
            }"
            :style="$vuetify.breakpoint.mdAndUp ? (showFinder ? 'height: 100%; overflow-y: hidden' : 'height: 100%; overflow-y: auto') : ''">
            <div v-if="loading && activeCustomer && !showFinder" class="py-4">
              <v-skeleton-loader type="image, article" :dark="isDarkMode" class="transparent"></v-skeleton-loader>
            </div>

            <!-- TERMIN-FINDER OVERLAY VIEW (when showFinder is true) -->
            <div v-else-if="showFinder && isRealAppointment" class="finder-fullscreen-view d-flex flex-column h-100">
              <div class="finder-fullscreen-toolbar flex-shrink-0">
                <div class="finder-fullscreen-title">
                  <span class="finder-fullscreen-icon"><v-icon color="white" size="21">mdi-calendar-search</v-icon></span>
                  <div>
                    <strong class="dynamic-text">Freien Termin finden</strong>
                    <small> {{ localData.title || "Termin" }} · {{ Number(localData.duration || 60) }} Min. · Bestätigung speichert direkt </small>
                  </div>
                </div>
                <div class="finder-fullscreen-actions">
                  <v-btn text small class="text-none font-weight-bold" :disabled="finderSaving" @click="showFinder = false">
                    <v-icon left small>mdi-arrow-left</v-icon>
                    Termindetails
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="finderSaving"
                    :dark="isDarkMode"
                    :light="!isDarkMode"
                    aria-label="Dialog schliessen"
                    @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>

              <work-calendar-appointment-finder
                ref="appointmentFinder"
                class="flex-grow-1 overflow-hidden"
                :employees="eligibleFinderEmployees"
                :date="localData.date"
                @update:date="onFinderUpdate('date', $event)"
                :start="localData.start"
                @update:start="onFinderUpdate('start', $event)"
                :col-id="localData.colId"
                @update:col-id="onFinderUpdate('colId', $event)"
                :duration="Number(localData.duration || 60)"
                :is-dark-mode="isDarkMode"
                :exclude-appointment-id="localData.id"
                :saving="finderSaving"
                confirm-label="Hierhin verschieben & speichern"
                hide-title
                @select-slot="onFinderSelectSlot"
                @clear-selection="onFinderClearSelection"
                @select-and-close="onFinderSelectAndClose" />
            </div>

            <div v-else-if="activeTab === 0 && (activeCustomer || localData.specialType === 'new_contract' || localData.isPause || hasNoAccountData)">
              <!-- PRIORITÄT 1: noAccountData → Account erstellen (vor allem anderen!) -->
              <div v-if="hasNoAccountData && !activeCustomer" class="mb-5">
                <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-4">
                  <v-icon :color="currentTheme.color" class="mr-2" size="28">mdi-account-off</v-icon>
                  Gast ohne Account
                </div>

                <div class="glass-panel-light rounded-lg pa-4 mb-4">
                  <div class="caption grey--text text-uppercase font-weight-bold mb-3">Erfasste Kontaktdaten</div>
                  <div class="d-flex align-center mb-2">
                    <v-icon small class="mr-2" :color="currentTheme.color">mdi-account</v-icon>
                    <span class="font-weight-bold dynamic-text">{{ activeNoAccountData.firstName }} {{ activeNoAccountData.lastName }}</span>
                  </div>
                  <div class="d-flex align-center mb-2" v-if="activeNoAccountData.phone">
                    <v-icon small class="mr-2" :color="currentTheme.color">mdi-phone</v-icon>
                    <span class="dynamic-text">{{ activeNoAccountData.phone }}</span>
                  </div>
                  <div class="d-flex align-center" v-if="activeNoAccountData.email">
                    <v-icon small class="mr-2" :color="currentTheme.color">mdi-email</v-icon>
                    <span class="dynamic-text">{{ activeNoAccountData.email }}</span>
                  </div>
                </div>

                <div v-if="!showSearchExistingCustomer">
                  <v-btn
                    block
                    x-large
                    :color="currentTheme.color"
                    class="white--text font-weight-bold rounded-lg mb-4"
                    @click="showRegisterDialog = true">
                    <v-icon left>mdi-account-plus</v-icon> Jetzt Account erstellen
                  </v-btn>

                  <v-btn
                    block
                    outlined
                    :color="currentTheme.color"
                    class="font-weight-bold rounded-lg mb-4"
                    @click="showSearchExistingCustomer = true"
                    :style="isDarkMode ? 'border-color: rgba(255,255,255,0.15) !important;' : ''">
                    <v-icon left>mdi-link-variant</v-icon> Mit bestehendem Account verknüpfen
                  </v-btn>

                  <div class="caption grey--text text-center">Nach der Erstellung werden die Kundendaten automatisch verknüpft.</div>
                </div>

                <div v-else class="glass-panel-light rounded-lg pa-4 mb-4" style="position: relative; z-index: 1000">
                  <div class="caption grey--text text-uppercase font-weight-bold mb-2">Bestehenden Account suchen & verknüpfen</div>
                  <user-search
                    @update="assignExistingCustomer"
                    return-user
                    ignore-rights
                    label="Kunde suchen..."
                    :dark="isDarkMode"
                    outlined
                    dense
                    :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
                  <div class="d-flex justify-end mt-2">
                    <v-btn text small @click="showSearchExistingCustomer = false" style="text-transform: none" class="grey--text">Abbrechen</v-btn>
                  </div>
                </div>
              </div>

              <!-- PRIORITÄT 2: Standard-Termin ohne Fachvorlage → hochwertiges Kunden-Cockpit -->
              <section
                v-else-if="isStandardAppointment && activeCustomer"
                class="standard-appointment-hub mb-6"
                :style="{
                  '--appointment-accent': currentTheme.color,
                  '--appointment-accent-soft': hexToRgba(currentTheme.color, 0.12),
                  '--appointment-accent-border': hexToRgba(currentTheme.color, 0.28),
                }">
                <header class="standard-appointment-hub__header">
                  <div class="standard-appointment-hub__identity">
                    <span class="standard-appointment-hub__icon">
                      <v-icon :color="currentTheme.color">mdi-account-details-outline</v-icon>
                    </span>
                    <div class="standard-appointment-hub__copy">
                      <div class="standard-appointment-hub__eyebrow">Individueller Kundentermin</div>
                      <h2 class="dynamic-text">{{ localData.title || "Kunden-Cockpit" }}</h2>
                      <p>Gesundheit, Trainingsplanung und Vertrag direkt aus dem Termin bearbeiten.</p>
                    </div>
                  </div>
                  <span class="standard-appointment-hub__badge">
                    <v-icon x-small>mdi-lightning-bolt</v-icon>
                    3 Direktzugriffe
                  </span>
                </header>

                <div class="standard-appointment-actions">
                  <button
                    v-for="action in quickActions"
                    :key="action.key"
                    type="button"
                    class="standard-appointment-action"
                    :class="{
                      'standard-appointment-action--ready': action.ready,
                      'standard-appointment-action--loading': action.loading,
                    }"
                    :style="{
                      '--quick-action-color': action.color,
                      '--quick-action-soft': hexToRgba(action.color, 0.12),
                      '--quick-action-border': hexToRgba(action.color, 0.28),
                      '--quick-action-status': action.statusColor,
                      '--quick-action-status-soft': hexToRgba(action.statusColor, 0.12),
                    }"
                    :disabled="action.disabled"
                    :aria-label="action.text + ': ' + action.cta"
                    @click="executeAction(action.type)">
                    <span class="standard-appointment-action__topline">
                      <span class="standard-appointment-action__icon">
                        <v-progress-circular
                          v-if="action.loading"
                          indeterminate
                          :size="24"
                          :width="2"
                          :color="action.color"></v-progress-circular>
                        <v-icon v-else :color="action.color" size="23">{{ action.icon }}</v-icon>
                      </span>
                      <span class="standard-appointment-action__status">
                        <span class="standard-appointment-action__status-dot"></span>
                        {{ action.statusLabel }}
                      </span>
                    </span>

                    <span class="standard-appointment-action__copy">
                      <small>{{ action.eyebrow }}</small>
                      <strong class="dynamic-text">{{ action.text }}</strong>
                      <span>{{ action.subtitle }}</span>
                    </span>

                    <span class="standard-appointment-action__footer">
                      <span>{{ action.loading ? "Wird geladen …" : action.cta }}</span>
                      <v-icon :color="action.color" size="17">mdi-arrow-right</v-icon>
                    </span>
                  </button>
                </div>

                <div class="standard-appointment-hub__context">
                  <v-icon x-small :color="currentTheme.color">mdi-link-variant</v-icon>
                  Kunde, Termin und Datum werden beim Öffnen automatisch mitgenommen.
                </div>
              </section>

              <div
                v-else-if="activeCustomer || localData.specialType === 'new_contract' || localData.isPause"
                class="mb-6 rounded-xl elevation-5"
                :style="`background: rgba(var(--rgb-color),0.02); border: 1px solid rgba(var(--rgb-color),0.05); border-left: 4px solid ${currentTheme.color}`">
                <div class="pa-4 pa-md-6">
                  <template v-if="showTrainingFlow">
                    <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-4">{{ currentTheme.title }} Ablauf</div>

                    <div class="d-flex align-center justify-space-between mb-4 p-3 rounded-lg" style="background: rgba(var(--rgb-color), 0.05)">
                      <div class="d-flex align-center">
                        <v-icon :color="anamneseInfo.exists ? 'primary' : 'grey'" class="mr-3">mdi-clipboard-pulse</v-icon>
                        <div>
                          <div class="font-weight-bold dynamic-text">1. Anamnese</div>
                          <div class="caption grey--text">{{ anamneseInfo.exists ? "Vorhanden" : "Muss noch erstellt werden" }}</div>
                        </div>
                      </div>
                      <v-btn small :color="currentTheme.color" class="white--text font-weight-bold" @click="executeAction('anamnese')">
                        <v-icon x-small>{{ anamneseInfo.exists ? "mdi-pencil" : "mdi-plus" }}</v-icon>
                      </v-btn>
                    </div>

                    <div class="d-flex align-center justify-space-between p-3 rounded-lg" style="background: rgba(var(--rgb-color), 0.05)">
                      <div class="d-flex align-center">
                        <v-icon :color="currentActivePlan ? 'primary' : 'grey'" class="mr-3">mdi-dumbbell</v-icon>
                        <div>
                          <div class="font-weight-bold dynamic-text">2. Trainingsplan</div>
                          <div class="caption grey--text">{{ currentActivePlan ? "Plan vorhanden" : "Benötigt einen Plan" }}</div>
                        </div>
                      </div>
                      <div class="d-flex gap-2">
                        <v-btn
                          v-if="currentActivePlan"
                          small
                          :color="isDarkMode ? 'white' : 'black'"
                          :class="isDarkMode ? 'black--text' : 'white--text'"
                          @click="executeAction('workout_edit')">
                          <v-icon x-small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn small :color="currentTheme.color" class="white--text font-weight-bold" @click="executeAction('workout_new')">
                          <v-icon x-small>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>

                  <template v-else-if="localData.specialType === 'workout'">
                    <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-2">Trainingsplan Management</div>
                    <div class="grey--text mb-4">Verwalte hier die Trainingspläne des Kunden.</div>

                    <div v-if="loadingWorkouts" class="pa-4 text-center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <template v-else>
                      <div
                        v-if="currentActivePlan"
                        class="pa-3 rounded-lg mb-4"
                        style="background: rgba(var(--rgb-color), 0.1); border: 1px solid rgba(var(--rgb-color), 0.1)">
                        <div class="caption dynamic-text opacity-60 mb-1">Aktueller Plan</div>
                        <div class="font-weight-bold dynamic-text">{{ currentActivePlan.title }}</div>
                        <div class="mt-2 d-flex flex-wrap gap-1" v-if="currentActivePlan.splitDays && currentActivePlan.splitDays.length > 0">
                          <v-chip
                            v-for="(day, idx) in currentActivePlan.splitDays"
                            :key="idx"
                            x-small
                            outlined
                            color="primary"
                            class="font-weight-bold">
                            {{ day }}
                          </v-chip>
                        </div>
                      </div>
                      <div
                        v-else-if="allWorkouts.length > 0"
                        class="pa-3 rounded-lg mb-4 opacity-70"
                        style="background: rgba(var(--rgb-color), 0.05); border: 1px dashed rgba(var(--rgb-color), 0.1)">
                        <div class="caption grey--text mb-1">Letzter Plan (Archiviert)</div>
                        <div class="font-weight-bold grey--text">{{ allWorkouts[0].title }}</div>
                        <div class="caption grey--text mt-1">Dieser Plan ist archiviert und kann nicht mehr bearbeitet werden.</div>
                      </div>

                      <div class="d-flex flex-column flex-sm-row gap-3">
                        <v-btn
                          v-if="currentActivePlan"
                          flex-grow-1
                          x-large
                          :color="isDarkMode ? 'white' : 'black'"
                          class="font-weight-bold"
                          :class="isDarkMode ? 'black--text' : 'white--text'"
                          @click="executeAction('workout_edit')">
                          <v-icon left>mdi-file-document-edit</v-icon> Bearbeiten
                        </v-btn>
                        <v-btn
                          flex-grow-1
                          x-large
                          :color="currentTheme.color"
                          class="white--text font-weight-bold"
                          @click="executeAction('workout_new')">
                          <v-icon left>mdi-plus</v-icon> Neuer Plan
                        </v-btn>
                      </div>
                    </template>
                  </template>

                  <template v-else-if="localData.specialType === 'trainingplan'">
                    <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-2">Trainingsplan erstellen</div>
                    <div class="grey--text mb-4">Erstelle oder bearbeite den Trainingsplan des Kunden.</div>

                    <div v-if="loadingWorkouts" class="pa-4 text-center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <template v-else>
                      <div
                        v-if="currentActivePlan"
                        class="pa-4 rounded-lg mb-4"
                        style="background: rgba(var(--rgb-color), 0.08); border: 1px solid rgba(var(--rgb-color), 0.1)">
                        <div class="d-flex align-center mb-2">
                          <v-icon small :color="currentTheme.color" class="mr-2">mdi-file-document-check</v-icon>
                          <div class="caption dynamic-text font-weight-bold text-uppercase">Aktueller Plan</div>
                        </div>
                        <div class="font-weight-bold dynamic-text text-h6 mb-2">{{ currentActivePlan.title }}</div>
                        <div class="d-flex flex-wrap gap-2 mb-3" v-if="currentActivePlan.splitDays && currentActivePlan.splitDays.length > 0">
                          <v-chip
                            v-for="(day, idx) in currentActivePlan.splitDays"
                            :key="idx"
                            small
                            :color="currentTheme.color"
                            text-color="white"
                            class="font-weight-bold">
                            <v-icon left x-small>mdi-arm-flex</v-icon>
                            {{ day }}
                          </v-chip>
                        </div>
                        <div class="caption" style="color: rgba(var(--rgb-color), 0.5)">
                          <v-icon x-small class="mr-1" style="color: rgba(var(--rgb-color), 0.4)">mdi-account</v-icon>
                          {{ currentActivePlan.instructor }} · {{ formatDate(currentActivePlan.createdDate) }}
                        </div>
                      </div>

                      <!-- Alte Pläne -->
                      <div v-if="allWorkouts.length > 1" class="mb-4">
                        <div class="caption font-weight-bold text-uppercase dynamic-text mb-2" style="opacity: 0.5">
                          <v-icon x-small class="mr-1" style="opacity: 0.5">mdi-history</v-icon> Archiv ({{ allWorkouts.length - 1 }})
                        </div>
                        <div
                          v-for="plan in allWorkouts.slice(1, 4)"
                          :key="plan.id"
                          class="d-flex align-center pa-2 rounded mb-1 glass-panel-light"
                          style="cursor: pointer"
                          @click="viewDocument('workout', plan)">
                          <v-icon x-small class="mr-2" style="opacity: 0.4">{{ plan.type === "old" ? "mdi-archive" : "mdi-file-document" }}</v-icon>
                          <span class="caption dynamic-text text-truncate" style="opacity: 0.6">{{ plan.title }}</span>
                          <v-spacer></v-spacer>
                          <span class="caption" style="opacity: 0.3">{{ formatDate(plan.createdDate) }}</span>
                        </div>
                      </div>

                      <div v-else-if="allWorkouts.length === 0" class="pa-4 text-center mb-4 glass-panel-light rounded-lg">
                        <v-icon size="48" style="opacity: 0.2" class="mb-2">mdi-clipboard-text-off</v-icon>
                        <div class="caption dynamic-text" style="opacity: 0.4">Noch kein Trainingsplan vorhanden</div>
                      </div>

                      <div class="d-flex flex-column flex-sm-row gap-3">
                        <v-btn
                          v-if="currentActivePlan"
                          flex-grow-1
                          x-large
                          :color="isDarkMode ? 'white' : 'black'"
                          class="font-weight-bold"
                          :class="isDarkMode ? 'black--text' : 'white--text'"
                          @click="executeAction('workout_edit')">
                          <v-icon left>mdi-file-document-edit</v-icon> Bearbeiten
                        </v-btn>
                        <v-btn
                          flex-grow-1
                          x-large
                          :color="currentTheme.color"
                          class="white--text font-weight-bold"
                          @click="executeAction('workout_new')">
                          <v-icon left>mdi-plus</v-icon> Neuer Plan
                        </v-btn>
                      </div>
                    </template>
                  </template>

                  <template v-else-if="localData.specialType === 'anamnese'">
                    <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-2">Anamnese / Checkup</div>
                    <div class="grey--text mb-4">Erfasse oder bearbeite die gesundheitlichen und sportlichen Daten des Kunden.</div>

                    <div class="pa-3 rounded-lg mb-4" style="background: rgba(var(--rgb-color), 0.1); border: 1px solid rgba(var(--rgb-color), 0.1)">
                      <div class="d-flex align-center">
                        <v-icon x-small :color="isDarkMode ? 'white' : 'black'" class="mr-2">mdi-history</v-icon>
                        <span class="caption dynamic-text" v-if="anamneseInfo.exists">Letzte Prüfung: {{ formatDate(anamneseInfo.date) }}</span>
                        <span class="caption dynamic-text font-weight-bold" v-else>Noch keine Daten vorhanden</span>
                      </div>
                    </div>

                    <div v-if="anamneseInfo.exists" class="d-flex flex-column flex-sm-row gap-3">
                      <v-btn
                        flex-grow-1
                        x-large
                        :color="isDarkMode ? 'white' : 'black'"
                        class="font-weight-bold rounded-lg"
                        :class="isDarkMode ? 'black--text' : 'white--text'"
                        @click="executeAction('anamnese')">
                        <v-icon left>mdi-file-document-edit</v-icon> Bearbeiten
                      </v-btn>
                      <v-btn
                        flex-grow-1
                        x-large
                        :color="currentTheme.color"
                        class="white--text font-weight-bold rounded-lg"
                        @click="executeAction('anamnese_new')">
                        <v-icon left>mdi-plus</v-icon> Neue Anamnese
                      </v-btn>
                    </div>
                    <v-btn
                      v-else
                      block
                      x-large
                      :color="currentTheme.color"
                      class="white--text font-weight-bold rounded-lg"
                      @click="executeAction('anamnese')">
                      Neue Anamnese erfassen <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </template>
                  <template v-else-if="localData.specialType === 'new_contract'">
                    <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-2">Vertrag erstellen</div>

                    <div v-if="activeCustomer" class="mb-4">
                      <div class="grey--text mb-4">Erstelle einen neuen Vertrag für den ausgewählten Stammkunden.</div>

                      <div
                        class="pa-3 rounded-lg mb-4 d-flex flex-column gap-1"
                        style="background: rgba(var(--rgb-color), 0.1); border: 1px solid rgba(var(--rgb-color), 0.1)">
                        <div class="caption dynamic-text opacity-60">Kunde</div>
                        <div class="font-weight-bold dynamic-text">{{ activeCustomer.vorname }} {{ activeCustomer.nachname }}</div>
                        <div class="caption">{{ activeCustomer.email || "Keine E-Mail hinterlegt" }}</div>
                      </div>

                      <div
                        class="pa-3 rounded-lg mb-4"
                        style="background: rgba(var(--rgb-color), 0.05); border: 1px solid rgba(var(--rgb-color), 0.1)">
                        <div class="d-flex align-center justify-space-between">
                          <span class="caption dynamic-text opacity-60">Aktueller Status</span>
                          <v-chip x-small :color="contractStatusColor" class="font-weight-bold white--text">{{
                            appointmentContractChipLabel
                          }}</v-chip>
                        </div>
                      </div>
                    </div>

                    <div v-else class="mb-4">
                      <div class="grey--text mb-4">Für diese Person wurde beim Termin noch kein Stammkunde hinterlegt.</div>

                      <div
                        v-if="localData.guestData"
                        class="pa-3 rounded-lg mb-4 d-flex flex-column gap-1"
                        style="background: rgba(var(--rgb-color), 0.1); border: 1px solid rgba(var(--rgb-color), 0.1)">
                        <div class="caption dynamic-text opacity-60">Erfasste Gast-Daten</div>
                        <div class="font-weight-bold dynamic-text">{{ localData.guestData.firstName }} {{ localData.guestData.lastName }}</div>
                        <div class="caption">{{ localData.guestData.email || "Keine E-Mail hinterlegt" }}</div>
                      </div>

                      <!-- Option zum Account erstellen für den Gast -->
                      <v-btn
                        v-if="localData.guestData || activeNoAccountData"
                        block
                        x-large
                        :color="currentTheme.color"
                        class="white--text font-weight-bold rounded-lg mb-4"
                        @click="showRegisterDialog = true">
                        <v-icon left>mdi-account-plus</v-icon> Jetzt Account erstellen
                      </v-btn>

                      <!-- Option zum nachträglichen Verknüpfen eines Stammkunden -->
                      <div class="mt-4" style="position: relative; z-index: 1000">
                        <div class="caption grey--text text-uppercase font-weight-bold mb-2">Stammkunde nachträglich verknüpfen</div>
                        <user-search
                          @update="assignExistingCustomer"
                          return-user
                          ignore-rights
                          label="Kunden suchen..."
                          :dark="isDarkMode"
                          outlined
                          dense
                          :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
                      </div>
                    </div>

                    <v-btn
                      block
                      x-large
                      :color="relevantRoodyContract ? 'orange darken-2' : currentTheme.color"
                      class="white--text font-weight-bold rounded-lg"
                      :loading="roodyContractsLoading"
                      :disabled="newContractAppointmentActionDisabled"
                      @click="executeAction('new_contract')">
                      <v-icon left>{{ newContractAppointmentActionIcon }}</v-icon>
                      {{ newContractAppointmentActionLabel }}
                    </v-btn>
                  </template>

                  <template v-else-if="localData.specialType === 'upgrade'">
                    <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-2">Vertrag / Upgrade</div>
                    <div class="grey--text mb-4">Bestehenden Vertrag anpassen oder neues Zusatzpaket hinzubuchen.</div>

                    <div class="pa-3 rounded-lg mb-6" style="background: rgba(var(--rgb-color), 0.1); border: 1px solid rgba(var(--rgb-color), 0.1)">
                      <div class="d-flex align-center justify-space-between">
                        <span class="caption dynamic-text opacity-60">Aktueller Status</span>
                        <v-chip x-small :color="contractStatusColor" class="font-weight-bold white--text">{{
                          appointmentContractChipLabel
                        }}</v-chip>
                      </div>
                    </div>

                    <v-btn
                      block
                      x-large
                      :color="currentTheme.color"
                      class="white--text font-weight-bold rounded-lg"
                      @click="executeAction('upgrade')">
                      Vertrag anpassen <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </template>

                  <template v-else-if="localData.isPause">
                    <div class="text-h6 text-md-h5 font-weight-black dynamic-text mb-2">Pause bearbeiten</div>
                    <div class="grey--text mb-6">Passe die Dauer oder die Zeit der Mitarbeiter-Pause an.</div>

                    <div class="pa-4 rounded-xl mb-6" style="background: rgba(var(--rgb-color), 0.05); border: 1px solid rgba(var(--rgb-color), 0.1)">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="orange" class="mr-3">mdi-coffee</v-icon>
                        <div class="dynamic-text font-weight-bold">Mitarbeiter-Pause</div>
                      </div>
                      <div class="caption grey--text">Diese Pause ist Teil des Schichtplans und blockiert keine Kunden-Termine.</div>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Bei Pause/Gast/Vertrag ohne Account liegt die Beschreibung bewusst links.
                   Dadurch gibt es nie zwei Editoren fuer dasselbe Feld. -->
              <div
                v-if="localData.isPause || !activeCustomer"
                class="cockpit-inline-notes detail-section-card mb-4"
                :style="{ '--section-accent': currentTheme.color }">
                <div class="detail-section-heading mb-2">
                  <span class="detail-section-icon" :style="`background: ${hexToRgba(currentTheme.color, 0.14)}`">
                    <v-icon small :color="currentTheme.color">mdi-text-box-edit-outline</v-icon>
                  </span>
                  <div>
                    <div class="detail-section-eyebrow">Notizen</div>
                    <div class="detail-section-title dynamic-text">Beschreibung & Ablauf</div>
                  </div>
                </div>
                <v-textarea
                  v-model="localData.description"
                  :placeholder="descriptionPlaceholder"
                  outlined
                  hide-details
                  auto-grow
                  rows="3"
                  :dark="isDarkMode"
                  class="cockpit-notes-input"
                  :color="currentTheme.color"></v-textarea>
              </div>

              <div v-if="activeCustomer && (!$vuetify.breakpoint.mdAndDown || $vuetify.breakpoint.smAndDown)" class="d-flex flex-column gap-3 mb-6">
                <!-- Sleek Minimalist Membership Card -->
                <div v-if="localData.specialType !== 'new_contract'" class="pa-4 rounded-lg" :style="membershipCardStyle(activeMainContract)">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <div class="caption font-weight-bold text-uppercase" style="color: var(--text-muted); opacity: 0.85; letter-spacing: 0.5px">
                      Hauptvertrag
                    </div>
                    <v-chip
                      x-small
                      :color="appointmentContractChipColor"
                      :class="appointmentContractChipClass"
                      class="font-weight-black px-2">
                      {{ appointmentContractChipLabel }}
                    </v-chip>
                  </div>
                  <div class="font-weight-black text-h6 text-truncate mb-1" style="color: var(--text-main)">
                    {{ appointmentContractTitle }}
                  </div>
                  <div class="caption font-weight-bold" style="color: var(--text-muted)">
                    {{ contractStatusText }}
                  </div>

                  <v-btn
                    v-if="activeMainContract"
                    block
                    small
                    outlined
                    color="orange darken-2"
                    class="mt-3 text-none font-weight-bold"
                    @click="executeAction('upgrade')">
                    <v-icon left small>mdi-star-shooting</v-icon>
                    Upgrade & Zusatzpakete
                  </v-btn>

                  <div v-if="!activeMainContract" class="appointment-contract-state mt-3">
                    <div class="appointment-contract-state__row">
                      <v-progress-circular v-if="roodyContractsLoading" indeterminate :size="22" :width="3" color="orange darken-2" />
                      <v-icon v-else :color="relevantRoodyContract ? 'orange darken-2' : roodyContractsError ? 'red lighten-1' : 'grey'">
                        {{ relevantRoodyContract ? "mdi-file-clock-outline" : roodyContractsError ? "mdi-alert-circle-outline" : "mdi-file-plus-outline" }}
                      </v-icon>
                      <div class="appointment-contract-state__copy">
                        <strong>{{ roodyContractStateTitle }}</strong>
                        <span>{{ roodyContractStateHint }}</span>
                      </div>
                    </div>
                    <v-btn
                      v-if="relevantRoodyContract"
                      block
                      small
                      depressed
                      color="orange darken-2"
                      class="mt-3 white--text text-none font-weight-bold"
                      @click="openRelevantRoodyContract">
                      <v-icon left small>{{ relevantRoodyContract.status === "temporary" ? "mdi-pencil-outline" : "mdi-eye-outline" }}</v-icon>
                      {{ relevantRoodyContractActionLabel }}
                    </v-btn>
                    <v-btn
                      v-else-if="roodyContractsLoaded && canCreateCustomerContract"
                      block
                      small
                      depressed
                      :color="currentTheme.color"
                      class="mt-3 white--text text-none font-weight-bold"
                      @click="startCustomerContract">
                      <v-icon left small>mdi-file-sign</v-icon>
                      Vertrag erstellen
                    </v-btn>
                    <v-btn
                      v-else-if="roodyContractsError && roodyContractsError !== 'missing_right'"
                      small
                      text
                      color="orange darken-2"
                      class="mt-2 text-none"
                      @click="loadRoodyContractStatus(activeCustomerId)">
                      Erneut prüfen
                    </v-btn>
                  </div>

                  <div v-if="customerDetails && customerDetails.packages && customerDetails.packages.length > 0" class="mt-4 pt-4 border-top-light">
                    <div class="caption grey--text mb-3 text-uppercase font-weight-bold letter-spacing-1">Zusatzpakete</div>
                    <div class="d-flex flex-column gap-2">
                      <div
                        v-for="pkg in customerDetails.packages"
                        :key="pkg.CONTRACTID"
                        class="d-flex align-center justify-space-between pa-2 rounded-lg"
                        style="background: rgba(var(--rgb-color), 0.05); border: 1px solid rgba(var(--rgb-color), 0.05)">
                        <div class="d-flex align-center">
                          <v-avatar color="rgba(255,152,0,0.15)" size="24" class="mr-3">
                            <v-icon x-small color="orange accent-2">mdi-star-four-points</v-icon>
                          </v-avatar>
                          <span class="text-body-2 dynamic-text font-weight-medium">{{ pkg.NAME }}</span>
                        </div>
                        <v-chip x-small color="rgba(128,128,128,0.2)" class="grey--text">Bis {{ formatDate(pkg.ENDDATE) }}</v-chip>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="activeTimestop"
                  class="glass-panel-light flex-grow-1 pa-4 rounded-lg border-left-indicator elevation-5"
                  style="border-left-color: #ff9800">
                  <div class="caption grey--text text-uppercase">Pausiert</div>
                  <div class="font-weight-bold dynamic-text text-body-1">Bis {{ formatDate(activeTimestop.timestopInfos.end) }}</div>
                  <div class="caption orange--text">
                    {{ activeTimestop.tacData.Reason || "Pausiert" }}
                  </div>
                </div>
              </div>

              <div
                v-if="activeCustomer && (!$vuetify.breakpoint.mdAndDown || $vuetify.breakpoint.smAndDown)"
                class="pa-4 rounded-lg mb-4"
                :style="{
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.02) !important' : '#ffffff !important',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} !important`,
                  borderLeft: `4px solid ${currentTheme.color} !important`,
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.04) !important',
                }">
                <div class="subtitle-2 dynamic-text mb-3 font-weight-bold" style="color: var(--text-main)">
                  <v-icon left small :color="currentTheme.color">mdi-door-open</v-icon> Letzte Besuche
                </div>
                <div v-if="processedAttendance.length > 0" class="d-flex align-center overflow-x-auto gap-2 pb-2 custom-scrollbar">
                  <div
                    v-for="(visit, i) in processedAttendance.slice(0, 5)"
                    :key="i"
                    class="checkin-pill d-flex align-center px-3 py-2 rounded-lg flex-shrink-0"
                    style="background: rgba(var(--rgb-color), 0.08); min-width: 120px; border: 1px solid rgba(var(--rgb-color), 0.1)">
                    <div class="mr-3 text-center" style="min-width: 32px">
                      <div class="font-weight-black dynamic-text" style="font-size: 1.1rem; line-height: 1.1">
                        {{ $moment(visit.date).format("DD") }}
                      </div>
                      <div class="grey--text" style="font-size: 0.62rem; white-space: nowrap; letter-spacing: 0.2px">
                        {{ $moment(visit.date).format("MMM") }}
                      </div>
                    </div>
                    <div>
                      <div class="caption dynamic-text font-weight-bold">{{ visit.start }}</div>
                      <div class="primary--text" style="font-size: 0.68rem">{{ visit.duration || "Aktiv" }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="caption grey--text">Keine Check-in Daten verfügbar.</div>
              </div>
            </div>

            <div v-else-if="activeTab === 1 && activeCustomer" class="pb-4">
              <div class="mb-6">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="text-subtitle-1 font-weight-bold dynamic-text">
                    <v-icon :color="currentTheme.color" class="mr-2">mdi-clipboard-pulse</v-icon> Anamnese & Gesundheit
                  </div>
                  <v-btn
                    x-small
                    :color="currentTheme.color"
                    outlined
                    @click="$refs.anamneseBogenRef.setVisible(null, false, false, activeCustomer._id)">
                    <v-icon left x-small>mdi-plus</v-icon> Neu
                  </v-btn>
                </div>

                <div v-if="sortedMedicalHistory.length > 0">
                  <div
                    v-for="med in sortedMedicalHistory"
                    :key="med._id"
                    class="glass-panel-light pa-3 rounded-lg mb-2 d-flex align-center justify-space-between hover-glow cursor-pointer"
                    @click="viewDocument('medicalHistory', med)">
                    <div class="d-flex align-center">
                      <v-avatar color="green lighten-4" size="36" class="mr-3">
                        <v-icon small color="primary">mdi-file-document</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-body-2 font-weight-bold dynamic-text">Gesundheitscheck (V2)</div>
                        <div class="caption grey--text">{{ formatDate(med.createdAt) }} • {{ med.creator ? med.creator.vorname : "System" }}</div>
                      </div>
                    </div>
                    <v-icon small color="grey">mdi-eye</v-icon>
                  </div>
                </div>

                <div v-if="sortedOldAnamnese.length > 0" class="mt-2">
                  <div class="glass-panel-light pa-3 rounded-lg mb-2 d-flex align-center justify-space-between opacity-70">
                    <div class="d-flex align-center">
                      <v-avatar color="orange lighten-5" size="36" class="mr-3">
                        <v-icon small color="orange darken-3">mdi-archive-clock</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-body-2 font-weight-bold dynamic-text">Anamnese (Altbestand)</div>
                        <div class="caption grey--text">
                          {{ formatDate(sortedOldAnamnese[0].createdAt || sortedOldAnamnese[0].date * 1000) }} • Archiviert
                        </div>
                      </div>
                    </div>
                    <v-chip x-small label dark>Nur Archiv</v-chip>
                  </div>
                </div>

                <div v-if="!anamneseInfo.exists" class="text-center py-4 glass-panel-light rounded-lg border-dashed">
                  <div class="caption grey--text">Keine Anamnese vorhanden</div>
                </div>
              </div>

              <v-divider class="my-4 border-light"></v-divider>

              <div class="mb-6">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="text-subtitle-1 font-weight-bold dynamic-text">
                    <v-icon color="primary" class="mr-2">mdi-dumbbell</v-icon> Trainingspläne
                  </div>
                  <v-btn x-small color="primary" outlined @click="executeAction('workout_new')"> <v-icon left x-small>mdi-plus</v-icon> Neu </v-btn>
                </div>

                <div v-if="loadingWorkouts" class="text-center py-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>
                <div v-else-if="allWorkouts.length > 0">
                  <div
                    v-for="(plan, idx) in allWorkouts"
                    :key="plan.id + '-' + idx"
                    class="glass-panel-light pa-3 rounded-lg mb-2 d-flex align-center justify-space-between hover-glow cursor-pointer"
                    :class="{ 'border-left-active': idx === 0 && plan.type === 'new', 'opacity-70': plan.type === 'old' }"
                    @click="viewDocument('workout', plan)">
                    <div class="d-flex align-center overflow-hidden">
                      <v-avatar :color="plan.type === 'new' ? 'blue lighten-5' : 'grey lighten-3'" size="36" class="mr-3 flex-shrink-0">
                        <v-icon small :color="plan.type === 'new' ? 'blue darken-2' : 'grey darken-1'">
                          {{ plan.type === "new" ? "mdi-cellphone-link" : "mdi-archive" }}
                        </v-icon>
                      </v-avatar>
                      <div class="overflow-hidden pr-2">
                        <div class="text-body-2 font-weight-bold dynamic-text text-truncate d-flex align-center gap-1">
                          {{ plan.title }}
                          <v-chip v-if="idx === 0 && plan.type === 'new'" x-small color="primary" class="white--text ml-1" label>Aktuell</v-chip>
                          <v-chip v-if="plan.type === 'old'" x-small label class="grey lighten-1 grey--text text--darken-3 ml-1 font-weight-bold"
                            >Archiv</v-chip
                          >
                        </div>

                        <div class="d-flex flex-wrap gap-1 mt-1 mb-1" v-if="plan.type === 'new' && plan.splitDays && plan.splitDays.length > 0">
                          <v-chip v-for="(day, dIdx) in plan.splitDays" :key="'s' + dIdx" x-small outlined color="primary" class="font-weight-bold">
                            {{ day }}
                          </v-chip>
                        </div>

                        <div class="caption grey--text text-truncate">{{ formatDate(plan.createdDate) }} • {{ plan.instructor }}</div>
                      </div>
                    </div>
                    <v-icon small :color="plan.type === 'new' ? 'grey' : 'grey lighten-1'">
                      {{ plan.type === "new" ? "mdi-pencil" : "mdi-eye-off" }}
                    </v-icon>
                  </div>
                </div>
                <div v-else class="text-center py-4 glass-panel-light rounded-lg border-dashed">
                  <div class="caption grey--text">Keine Pläne vorhanden</div>
                </div>
              </div>

              <v-divider class="my-4 border-light"></v-divider>

              <div class="mb-6">
                <div class="text-subtitle-1 font-weight-bold dynamic-text mb-2">
                  <v-icon color="#FF9800" class="mr-2">mdi-file-sign</v-icon> Verträge & Pausen
                </div>

                <div class="glass-panel-light pa-3 rounded-lg mb-2" v-if="customerDetails && customerDetails.mainContract">
                  <div class="d-flex justify-space-between">
                    <span class="text-body-2 font-weight-bold dynamic-text">{{ customerDetails.mainContract.NAME }}</span>
                    <v-chip x-small :color="contractStatusColor" outlined class="font-weight-bold">{{
                      customerDetails.mainContract.CONTRACTSTATE
                    }}</v-chip>
                  </div>
                  <div class="d-flex justify-space-between mt-2 caption grey--text">
                    <span>Start: {{ formatDate(customerDetails.mainContract.STARTDATE) }}</span>
                    <span>Ende: {{ formatDate(customerDetails.mainContract.TERM_ENDDATE) }}</span>
                  </div>
                </div>

                <div v-if="sortedTimestops.length > 0">
                  <div class="caption font-weight-bold grey--text mb-1 mt-3">Timestop Historie</div>
                  <div v-for="ts in sortedTimestops" :key="ts._id" class="d-flex align-center justify-space-between py-1 border-bottom-light">
                    <div>
                      <div class="caption dynamic-text">{{ formatDate(ts.timestopInfos.start) }} - {{ formatDate(ts.timestopInfos.end) }}</div>
                      <div class="caption grey--text" style="font-size: 10px">{{ ts.tacData.Reason }}</div>
                    </div>
                    <v-icon v-if="ts.status === 'accepted'" small color="primary">mdi-check-circle</v-icon>
                    <v-icon v-else-if="ts.status === 'rejected'" small color="red">mdi-close-circle</v-icon>
                    <v-icon v-else small color="grey">mdi-help-circle</v-icon>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="activeTab === 2 && activeCustomer" class="pl-2 pr-2">
              <!-- Log Interaction Button -->
              <div class="d-flex justify-end mb-4">
                <v-btn small rounded depressed :color="currentTheme.color" class="white--text font-weight-bold" @click="openLogInteraction">
                  <v-icon left small>mdi-comment-plus-outline</v-icon>
                  Interaktion erfassen
                </v-btn>
              </div>

              <div v-if="loadingHistory" class="text-center py-4">
                <v-progress-circular indeterminate :color="currentTheme.color"></v-progress-circular>
              </div>
              <div v-else-if="appointmentHistory.length > 0">
                <div v-for="appt in appointmentHistory" :key="appt._id" class="mb-4 relative pl-6 timeline-item">
                  <div class="timeline-line"></div>
                  <div class="timeline-dot" :style="`background: ${appt.color || currentTheme.color}`"></div>

                  <div
                    class="glass-panel-light pa-3 rounded-lg hover-glow cursor-pointer"
                    :style="
                      appt.type === 'appointment' && String(appt._id) === String(editData.id)
                        ? 'border: 2px solid #26A69A !important; background: rgba(38, 166, 154, 0.08) !important; box-shadow: 0 0 15px rgba(38, 166, 154, 0.25) !important;'
                        : ''
                    "
                    @click="openHistoryDetails(appt)">
                    <div class="d-flex justify-space-between align-center">
                      <span class="font-weight-bold dynamic-text d-flex align-center flex-wrap">
                        <v-icon small class="mr-1" :color="appt.isTac ? appt.color : appt.type === 'interaction' ? 'orange' : ''">
                          {{ appt.isTac ? "mdi-history" : appt.type === "interaction" ? "mdi-notebook-edit" : "mdi-calendar-clock" }}
                        </v-icon>
                        <span>{{ appt.title || (appt.type === "interaction" ? "Interaktion" : "Termin") }}</span>
                        <v-chip
                          x-small
                          v-if="appt.type === 'appointment' && String(appt._id) === String(editData.id)"
                          color="teal"
                          class="white--text font-weight-bold ml-2 py-0"
                          style="height: 16px; font-size: 0.6rem !important">
                          Aktueller Termin
                        </v-chip>
                      </span>
                      <span class="caption grey--text">{{ formatDate(appt.date) }}</span>
                    </div>

                    <!-- Appointment note block -->
                    <div
                      v-if="appt.type === 'appointment' && appt.description"
                      class="caption mt-2 font-italic pa-2 rounded"
                      style="
                        white-space: pre-wrap;
                        background: rgba(128, 128, 128, 0.05);
                        border-left: 3px solid rgba(128, 128, 128, 0.4);
                        color: var(--text-main);
                        line-height: 1.3;
                      ">
                      <strong style="font-size: 0.68rem; font-style: normal; color: var(--text-muted); display: block; margin-bottom: 2px"
                        >NOTIZ IM TERMIN:</strong
                      >
                      {{ appt.description }}
                    </div>

                    <!-- Interaction note block -->
                    <div
                      v-if="appt.type === 'interaction' && appt.description"
                      class="caption mt-2 font-italic pa-2 rounded"
                      :style="
                        appt.isTac
                          ? appt.storno === 'Yes'
                            ? 'white-space: pre-wrap; background: rgba(229, 57, 53, 0.04); border-left: 3px solid #D32F2F; color: var(--text-main); line-height: 1.3;'
                            : 'white-space: pre-wrap; background: rgba(90, 110, 122, 0.04); border-left: 3px solid #455A64; color: var(--text-main); line-height: 1.3;'
                          : 'white-space: pre-wrap; background: rgba(255, 152, 0, 0.05); border-left: 3px solid rgba(255, 152, 0, 0.4); color: var(--text-main); line-height: 1.3;'
                      ">
                      <strong
                        v-if="appt.isTac"
                        :style="
                          'font-size: 0.68rem; font-style: normal; color: ' +
                          (appt.storno === 'Yes' ? '#D32F2F' : '#455A64') +
                          '; display: block; margin-bottom: 2px;'
                        "
                        >TAC NOTIZ:</strong
                      >
                      <strong v-else style="font-size: 0.68rem; font-style: normal; color: orange; display: block; margin-bottom: 2px"
                        >INTERAKTION:</strong
                      >
                      {{ appt.description }}
                    </div>

                    <div class="d-flex align-center justify-space-between mt-2">
                      <div class="d-flex align-center">
                        <v-avatar size="20" class="mr-2">
                          <img v-if="appt.assignedEmployee && appt.assignedEmployee.bild" :src="'/img/profilbilder/' + appt.assignedEmployee.bild" />
                          <v-icon v-else-if="appt.type === 'interaction' && appt.assignedEmployee" x-small>mdi-account</v-icon>
                          <v-icon v-else x-small>mdi-account</v-icon>
                        </v-avatar>
                        <span class="caption grey--text">{{
                          appt.assignedEmployee
                            ? `${appt.assignedEmployee.vorname || ""} ${appt.assignedEmployee.nachname || ""}`.trim()
                            : "Unbekannt"
                        }}</span>
                        <!-- Studio Chip -->
                        <v-chip x-small v-if="appt.location" color="grey lighten-3" class="grey--text text--darken-2 font-weight-bold ml-2">
                          {{ appt.location }}
                        </v-chip>
                      </div>
                      <v-chip
                        x-small
                        v-if="appt.type === 'interaction'"
                        :color="appt.isTac ? (appt.storno === 'Yes' ? '#D32F2F' : '#455A64') : 'orange'"
                        class="white--text font-weight-bold">
                        {{ appt.isTac ? (appt.storno === "Yes" ? "TAC Storno" : "TAC Termin") : "Interaktion" }}
                      </v-chip>
                    </div>

                    <!-- Completed actions sub-points (Workouts, Anamnesen nested) -->
                    <div
                      v-if="appt.type === 'appointment' && appt.completedActions && appt.completedActions.length > 0"
                      class="mt-3 pt-2"
                      style="border-top: 1px dashed rgba(255, 255, 255, 0.1)">
                      <div class="caption font-weight-bold grey--text text--lighten-1 mb-1">Durchgeführte Aktionen:</div>
                      <div
                        v-for="action in appt.completedActions"
                        :key="action._id"
                        class="caption d-flex align-center mt-1 teal--text text--lighten-2">
                        <v-icon x-small color="teal lighten-2" class="mr-1">
                          {{ action.actionType && action.actionType.startsWith("workout") ? "mdi-dumbbell" : "mdi-file-document-edit" }}
                        </v-icon>
                        <span class="font-weight-medium">{{ action.title }}</span>
                        <v-spacer></v-spacer>
                        <span class="grey--text font-weight-light">{{ formatTime(action.createdAt) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center caption grey--text py-4">Keine vergangenen Termine.</div>
            </div>

            <div v-else-if="!editData.customer && localData.isTask" class="task-editor fill-height d-flex flex-column">
              <div class="left-editor-heading d-flex align-center mb-3">
                <span class="left-editor-heading-icon" :style="`background: ${hexToRgba(currentTheme.color, 0.12)}`">
                  <v-icon :color="currentTheme.color" size="20">mdi-format-list-checks</v-icon>
                </span>
                <div>
                  <div class="detail-section-eyebrow">Aufgabe</div>
                  <div class="left-editor-heading-title dynamic-text">Inhalt & Checkliste</div>
                </div>
              </div>

              <!-- Grosses Notizfeld für Aufgaben -->
              <div class="editor-notes-panel d-flex flex-column overflow-hidden mb-3 flex-shrink-0" :style="`--editor-accent: ${currentTheme.color}`">
                <div class="editor-panel-heading d-flex align-center">
                  <v-icon small :color="currentTheme.color" class="mr-2">mdi-text-box-outline</v-icon>
                  <span class="caption font-weight-bold text-uppercase" :style="`color: ${currentTheme.color}`">Beschreibung & Notiz</span>
                </div>
                <v-textarea
                  v-model="localData.description"
                  placeholder="Notizen, Ablauf, Ergebnisse..."
                  solo
                  flat
                  background-color="transparent"
                  :dark="isDarkMode"
                  class="custom-textarea editor-notes-input"
                  hide-details
                  auto-grow
                  rows="3"
                  :color="currentTheme.color"></v-textarea>
              </div>

              <div class="task-checklist-heading d-flex align-center justify-space-between mb-2">
                <div class="font-weight-bold dynamic-text text-uppercase caption opacity-70">Checkliste</div>
                <v-chip :color="currentTheme.color" class="font-weight-bold white--text" x-small>
                  {{ completedChecklistItems }} / {{ localData.checklist?.length || 0 }} Erledigt
                </v-chip>
              </div>

              <v-progress-linear
                :value="checklistProgress"
                :color="currentTheme.color"
                height="4"
                rounded
                class="mb-3 opacity-80"
                :background-color="isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'"></v-progress-linear>

              <div
                class="task-checklist-panel mb-3 overflow-y-auto custom-scrollbar"
                :class="{ 'flex-grow-1': localData.checklist && localData.checklist.length > 0 }">
                <div v-if="!localData.checklist || localData.checklist.length === 0" class="task-empty-state text-center opacity-50">
                  <span class="task-empty-icon"><v-icon size="30">mdi-check-all</v-icon></span>
                  <div class="text-body-2 font-weight-medium dynamic-text mt-2">Noch keine Checklistenpunkte</div>
                  <div class="caption mt-1">Füge unten den ersten Punkt hinzu.</div>
                </div>

                <div v-for="(item, i) in localData.checklist" :key="i" class="d-flex align-center mb-3">
                  <v-checkbox v-model="item.isDone" hide-details class="mt-0 pt-0" :color="currentTheme.color" :dark="isDarkMode"></v-checkbox>
                  <v-text-field
                    v-model="item.text"
                    dense
                    hide-details
                    :dark="isDarkMode"
                    flat
                    solo
                    background-color="transparent"
                    class="flex-grow-1 checklist-input"
                    :class="{ 'text-strike text-muted': item.isDone }"></v-text-field>
                  <v-btn icon small class="ml-2" color="red darken-2" @click="localData.checklist.splice(i, 1)"
                    ><v-icon small>mdi-close</v-icon></v-btn
                  >
                </div>
              </div>

              <div>
                <v-text-field
                  v-model="newChecklistItem"
                  placeholder="Neu hinzufügen (Enter drücken)"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  prepend-inner-icon="mdi-plus"
                  @keyup.enter="addChecklistItem"
                  class="rounded-lg mb-3 checklist-add-control cockpit-control"
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'"
                  :color="currentTheme.color"></v-text-field>
              </div>

              <div v-if="localData.instructions" class="px-2">
                <v-expansion-panels flat :dark="isDarkMode" class="transparent-panels">
                  <v-expansion-panel class="transparent">
                    <v-expansion-panel-header class="pa-0 py-2 caption grey--text font-weight-bold uppercase letter-spacing-1">
                      <v-icon left x-small color="grey">mdi-information-outline</v-icon> Anleitung anzeigen
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pa-0">
                      <div
                        class="glass-panel-light pa-3 rounded-lg dynamic-text text-caption"
                        style="line-height: 1.4; border-left: 2px solid var(--text-color)">
                        {{ localData.instructions }}
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>

            <div v-else class="generic-entry-editor d-flex flex-column fill-height">
              <!-- Grosses Notizfeld für Einträge ohne Kunde -->
              <div class="left-editor-heading d-flex align-center mb-3">
                <span class="left-editor-heading-icon" :style="`background: ${hexToRgba(currentTheme.color, 0.12)}`">
                  <v-icon :color="currentTheme.color" size="20">mdi-notebook-edit-outline</v-icon>
                </span>
                <div>
                  <div class="detail-section-eyebrow">Inhalt</div>
                  <div class="left-editor-heading-title dynamic-text">Notizen & Ablauf</div>
                </div>
              </div>

              <div class="editor-notes-panel d-flex flex-column overflow-hidden mb-3 flex-grow-1" :style="`--editor-accent: ${currentTheme.color}`">
                <div class="editor-panel-heading d-flex align-center">
                  <v-icon small :color="currentTheme.color" class="mr-2">mdi-text-box-outline</v-icon>
                  <span class="caption font-weight-bold text-uppercase" :style="`color: ${currentTheme.color}`">Beschreibung</span>
                </div>
                <v-textarea
                  v-model="localData.description"
                  placeholder="Notizen, Ablauf, Ergebnisse..."
                  solo
                  flat
                  background-color="transparent"
                  :dark="isDarkMode"
                  class="custom-textarea editor-notes-input flex-grow-1"
                  hide-details
                  auto-grow
                  rows="8"
                  :color="currentTheme.color"></v-textarea>
              </div>

              <!-- Checkliste -->
              <div v-if="localData.checklist && localData.checklist.length > 0" class="mb-5">
                <div class="d-flex align-center mb-3">
                  <v-icon small :color="currentTheme.color" class="mr-2">mdi-checkbox-marked-outline</v-icon>
                  <span class="caption font-weight-bold text-uppercase dynamic-text">Checkliste</span>
                  <v-spacer></v-spacer>
                  <span class="caption" :style="`color: ${currentTheme.color}`">{{ completedChecklistItems }}/{{ localData.checklist.length }}</span>
                </div>
                <v-progress-linear :value="checklistProgress" :color="currentTheme.color" height="3" rounded class="mb-3"></v-progress-linear>
                <div
                  v-for="(item, idx) in localData.checklist"
                  :key="idx"
                  class="d-flex align-center mb-2 glass-panel-light rounded-lg pa-2"
                  style="cursor: pointer"
                  @click="item.isDone = !item.isDone">
                  <v-icon small :color="item.isDone ? currentTheme.color : 'grey'" class="mr-3">{{
                    item.isDone ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline"
                  }}</v-icon>
                  <span
                    class="dynamic-text text-body-2"
                    :style="{ textDecoration: item.isDone ? 'line-through' : 'none', opacity: item.isDone ? 0.5 : 1 }"
                    >{{ item.text }}</span
                  >
                </div>
              </div>

              <!-- Anleitung (wenn vorhanden) -->
              <div v-if="localData.instructions" class="mb-4">
                <div class="d-flex align-center mb-2">
                  <v-icon small :color="currentTheme.color" class="mr-2">mdi-book-open-variant</v-icon>
                  <span class="caption font-weight-bold text-uppercase dynamic-text">Anleitung</span>
                </div>
                <div
                  class="glass-panel-light pa-4 rounded-lg dynamic-text text-body-2"
                  style="line-height: 1.6; white-space: pre-wrap; border-left: 3px solid"
                  :style="`border-left-color: ${currentTheme.color}`">
                  {{ localData.instructions }}
                </div>
              </div>
            </div>
          </v-col>

          <v-col
            v-if="showDetailsColumn"
            cols="12"
            :md="showLeftColumn ? 5 : 12"
            :lg="showLeftColumn ? 5 : 12"
            class="order-2 order-md-2 d-flex flex-column cockpit-details-column"
            :class="{ 'pl-4 border-left-light': $vuetify.breakpoint.mdAndUp, 'px-3 py-3 mb-4': $vuetify.breakpoint.smAndDown }"
            :style="
              $vuetify.breakpoint.mdAndUp
                ? `background: var(--right-panel-md); height: 100%; overflow-y: auto;`
                : `background: var(--right-panel-sm); border-radius: 12px;`
            ">
            <div v-if="!$vuetify.breakpoint.mdAndDown || showMobileDetails || !activeCustomer" class="d-flex flex-column h-100 cockpit-details-shell">
              <div class="flex-grow-1 overflow-visible custom-scrollbar pr-md-2 cockpit-details-scroll">
                <section
                  v-if="isRealAppointment && appointmentCustomerSlots.length"
                  class="detail-section-card customer-assignment-card mb-3"
                  :style="{ '--section-accent': currentTheme.color }">
                  <div class="detail-section-heading mb-2">
                    <span class="detail-section-icon" :style="`background: ${hexToRgba(currentTheme.color, 0.14)}`">
                      <v-icon small :color="currentTheme.color">mdi-account-multiple-outline</v-icon>
                    </span>
                    <div class="flex-grow-1 min-width-0">
                      <div class="detail-section-eyebrow">Kundenzuordnung</div>
                      <div class="detail-section-title dynamic-text">
                        {{ appointmentCustomerSlots.length > 1 ? "Kunden des Termins" : "Kunde des Termins" }}
                      </div>
                    </div>
                    <v-chip v-if="customerAssignmentChanged" x-small outlined :color="currentTheme.color" class="customer-change-pending-chip">
                      Noch speichern
                    </v-chip>
                  </div>

                  <div class="customer-assignment-list">
                    <button
                      v-for="slot in appointmentCustomerSlots"
                      :key="slot.index"
                      type="button"
                      class="customer-assignment-row"
                      :class="{ 'customer-assignment-row--active': showCustomerChange && activeCustomerIndex === slot.index }"
                      :style="
                        showCustomerChange && activeCustomerIndex === slot.index
                          ? { borderColor: hexToRgba(currentTheme.color, 0.38), background: hexToRgba(currentTheme.color, 0.08) }
                          : null
                      "
                      @click="prepareCustomerChange(slot.index)">
                      <v-avatar size="34" class="customer-assignment-avatar">
                        <img v-if="resolveCustomerPicture(slot.customer)" :src="resolveCustomerPicture(slot.customer)" alt="" />
                        <span v-else class="font-weight-bold">{{ getCustomerInitials(slot.customer, slot.noAccountData) }}</span>
                      </v-avatar>
                      <span class="customer-assignment-copy">
                        <small>{{ slot.slotLabel }}</small>
                        <strong>{{ slot.name }}</strong>
                      </span>
                      <span class="customer-assignment-action">
                        <span>Ändern</span>
                        <v-icon small :color="currentTheme.color">mdi-account-switch-outline</v-icon>
                      </span>
                    </button>
                  </div>

                  <v-expand-transition>
                    <div v-if="showCustomerChange" class="customer-change-search mt-3">
                      <div class="d-flex align-center mb-2">
                        <v-icon small :color="currentTheme.color" class="mr-2">mdi-magnify</v-icon>
                        <strong class="text-body-2 dynamic-text">{{ customerChangeTitle }}</strong>
                        <v-spacer></v-spacer>
                        <v-btn icon x-small :color="isDarkMode ? 'white' : 'grey darken-1'" @click="showCustomerChange = false">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <user-search
                        :key="`customer-change-${localData.id}-${activeCustomerIndex}`"
                        @update="selectReplacementCustomer"
                        return-user
                        ignore-rights
                        :label="customerChangeSearchLabel"
                        :dark="isDarkMode"
                        outlined
                        dense
                        :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
                      <div class="customer-change-hint mt-1">
                        Die Auswahl ersetzt nur {{ hasTwoCustomers ? `Person ${activeCustomerIndex + 1}` : "den Kunden" }} dieses Termins. Wirksam
                        wird sie gemeinsam mit den übrigen Änderungen über „Speichern“.
                      </div>
                    </div>
                  </v-expand-transition>
                </section>

                <section class="detail-section-card mb-3" :style="{ '--section-accent': currentTheme.color }">
                  <div class="detail-section-heading mb-2">
                    <span class="detail-section-icon" :style="`background: ${hexToRgba(currentTheme.color, 0.14)}`">
                      <v-icon small :color="currentTheme.color">mdi-calendar-clock-outline</v-icon>
                    </span>
                    <div>
                      <div class="detail-section-eyebrow">Planung</div>
                      <div class="detail-section-title dynamic-text">Zeit & Zuständigkeit</div>
                    </div>
                  </div>

                  <div class="cockpit-field-grid mb-3">
                    <div class="cockpit-field-cell">
                      <v-text-field
                        v-if="isRealAppointment"
                        :value="formatDate(localData.date)"
                        label="Datum + MA ändern"
                        outlined
                        dense
                        :dark="isDarkMode"
                        readonly
                        hide-details
                        prepend-inner-icon="mdi-calendar-search"
                        append-icon="mdi-chevron-right"
                        class="rounded-lg cockpit-control cursor-pointer"
                        :background-color="controlBackground"
                        :color="currentTheme.color"
                        @click="openAppointmentDateFinder"
                        @click:append="openAppointmentDateFinder"></v-text-field>
                      <v-menu v-else offset-y :close-on-content-click="false" transition="scale-transition">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="localData.date"
                            label="Datum"
                            outlined
                            dense
                            :dark="isDarkMode"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            hide-details
                            prepend-inner-icon="mdi-calendar"
                            class="rounded-lg cockpit-control"
                            :background-color="controlBackground"
                            :color="currentTheme.color"></v-text-field>
                        </template>
                        <v-date-picker v-model="localData.date" no-title scrollable :color="currentTheme.color" :dark="isDarkMode"></v-date-picker>
                      </v-menu>
                    </div>

                    <div class="cockpit-field-cell">
                      <v-select
                        v-model="localData.colId"
                        :items="extendedEmployees"
                        item-text="name"
                        item-value="id"
                        label="Mitarbeiter"
                        outlined
                        dense
                        :dark="isDarkMode"
                        hide-details
                        prepend-inner-icon="mdi-account"
                        class="rounded-lg cockpit-control"
                        :background-color="controlBackground"
                        :color="currentTheme.color"
                        :item-color="currentTheme.color">
                        <template v-slot:item="{ item }">
                          <template v-if="item.divider">
                            <v-divider class="my-1"></v-divider>
                          </template>
                          <template v-else>
                            <v-avatar v-if="item.picture" size="28" class="mr-3 flex-shrink-0">
                              <img :src="'/img/profilbilder/' + item.picture" style="object-fit: cover" />
                            </v-avatar>
                            <v-avatar
                              v-else-if="item.id && item.id !== 'ma_info' && item.id !== 'no_show'"
                              size="28"
                              class="mr-3 flex-shrink-0"
                              color="grey darken-3">
                              <v-icon x-small color="white">mdi-account</v-icon>
                            </v-avatar>
                            <v-list-item-content>
                              <v-list-item-title class="body-2">{{ item.name }}</v-list-item-title>
                            </v-list-item-content>
                          </template>
                        </template>
                        <template v-slot:selection="{ item }">
                          <v-avatar v-if="item.picture" size="20" class="mr-2 flex-shrink-0">
                            <img :src="'/img/profilbilder/' + item.picture" style="object-fit: cover" />
                          </v-avatar>
                          <v-avatar
                            v-else-if="item.id && item.id !== 'ma_info' && item.id !== 'no_show'"
                            size="20"
                            class="mr-2 flex-shrink-0"
                            color="grey darken-3">
                            <v-icon x-small color="white">mdi-account</v-icon>
                          </v-avatar>
                          <span class="body-2 text-truncate">{{ item.name }}</span>
                        </template>
                      </v-select>
                    </div>
                  </div>

                  <div class="cockpit-field-grid">
                    <div class="cockpit-field-cell">
                      <v-text-field
                        v-model="localData.start"
                        label="Start"
                        type="time"
                        outlined
                        dense
                        :dark="isDarkMode"
                        hide-details
                        prepend-inner-icon="mdi-clock-outline"
                        class="rounded-lg centered-input cockpit-control"
                        :background-color="controlBackground"
                        :color="currentTheme.color"></v-text-field>
                    </div>
                    <div class="cockpit-field-cell">
                      <v-text-field
                        v-model="localData.duration"
                        label="Dauer in Minuten"
                        type="number"
                        min="1"
                        outlined
                        dense
                        :dark="isDarkMode"
                        hide-details
                        prepend-inner-icon="mdi-timer-outline"
                        class="rounded-lg centered-input cockpit-control"
                        :background-color="controlBackground"
                        :color="currentTheme.color"></v-text-field>
                    </div>
                  </div>

                  <v-btn
                    v-if="isRealAppointment"
                    block
                    depressed
                    class="finder-launch-button text-none font-weight-bold rounded-lg mt-3"
                    :color="showFinder ? currentTheme.color : controlBackground"
                    :style="{ color: showFinder ? 'white' : 'var(--text-color)', borderColor: hexToRgba(currentTheme.color, 0.25) }"
                    @click="showFinder = !showFinder">
                    <v-icon left size="19">mdi-calendar-search</v-icon>
                    <span>{{ showFinder ? "Termin-Finder ausblenden" : "Freien Termin auf der Zeitleiste finden" }}</span>
                    <v-spacer></v-spacer>
                    <v-icon small>{{ showFinder ? "mdi-chevron-up" : "mdi-chevron-right" }}</v-icon>
                  </v-btn>

                  <div v-if="showTrainerMismatchWarning" class="trainer-warning d-flex align-center mt-3 px-3 py-2 rounded-lg">
                    <v-icon color="orange darken-2" class="mr-2" small>mdi-alert-circle-outline</v-icon>
                    <span class="caption orange--text text--darken-3 font-weight-medium flex-grow-1">
                      Aktiver Trainer:
                      {{ getSelectedEmployee.fullName || `${getSelectedEmployee.vorname || ""} ${getSelectedEmployee.nachname || ""}`.trim() }}
                    </span>
                    <v-btn
                      small
                      depressed
                      color="orange darken-2"
                      class="white--text font-weight-bold trainer-warning-action"
                      @click="syncActiveTrainerToAppointment">
                      Wechseln
                    </v-btn>
                  </div>
                </section>

                <section class="detail-section-card mb-3" :style="{ '--section-accent': currentTheme.color }">
                  <div class="detail-section-heading mb-2">
                    <span class="detail-section-icon" :style="`background: ${hexToRgba(currentTheme.color, 0.14)}`">
                      <v-icon small :color="currentTheme.color">{{ appointmentTypeMeta.icon }}</v-icon>
                    </span>
                    <div>
                      <div class="detail-section-eyebrow">Inhalt</div>
                      <div class="detail-section-title dynamic-text">
                        {{ localData.isTask ? "Aufgabe & Status" : localData.isPause ? "Pause & Status" : "Terminart & Status" }}
                      </div>
                    </div>
                  </div>

                  <v-select
                    v-if="isRealAppointment"
                    v-model="localData.templateId"
                    :items="appointmentTemplates"
                    item-text="title"
                    item-value="id"
                    label="Termin-Vorlage"
                    outlined
                    dense
                    :dark="isDarkMode"
                    hide-details
                    class="rounded-lg cockpit-control template-select-control mb-3"
                    :background-color="controlBackground"
                    :color="currentTheme.color"
                    :item-color="currentTheme.color"
                    @change="onTemplateChange">
                    <template v-slot:item="{ item }">
                      <div class="template-option">
                        <span
                          class="template-option-icon"
                          :style="{ background: hexToRgba(item.color || '#4CAF50', 0.16), borderColor: hexToRgba(item.color || '#4CAF50', 0.35) }">
                          <v-icon size="20" :color="item.color || '#4CAF50'">{{ item.icon || "mdi-calendar" }}</v-icon>
                        </span>
                        <span class="template-option-copy">
                          <span class="template-option-title">{{ item.title }}</span>
                          <span class="template-option-meta">
                            {{ getTemplateCategory(item) }}<template v-if="item.duration"> · {{ item.duration }} Min.</template>
                          </span>
                        </span>
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <div class="template-option template-option--selection">
                        <span
                          class="template-option-icon"
                          :style="{ background: hexToRgba(item.color || '#4CAF50', 0.16), borderColor: hexToRgba(item.color || '#4CAF50', 0.35) }">
                          <v-icon size="18" :color="item.color || '#4CAF50'">{{ item.icon || "mdi-calendar" }}</v-icon>
                        </span>
                        <span class="template-option-copy">
                          <span class="template-option-title">{{ item.title }}</span>
                          <span class="template-option-meta">{{ getTemplateCategory(item) }}</span>
                        </span>
                      </div>
                    </template>
                  </v-select>

                  <div class="cockpit-field-grid">
                    <div class="cockpit-field-cell">
                      <v-text-field
                        v-model="localData.title"
                        label="Titel"
                        outlined
                        dense
                        :dark="isDarkMode"
                        hide-details
                        prepend-inner-icon="mdi-format-title"
                        class="rounded-lg font-weight-bold cockpit-control"
                        :background-color="controlBackground"
                        :color="currentTheme.color"></v-text-field>
                    </div>
                    <div class="cockpit-field-cell">
                      <v-select
                        v-model="statusValue"
                        :items="localData.isTask ? taskStatusOptions : statusOptions"
                        :label="localData.isTask ? 'Aufgaben-Status' : 'Status'"
                        outlined
                        dense
                        :dark="isDarkMode"
                        hide-details
                        class="rounded-lg cockpit-control"
                        item-text="text"
                        item-value="value"
                        :prepend-inner-icon="statusIcon"
                        :background-color="controlBackground"
                        :color="currentTheme.color"
                        :item-color="currentTheme.color">
                      </v-select>
                    </div>
                  </div>
                </section>

                <section class="detail-section-card" :style="{ '--section-accent': currentTheme.color }">
                  <div class="detail-section-heading mb-2">
                    <span class="detail-section-icon" :style="`background: ${hexToRgba(currentTheme.color, 0.14)}`">
                      <v-icon small :color="currentTheme.color">mdi-palette-outline</v-icon>
                    </span>
                    <div>
                      <div class="detail-section-eyebrow">Feinschliff</div>
                      <div class="detail-section-title dynamic-text">{{ showDetailsDescription ? "Notizen & Darstellung" : "Darstellung" }}</div>
                    </div>
                  </div>

                  <v-textarea
                    v-if="showDetailsDescription"
                    v-model="localData.description"
                    :placeholder="descriptionPlaceholder"
                    outlined
                    hide-details
                    auto-grow
                    rows="3"
                    :dark="isDarkMode"
                    class="cockpit-notes-input mb-3"
                    :color="currentTheme.color"></v-textarea>

                  <button
                    type="button"
                    class="color-palette-toggle"
                    :aria-expanded="String(showColorPalette)"
                    @click="showColorPalette = !showColorPalette">
                    <span class="color-palette-current" :style="{ background: localData.color || currentTheme.color }"></span>
                    <span class="color-palette-copy">
                      <strong class="dynamic-text">Kalenderfarbe</strong>
                      <small>{{ showColorPalette ? "Farbe auswählen" : "Zum Ändern öffnen" }}</small>
                    </span>
                    <v-icon small>{{ showColorPalette ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                  </button>

                  <v-expand-transition>
                    <div v-show="showColorPalette" class="color-palette-grid mt-2" role="group" aria-label="Kalenderfarbe auswählen">
                      <button
                        v-for="c in colorOptions"
                        :key="c"
                        type="button"
                        class="color-swatch"
                        :class="{ active: localData.color === c }"
                        :aria-label="`Farbe ${c}`"
                        :aria-pressed="String(localData.color === c)"
                        @click="localData.color = c">
                        <span class="color-swatch-dot" :style="{ background: c }">
                          <v-icon v-if="localData.color === c" size="17" color="white">mdi-check</v-icon>
                        </span>
                      </button>
                    </div>
                  </v-expand-transition>
                </section>

                <section
                  v-if="isRealAppointment && localData.id"
                  class="appointment-change-history mt-3"
                  :style="{ '--history-accent': currentTheme.color }">
                  <button
                    type="button"
                    class="appointment-change-history__toggle"
                    :aria-expanded="String(appointmentChangesExpanded)"
                    @click="toggleAppointmentChanges">
                    <span class="appointment-change-history__icon">
                      <v-icon size="18" :color="currentTheme.color">mdi-history</v-icon>
                    </span>
                    <span class="appointment-change-history__copy">
                      <span class="appointment-change-history__title-row">
                        <strong class="dynamic-text">Termin-Verlauf</strong>
                        <span v-if="appointmentChangeEntries.length" class="appointment-change-history__count">
                          {{ appointmentChangeEntries.length }}
                        </span>
                      </span>
                      <small v-if="latestAppointmentChange" class="appointment-change-history__latest">
                        {{ appointmentChangeHeadline(latestAppointmentChange) }} · {{ latestAppointmentChange.actorName || "Unbekannt" }} ·
                        {{ formatAppointmentChangeDate(latestAppointmentChange.createdAt) }}
                      </small>
                      <small v-else-if="appointmentChangesError" class="appointment-change-history__latest">
                        Verlauf konnte nicht geladen werden
                      </small>
                      <small v-else-if="!loadingAppointmentChanges" class="appointment-change-history__latest">
                        Noch keine Änderung protokolliert
                      </small>
                    </span>
                    <v-progress-circular
                      v-if="loadingAppointmentChanges"
                      indeterminate
                      :color="currentTheme.color"
                      :size="18"
                      :width="2"></v-progress-circular>
                    <v-icon v-else small>{{ appointmentChangesExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                  </button>

                  <v-expand-transition>
                    <div v-show="appointmentChangesExpanded" class="appointment-change-history__body">
                      <div v-if="loadingAppointmentChanges" class="appointment-change-history__state">
                        <v-progress-circular indeterminate :color="currentTheme.color" :size="22" :width="2"></v-progress-circular>
                        <span>Verlauf wird geladen …</span>
                      </div>

                      <div v-else-if="appointmentChangesError" class="appointment-change-history__state appointment-change-history__state--error">
                        <v-icon small color="error">mdi-alert-circle-outline</v-icon>
                        <span>{{ appointmentChangesError }}</span>
                        <v-btn x-small text color="error" class="text-none" @click.stop="loadAppointmentChanges(true)">Erneut laden</v-btn>
                      </div>

                      <template v-else>
                        <div
                          v-if="currentAppointmentAssignment && currentAppointmentAssignment.helperColumn === 'no_show'"
                          class="appointment-change-history__no-show">
                          <v-icon small color="red darken-1">mdi-account-alert-outline</v-icon>
                          <span>
                            Aktuell „Nicht gekommen“
                            <strong v-if="currentAppointmentAssignment.employeeName">
                              · vorher zuständig: {{ currentAppointmentAssignment.employeeName }}
                            </strong>
                          </span>
                        </div>

                        <div v-if="appointmentChangeEntries.length" class="appointment-change-history__list">
                          <article
                            v-for="entry in appointmentChangeEntries"
                            :key="entry.eventId || entry.action + '-' + entry.createdAt"
                            class="appointment-change-entry">
                            <span class="appointment-change-entry__marker" :style="{ background: hexToRgba(appointmentChangeColor(entry), 0.14) }">
                              <v-icon :color="appointmentChangeColor(entry)" size="16">{{ appointmentChangeIcon(entry) }}</v-icon>
                            </span>
                            <div class="appointment-change-entry__content">
                              <strong class="appointment-change-entry__headline dynamic-text">
                                {{ appointmentChangeHeadline(entry) }}
                              </strong>
                              <span class="appointment-change-entry__meta">
                                {{ entry.actorName || "Unbekannt" }} · {{ formatAppointmentChangeDate(entry.createdAt) }}
                              </span>
                              <div
                                v-for="(detail, detailIndex) in appointmentChangeDetails(entry)"
                                :key="(entry.eventId || entry.createdAt) + '-' + detailIndex"
                                class="appointment-change-entry__detail">
                                {{ detail }}
                              </div>
                            </div>
                          </article>
                        </div>

                        <div v-else class="appointment-change-history__state">
                          <v-icon small color="grey">mdi-history</v-icon>
                          <span>Für diesen Termin gibt es noch keinen Verlauf.</span>
                        </div>
                      </template>
                    </div>
                  </v-expand-transition>
                </section>

                <!-- Live Conflict Alert Panel -->
                <v-expand-transition>
                  <div v-if="localConflicts.length > 0" class="cockpit-conflicts mt-3">
                    <div
                      v-for="(conflict, idx) in localConflicts"
                      :key="idx"
                      class="d-flex align-center pa-3 rounded-lg mb-2"
                      :style="{
                        background:
                          conflict.type === 'error'
                            ? 'rgba(239, 83, 80, 0.12)'
                            : conflict.type === 'warning'
                            ? 'rgba(255, 167, 38, 0.12)'
                            : 'rgba(129, 199, 132, 0.12)',
                        border: '1px solid ' + (conflict.type === 'error' ? '#ef5350' : conflict.type === 'warning' ? '#ffa726' : '#81c784'),
                        color: conflict.type === 'error' ? '#ef5350' : conflict.type === 'warning' ? '#ffa726' : '#81c784',
                      }">
                      <v-icon :color="conflict.type === 'error' ? 'error' : conflict.type === 'warning' ? 'warning' : 'success'" class="mr-2" small>
                        {{ conflict.type === "error" ? "mdi-alert-circle" : "mdi-alert-circle-outline" }}
                      </v-icon>
                      <span class="caption font-weight-bold flex-grow-1" style="color: inherit">
                        {{ conflict.message }}
                      </span>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>

            <!-- NEU: Hauptvertrag & Letzte Besuche in der rechten Spalte auf iPad/Mobil -->
            <div
              v-else-if="$vuetify.breakpoint.mdAndDown && !showMobileDetails && activeCustomer"
              class="d-flex flex-column gap-3 h-100 pr-md-2"
              style="overflow-y: auto">
              <!-- Title -->
              <div class="d-flex align-center mb-4 mt-2">
                <v-icon :color="currentTheme.color" class="mr-3">mdi-file-document-outline</v-icon>
                <div class="text-h6 font-weight-bold dynamic-text">Kundenkartei & Infos</div>
              </div>

              <!-- Hauptvertrag -->
              <div v-if="localData.specialType !== 'new_contract'" class="pa-4 rounded-lg mb-2" :style="membershipCardStyle(activeMainContract)">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="caption font-weight-bold text-uppercase" style="color: var(--text-muted); opacity: 0.85; letter-spacing: 0.5px">
                    Hauptvertrag
                  </div>
                  <v-chip
                    x-small
                    :color="appointmentContractChipColor"
                    :class="appointmentContractChipClass"
                    class="font-weight-black px-2">
                    {{ appointmentContractChipLabel }}
                  </v-chip>
                </div>
                <div class="font-weight-black text-h6 text-truncate mb-1" style="color: var(--text-main)">
                  {{ appointmentContractTitle }}
                </div>
                <div class="caption font-weight-bold" style="color: var(--text-muted)">
                  {{ contractStatusText }}
                </div>

                <v-btn
                  v-if="activeMainContract"
                  block
                  small
                  outlined
                  color="orange darken-2"
                  class="mt-3 text-none font-weight-bold"
                  @click="executeAction('upgrade')">
                  <v-icon left small>mdi-star-shooting</v-icon>
                  Upgrade & Zusatzpakete
                </v-btn>

                <div v-if="!activeMainContract" class="appointment-contract-state mt-3">
                  <div class="appointment-contract-state__row">
                    <v-progress-circular v-if="roodyContractsLoading" indeterminate :size="22" :width="3" color="orange darken-2" />
                    <v-icon v-else :color="relevantRoodyContract ? 'orange darken-2' : roodyContractsError ? 'red lighten-1' : 'grey'">
                      {{ relevantRoodyContract ? "mdi-file-clock-outline" : roodyContractsError ? "mdi-alert-circle-outline" : "mdi-file-plus-outline" }}
                    </v-icon>
                    <div class="appointment-contract-state__copy">
                      <strong>{{ roodyContractStateTitle }}</strong>
                      <span>{{ roodyContractStateHint }}</span>
                    </div>
                  </div>
                  <v-btn
                    v-if="relevantRoodyContract"
                    block
                    small
                    depressed
                    color="orange darken-2"
                    class="mt-3 white--text text-none font-weight-bold"
                    @click="openRelevantRoodyContract">
                    <v-icon left small>{{ relevantRoodyContract.status === "temporary" ? "mdi-pencil-outline" : "mdi-eye-outline" }}</v-icon>
                    {{ relevantRoodyContractActionLabel }}
                  </v-btn>
                  <v-btn
                    v-else-if="roodyContractsLoaded && canCreateCustomerContract"
                    block
                    small
                    depressed
                    :color="currentTheme.color"
                    class="mt-3 white--text text-none font-weight-bold"
                    @click="startCustomerContract">
                    <v-icon left small>mdi-file-sign</v-icon>
                    Vertrag erstellen
                  </v-btn>
                  <v-btn
                    v-else-if="roodyContractsError && roodyContractsError !== 'missing_right'"
                    small
                    text
                    color="orange darken-2"
                    class="mt-2 text-none"
                    @click="loadRoodyContractStatus(activeCustomerId)">
                    Erneut prüfen
                  </v-btn>
                </div>

                <div v-if="customerDetails && customerDetails.packages && customerDetails.packages.length > 0" class="mt-4 pt-4 border-top-light">
                  <div class="caption grey--text mb-3 text-uppercase font-weight-bold letter-spacing-1">Zusatzpakete</div>
                  <div class="d-flex flex-column gap-2">
                    <div
                      v-for="pkg in customerDetails.packages"
                      :key="pkg.CONTRACTID"
                      class="d-flex align-center justify-space-between pa-2 rounded-lg"
                      style="background: rgba(var(--rgb-color), 0.05); border: 1px solid rgba(var(--rgb-color), 0.05)">
                      <div class="d-flex align-center">
                        <v-avatar color="rgba(255,152,0,0.15)" size="24" class="mr-3">
                          <v-icon x-small color="orange accent-2">mdi-star-four-points</v-icon>
                        </v-avatar>
                        <span class="text-body-2 dynamic-text font-weight-medium">{{ pkg.NAME }}</span>
                      </div>
                      <v-chip x-small color="rgba(128,128,128,0.2)" class="grey--text">Bis {{ formatDate(pkg.ENDDATE) }}</v-chip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pausiert Info -->
              <div
                v-if="activeTimestop"
                class="glass-panel-light pa-4 rounded-lg border-left-indicator elevation-5 mb-4"
                style="border-left-color: #ff9800">
                <div class="caption grey--text text-uppercase">Pausiert</div>
                <div class="font-weight-bold dynamic-text text-body-1">Bis {{ formatDate(activeTimestop.timestopInfos.end) }}</div>
                <div class="caption orange--text">
                  {{ activeTimestop.tacData.Reason || "Pausiert" }}
                </div>
              </div>

              <!-- Letzte Besuche -->
              <div
                class="pa-4 rounded-lg"
                :style="{
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.02) !important' : '#ffffff !important',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} !important`,
                  borderLeft: `4px solid ${currentTheme.color} !important`,
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.04) !important',
                }">
                <div class="subtitle-2 dynamic-text mb-3 font-weight-bold" style="color: var(--text-main)">
                  <v-icon left small :color="currentTheme.color">mdi-door-open</v-icon> Letzte Besuche
                </div>
                <div v-if="processedAttendance.length > 0" class="d-flex align-center overflow-x-auto gap-2 pb-2 custom-scrollbar">
                  <div
                    v-for="(visit, i) in processedAttendance.slice(0, 5)"
                    :key="i"
                    class="checkin-pill d-flex align-center px-3 py-2 rounded-lg flex-shrink-0"
                    style="background: rgba(var(--rgb-color), 0.08); min-width: 120px; border: 1px solid rgba(var(--rgb-color), 0.1)">
                    <div class="mr-3 text-center" style="min-width: 32px">
                      <div class="font-weight-black dynamic-text" style="font-size: 1.1rem; line-height: 1.1">
                        {{ $moment(visit.date).format("DD") }}
                      </div>
                      <div class="grey--text" style="font-size: 0.62rem; white-space: nowrap; letter-spacing: 0.2px">
                        {{ $moment(visit.date).format("MMM") }}
                      </div>
                    </div>
                    <div>
                      <div class="caption dynamic-text font-weight-bold">{{ visit.start }}</div>
                      <div class="primary--text" style="font-size: 0.68rem">{{ visit.duration || "Aktiv" }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="caption grey--text">Keine Check-in Daten verfügbar.</div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider v-if="!showFinder" style="border-color: rgba(var(--rgb-color), 0.06)"></v-divider>
      <v-card-actions
        v-if="!showFinder"
        class="cockpit-footer px-4 px-md-6 py-2 flex-shrink-0 z-index-20"
        style="background: var(--actions-bg) !important">
        <v-btn text color="red darken-1" class="cockpit-delete-button text-none" @click="openDeleteDialog">
          <v-icon left small>mdi-trash-can</v-icon>
          <span class="d-none d-sm-inline">Löschen</span>
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn text class="mr-2 dynamic-text opacity-70 d-sm-none" @click="closeDialog">Zurück</v-btn>
        <v-btn text class="cockpit-cancel-button mr-2 dynamic-text d-none d-sm-flex text-none" @click="closeDialog">Abbrechen</v-btn>

        <v-btn
          large
          depressed
          :color="currentTheme.color"
          class="cockpit-save-button white--text font-weight-bold px-6 px-md-10 rounded-lg box-shadow-glow text-none"
          @click="save">
          <v-icon left v-if="$vuetify.breakpoint.xsOnly">mdi-check</v-icon>
          <span class="d-none d-sm-inline">Speichern</span>
          <span class="d-sm-none">OK</span>
        </v-btn>
      </v-card-actions>

      <!-- Delete Reason Dialog -->
      <v-dialog v-model="showDeleteReasonDialog" max-width="440" persistent>
        <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
          <div class="pa-5">
            <div class="d-flex align-center mb-4" style="gap: 12px">
              <div
                style="
                  width: 40px;
                  height: 40px;
                  border-radius: 10px;
                  background: rgba(244, 67, 54, 0.15);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                <v-icon color="red accent-2">mdi-trash-can-outline</v-icon>
              </div>
              <div>
                <div style="font-size: 1rem; font-weight: 700; color: white">Termin löschen</div>
                <div style="font-size: 0.7rem; color: rgba(255, 255, 255, 0.4)">{{ localData.title }}</div>
              </div>
            </div>

            <!-- Scope selection if part of a series -->
            <div v-if="localData.seriesId" class="mt-1 mb-4">
              <div
                style="font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.5px; color: rgba(255, 255, 255, 0.35); font-weight: 700"
                class="mb-2">
                Löschungs-Umfang
              </div>
              <v-radio-group v-model="deleteScope" dense dark hide-details class="mt-0 pt-0">
                <v-radio value="single" color="red accent-2">
                  <template v-slot:label>
                    <span style="font-size: 0.8rem; color: white">Nur diese{{ localData.isTask ? "s Ämtli" : "n Termin" }} löschen</span>
                  </template>
                </v-radio>
                <v-radio value="future" color="red accent-2" class="mt-1">
                  <template v-slot:label>
                    <span style="font-size: 0.8rem; color: white">Diese{{ localData.isTask ? "s" : "n" }} und alle zukünftigen löschen</span>
                  </template>
                </v-radio>
                <v-radio value="all" color="red accent-2" class="mt-1">
                  <template v-slot:label>
                    <span style="font-size: 0.8rem; color: white">Alle {{ localData.isTask ? "Ämtlis" : "Termine" }} der Serie löschen</span>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>

            <div
              style="font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.5px; color: rgba(255, 255, 255, 0.3); font-weight: 600"
              class="mb-2">
              Grund für die Löschung {{ localData.isTask ? "(Optional)" : "*" }}
            </div>
            <v-textarea
              v-model="deleteReason"
              placeholder="z.B. Kunde hat abgesagt, Termin wurde verschoben..."
              outlined
              dense
              rows="2"
              dark
              hide-details
              autofocus
              style="font-size: 0.85rem"></v-textarea>

            <div class="d-flex justify-end mt-4" style="gap: 8px">
              <v-btn text small @click="cancelDelete" style="color: rgba(255, 255, 255, 0.4); text-transform: none"> Abbrechen </v-btn>
              <v-btn
                small
                depressed
                :disabled="!localData.isTask && !deleteReason.trim()"
                style="text-transform: none; font-weight: 600; font-size: 0.8rem; border-radius: 8px"
                :style="
                  localData.isTask || deleteReason.trim()
                    ? 'background: linear-gradient(135deg, #e53935, #b71c1c) !important; color: white !important'
                    : ''
                "
                @click="confirmDelete">
                <v-icon left x-small>mdi-trash-can</v-icon>
                Löschen bestätigen
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <anamnese-bogen ref="anamneseBogenRef" @saved="refreshCustomer"></anamnese-bogen>
      <customer-interaction ref="customerInteraction" @interactionCreated="onInteractionCreated"></customer-interaction>
      <upgrade-contract
        v-if="activeCustomer && showContractDialog"
        :selectedCustomer="activeCustomer._id"
        @hide="showContractDialog = false"
        :dialog="showContractDialog"
        @created="$toast.success('Vertrag angepasst')"></upgrade-contract>

      <v-dialog v-model="showHistoryDialog" max-width="500" content-class="glass-dialog-wrapper">
        <v-card class="glass-card-theme pa-5" :style="[innerDialogStyle, themeVars]" v-if="selectedHistoryEntry">
          <div class="d-flex justify-space-between align-start">
            <div class="text-h5 dynamic-text font-weight-bold mb-1">{{ selectedHistoryEntry.title || "Termin" }}</div>
            <v-btn icon small class="dynamic-text" @click="showHistoryDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div class="caption grey--text mb-4">
            {{ formatDate(selectedHistoryEntry.date) }} •
            {{
              selectedHistoryEntry.assignedEmployee
                ? `${selectedHistoryEntry.assignedEmployee.vorname || ""} ${selectedHistoryEntry.assignedEmployee.nachname || ""}`.trim()
                : "Unbekannt"
            }}
          </div>
          <div
            class="glass-panel-light pa-3 rounded-lg dynamic-text text-body-2 custom-scrollbar"
            style="white-space: pre-wrap; max-height: 400px; overflow-y: auto">
            {{ selectedHistoryEntry.description || "Keine Notizen" }}
          </div>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showWorkoutWarningDialog" max-width="400" content-class="glass-dialog-wrapper">
        <v-card class="glass-card-theme pa-5" :style="[innerDialogStyle, themeVars]">
          <div class="d-flex align-center mb-4">
            <v-icon color="warning" large class="mr-3">mdi-alert</v-icon>
            <div class="text-h6 dynamic-text font-weight-bold">Neuer Trainingsplan</div>
          </div>
          <div class="dynamic-text text-body-2 mb-6">
            Mit dem Erstellen eines neuen Plans wird der alte für den Kunden unsichtbar.<br /><br />Gleichwohl fortfahren?
          </div>
          <div class="d-flex justify-end gap-2">
            <v-btn text color="grey" @click="showWorkoutWarningDialog = false">Abbrechen</v-btn>
            <v-btn depressed color="warning" class="font-weight-bold black--text" @click="proceedNewWorkout">Fortfahren</v-btn>
          </div>
        </v-card>
      </v-dialog>
    </v-card>
    <user-contact-dialog v-if="mergedCustomer" v-model="showContactDialog" :user="mergedCustomer" :color="currentTheme.color"> </user-contact-dialog>
    <register
      @register="newAccountRegistered"
      :auto-login="false"
      :requirePassword="false"
      @close="showRegisterDialog = false"
      :active="showRegisterDialog"></register>
  </v-dialog>
</template>

<script>
import moment from "moment"
import { mapActions, mapGetters } from "vuex"
import userService from "@/services/userService"
import AnamneseBogen from "@/components/anamneseBogenCreate"
import UpgradeContract from "@/views/dashboard/contract/components/upgradeContract_sebredesign"
import UserContactDialog from "@/components/tools/contactDialog"
import register from "@/views/launchScreen/register.vue"
import api from "@/api"
import UserSearch from "@/components/userSearch"
import customerInteraction from "@/components/employee/customerInteraction"
import WorkCalendarAppointmentFinder from "./WorkCalendarAppointmentFinder"

export default {
  name: "WorkCalendarEditDialog",
  components: { AnamneseBogen, UpgradeContract, UserContactDialog, register, UserSearch, customerInteraction, WorkCalendarAppointmentFinder },
  props: {
    value: { default: false },
    editData: { type: Object, default: () => ({}) },
    customerDetails: { default: null },
    loading: { type: Boolean, default: false },
    employees: { type: Array, default: () => [] },
    finderEmployees: { type: Array, default: () => [] },
    currentDate: { default: null },
    isDarkMode: { type: Boolean, default: false },
    appointmentTemplates: { type: Array, default: () => [] },
  },
  data() {
    return {
      activeTab: 0,
      localData: {},
      colorOptions: [
        "#2196F3",
        "#1976D2",
        "#0D47A1",
        "#4CAF50",
        "#2E7D32",
        "#00963f",
        "#FF9800",
        "#F57C00",
        "#E65100",
        "#F44336",
        "#C62828",
        "#9C27B0",
        "#7B1FA2",
        "#00BCD4",
        "#0097A7",
        "#607D8B",
        "#455A64",
        "#E91E63",
        "#AD1457",
        "#FFD700",
        "#FF6F00",
        "#3F51B5",
        "#283593",
        "#009688",
        "#00695C",
      ],
      statusOptions: [
        { text: "Geplant", value: "planned" },
        { text: "Erschienen (Check-in)", value: "checkedin" },
        { text: "Erledigt", value: "done" },
        { text: "Abgesagt", value: "cancelled" },
        { text: "No-Show (Nicht erschienen)", value: "noshow" },
      ],
      taskStatusOptions: [
        { text: "Geplant", value: false },
        { text: "Erledigt", value: true },
      ],
      appointmentHistory: [],
      loadingHistory: false,
      showHistoryDialog: false,
      selectedHistoryEntry: null,
      appointmentChangeEntries: [],
      appointmentChangeMeta: null,
      loadingAppointmentChanges: false,
      appointmentChangesError: "",
      appointmentChangesExpanded: false,
      appointmentChangesLoadedId: null,
      appointmentChangesRequestId: 0,
      showContractDialog: false,
      roodyContracts: [],
      roodyContractsLoading: false,
      roodyContractsLoaded: false,
      roodyContractsError: "",
      roodyContractsCustomerId: null,
      roodyContractRequestId: 0,
      showContactDialog: false,
      showRegisterDialog: false,
      showSearchExistingCustomer: false,
      showCustomerChange: false,
      activeCustomerIndex: 0,
      newChecklistItem: "",
      asynchronousAttendance: [],
      loadingAttendance: false,
      showWorkoutWarningDialog: false,

      // NEU: Für asynchrone Pläne
      loadingWorkouts: false,
      fetchedWorkouts: [],

      // Soft-Delete Dialog
      showDeleteReasonDialog: false,
      deleteReason: "",
      deleteScope: "single",
      refreshingCustomer: false,
      bypassWarning: false,
      finderChangedData: false,
      finderSelection: null,
      finderSaving: false,
      showFinder: false,
      showMobileDetails: false,
      showColorPalette: false,
      showCustomerImagePreview: false,
    }
  },
  watch: {
    activeCustomerId: {
      immediate: true,
      handler(customerId) {
        this.loadRoodyContractStatus(customerId)
      },
    },
    value(newVal) {
      if (newVal) {
        this.bypassWarning = false
        this.showMobileDetails = false
        this.showCustomerImagePreview = false
        this.showCustomerChange = false
        this.$nextTick(() => this.loadAppointmentChanges())
      } else {
        this.appointmentChangesRequestId += 1
        this.loadingAppointmentChanges = false
        this.appointmentChangesLoadedId = null
      }
    },
    editData: {
      immediate: true,
      handler(val) {
        if (val) {
          this.finderChangedData = false
          this.finderSelection = null
          this.finderSaving = false
          this.localData = JSON.parse(JSON.stringify(val))
          // Der Finder ist ausschliesslich fuer echte Termine. Tasks und Pausen
          // duerfen ihn auch bei einem veralteten openFinderDirectly-Flag nie oeffnen.
          this.showFinder = Boolean(val.openFinderDirectly && !this.localData.isTask && !this.localData.isPause)
          this.showColorPalette = false
          this.showCustomerImagePreview = false
          if (!this.localData.checklist) this.$set(this.localData, "checklist", [])
          if (this.localData.description === undefined) this.$set(this.localData, "description", "")
          if (!this.localData.instructions) this.$set(this.localData, "instructions", "")

          if (this.localData.start && this.localData.end && !this.localData.duration) {
            const mStart = moment(this.localData.start, "HH:mm")
            const mEnd = moment(this.localData.end, "HH:mm")
            this.$set(this.localData, "duration", mEnd.diff(mStart, "minutes"))
          }

          if (!this.localData.date) {
            this.$set(this.localData, "date", this.currentDate)
          }
          if (!this.localData.status) this.$set(this.localData, "status", "planned")
          this.activeTab = 0
          this.activeCustomerIndex = 0
          this.showSearchExistingCustomer = false
          this.showCustomerChange = false
          this.appointmentHistory = []
          this.appointmentChangesRequestId += 1
          this.appointmentChangeEntries = []
          this.appointmentChangeMeta = null
          this.loadingAppointmentChanges = false
          this.appointmentChangesError = ""
          this.appointmentChangesExpanded = false
          this.appointmentChangesLoadedId = null

          if (this.activeCustomer?._id) {
            this.loadAttendance()
            this.loadWorkouts() // <--- NEU
          }
          if (this.value && val.id && !val.isTask && !val.isPause) {
            this.$nextTick(() => this.loadAppointmentChanges())
          }
        }
      },
    },
    "localData.checklist": {
      deep: true,
      handler(val) {
        if (val && val.length > 0) {
          const allDone = val.every((item) => item.isDone)
          if (this.localData.isDone !== allDone) {
            this.localData.isDone = allDone
          }
        }
      },
    },
    "localData.isDone"(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.localData.checklist && this.localData.checklist.length > 0) {
        const allDone = this.localData.checklist.every((item) => item.isDone)
        if (newVal && !allDone) {
          this.localData.checklist.forEach((item) => {
            item.isDone = true
          })
        } else if (!newVal && allDone) {
          this.localData.checklist.forEach((item) => {
            item.isDone = false
          })
        }
      }
    },
    activeTab(newVal) {
      if (newVal !== 0) {
        this.showMobileDetails = false
      }
    },
  },
  computed: {
    showLeftColumn() {
      if (this.showFinder && this.isRealAppointment) return true
      if (this.$vuetify.breakpoint.smAndDown) {
        // Eintraege ohne Kunden (Task, Pause, Gast, Studio-Termin) besitzen
        // links ihre inhaltliche Bearbeitung. Auf Tablet/Mobil wird sie vor den
        // kompakten Stammdaten angezeigt, statt komplett zu verschwinden.
        if (!this.activeCustomer) return true
        return !this.showMobileDetails
      }
      return true
    },
    showDetailsColumn() {
      if (this.showFinder && this.isRealAppointment) return false
      if (this.$vuetify.breakpoint.smAndDown) {
        if (!this.activeCustomer) return true
        return this.showMobileDetails
      }
      return true
    },
    isRealAppointment() {
      return Boolean(this.localData && !this.localData.isTask && !this.localData.isPause)
    },
    latestAppointmentChange() {
      return this.appointmentChangeEntries[0] || null
    },
    currentAppointmentAssignment() {
      return this.appointmentChangeMeta?.currentAssignment || null
    },
    isFinderExpanded() {
      return Boolean(this.showFinder && this.isRealAppointment)
    },
    isDialogFullscreen() {
      // Auf Tablet/Mobil bleibt der Edit-Dialog wie bisher fullscreen. Auf
      // Desktop expandiert derselbe Dialog über eine animierbare CSS-Klasse,
      // statt während des Öffnens hart in einen zweiten Fullscreen-Modus zu springen.
      return Boolean(this.$vuetify.breakpoint.mdAndDown)
    },
    dialogContentClass() {
      return ["glass-dialog-wrapper", "work-calendar-edit-dialog", this.isFinderExpanded ? "work-calendar-edit-dialog--finder" : ""]
        .filter(Boolean)
        .join(" ")
    },
    controlBackground() {
      return this.isDarkMode ? "rgba(255,255,255,0.055)" : "rgba(255,255,255,0.82)"
    },
    selectedAppointmentTemplate() {
      if (!this.localData || !this.localData.templateId) return null
      return (
        (this.appointmentTemplates || []).find((template) => {
          const templateId = template.id || template._id
          return String(templateId) === String(this.localData.templateId)
        }) || null
      )
    },
    eligibleAppointmentEmployees() {
      if (!this.isRealAppointment) return this.employees || []
      return (this.employees || []).filter((employee) => this.isEmployeeEligibleForAppointment(employee))
    },
    eligibleFinderEmployees() {
      const source = this.finderEmployees && this.finderEmployees.length ? this.finderEmployees : this.employees
      return (source || []).filter((employee) => this.isEmployeeEligibleForAppointment(employee))
    },
    appointmentTypeMeta() {
      if (this.localData.isTask) {
        return {
          category: "ÄMTLI & AUFGABE",
          title: this.localData.title || "Aufgabe",
          icon: this.localData.icon || "mdi-format-list-checks",
        }
      }
      if (this.localData.isPause) {
        return {
          category: "PAUSE & SCHICHT",
          title: this.localData.title || "Mitarbeiter-Pause",
          icon: this.localData.icon || "mdi-coffee-outline",
        }
      }

      const source = this.selectedAppointmentTemplate || this.localData || {}
      return {
        category: this.getTemplateCategory(source),
        title: this.localData.title || source.title || this.currentTheme.title || "Termin",
        icon: source.icon || this.localData.icon || this.currentTheme.icon || "mdi-calendar",
      }
    },
    appointmentTimeSummary() {
      const dateValue = this.localData && this.localData.date
      const dateMoment = dateValue ? moment(dateValue, "YYYY-MM-DD", true) : null
      const dateLabel = dateMoment && dateMoment.isValid() ? dateMoment.format("DD.MM.YYYY") : dateValue || "Datum offen"
      const start = (this.localData && this.localData.start) || "--:--"
      const duration = Number(this.localData && this.localData.duration)
      const end =
        start !== "--:--" && Number.isFinite(duration) && duration > 0 ? moment(start, "HH:mm").add(duration, "minutes").format("HH:mm") : null
      return `${dateLabel} · ${start}${end ? `–${end}` : ""}`
    },
    appointmentEmployeeLabel() {
      if (!this.localData || !this.localData.colId) return ""
      const employee = this.extendedEmployees.find((item) => item && !item.divider && String(item.id) === String(this.localData.colId))
      return employee ? employee.name : "Mitarbeiter offen"
    },
    reassignmentReasonText() {
      if (this.localData?.orphanedEmployeeReason === "employee_without_visible_shift") {
        return this.localData.orphanedEmployeeName
          ? `${this.localData.orphanedEmployeeName} hat an diesem Tag keine sichtbare Schicht mehr.`
          : "Der zugewiesene Mitarbeiter hat an diesem Tag keine sichtbare Schicht mehr."
      }
      if (this.localData?.orphanedEmployeeReason === "employee_assignment_pending") {
        return this.localData.orphanedEmployeeName
          ? `${this.localData.orphanedEmployeeName} ist weiterhin nicht als sichtbare Arbeitsspalte verfügbar.`
          : "Die bisherige Mitarbeiter-Zuordnung ist nicht mehr als sichtbare Arbeitsspalte verfügbar."
      }
      return "Der ursprünglich zugewiesene Mitarbeiter ist nicht mehr im Arbeitskalender verfügbar."
    },
    tacBillingMeta() {
      const billing = this.localData?.tacBilling
      if (!billing?.serviceType) return null
      const service = billing.serviceType === "massage" ? "Massage" : "Personal Training"
      const stateMeta = {
        pending: { label: "TAC wird verarbeitet", color: "#FF9800" },
        debited: { label: "1 Einheit abgebucht", color: "#4CAF50" },
        cancellation_pending: { label: "TAC-Storno läuft", color: "#FF9800" },
        cancelled: { label: "TAC storniert", color: "#90A4AE" },
        unknown: { label: "TAC-Status prüfen", color: "#F44336" },
        failed: { label: "TAC fehlgeschlagen", color: "#F44336" },
      }[billing.state] || { label: "TAC-Status prüfen", color: "#F44336" }
      const remaining =
        billing.remainingAfterDebit !== undefined && billing.remainingAfterDebit !== null ? ` · noch ${billing.remainingAfterDebit}` : ""
      return {
        color: stateMeta.color,
        text: `${service}: ${stateMeta.label}${remaining}`,
        title: [billing.accessPassName, billing.errorMessage].filter(Boolean).join(" · "),
      }
    },
    showDetailsDescription() {
      // Aufgaben und Eintraege ohne echten Kunden haben bereits links ihren
      // vollwertigen Notiz-Editor. Rechts bleibt so nur eine einzige Quelle.
      return Boolean(this.activeCustomer && !this.localData.isTask && !this.localData.isPause)
    },
    descriptionPlaceholder() {
      const type = String((this.localData && this.localData.specialType) || "").toLowerCase()
      if (["trial", "intro", "followup", "workout", "trainingplan", "milon"].includes(type)) {
        return "Trainingsziele, Einstellungen, Fortschritt oder nächste Schritte ..."
      }
      if (["consultation", "new_contract", "upgrade"].includes(type)) {
        return "Absprachen, Wünsche, Angebot oder nächste Schritte ..."
      }
      if (["anamnese", "nutrition", "check", "abschlusstermin_11d"].includes(type)) {
        return "Ergebnisse, Beobachtungen und vereinbarte Massnahmen ..."
      }
      if (this.localData.isTask) return "Notiz, Ablauf oder Übergabe ..."
      if (this.localData.isPause) return "Optionaler Hinweis zu dieser Pause ..."
      return "Besonderheiten, Gesprächsnotizen oder nächste Schritte ..."
    },
    hasUnsavedChanges() {
      return this.finderChangedData || this.customerAssignmentChanged
    },
    ...mapGetters("studioStore", ["getSelectedEmployee"]),
    isSelfSelectedEmployee() {
      return this.getSelectedEmployee && this.getSelectedEmployee.user === userService.getUserId()
    },
    canSwitchEmployee() {
      return this.getSelectedEmployee && !this.isSelfSelectedEmployee
    },
    showTrainerMismatchWarning() {
      if (!this.getSelectedEmployee) return false
      if (!this.canSwitchEmployee) return false
      if (!this.localData || !this.localData.colId) return false
      if (this.localData.colId === "ma_info" || this.localData.colId === "no_show") return false
      return String(this.getSelectedEmployee.user) !== String(this.localData.colId)
    },
    currentAppointmentTrainerName() {
      if (!this.localData || !this.localData.colId) return ""
      const emp = (this.employees || []).find((e) => String(e.id) === String(this.localData.colId))
      return emp ? emp.name : "Unbekannt"
    },
    /* ----- THEME COMPUTED PROPERTIES START ----- */
    themeVars() {
      if (this.isDarkMode) {
        return {
          "--bg-color": "#1c1c1e",
          "--text-color": "#ffffff",
          "--rgb-color": "255, 255, 255",
          "--overlay-grad": "linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, #1c1c1e 100%)",
          "--actions-bg": "#1c1c1e",
          "--card-bg": "#2c2c2e",
          "--border-color": "#333",
          "--right-panel-md": "rgba(0, 0, 0, 0.2)",
          "--right-panel-sm": "rgba(0, 0, 0, 0.3)",
        }
      } else {
        return {
          "--bg-color": "#ffffff",
          "--text-color": "#121212",
          "--rgb-color": "0, 0, 0",
          "--overlay-grad": "linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, #ffffff 100%)",
          "--actions-bg": "#ffffff",
          "--card-bg": "#f2f4f7",
          "--border-color": "#e0e0e0",
          "--right-panel-md": "rgba(0, 0, 0, 0.03)",
          "--right-panel-sm": "rgba(0, 0, 0, 0.05)",
        }
      }
    },
    cardStyle() {
      return {
        ...this.themeVars,
        borderTop: "0",
        height: "90vh",
        background: "var(--bg-color) !important",
      }
    },
    innerDialogStyle() {
      return {
        background: "var(--card-bg) !important",
        border: "1px solid var(--border-color)",
      }
    },
    /* ----- THEME COMPUTED PROPERTIES END ----- */

    statusValue: {
      get() {
        return this.localData.isTask ? this.localData.isDone : this.localData.status
      },
      set(val) {
        if (this.localData.isTask) {
          this.$set(this.localData, "isDone", val)
        } else {
          this.$set(this.localData, "status", val)
        }
      },
    },

    statusIcon() {
      if (this.localData.isTask) {
        return this.localData.isDone ? "mdi-check-circle" : "mdi-checkbox-blank-circle-outline"
      }
      const s = this.localData.status
      if (s === "checkedin" || s === "done") return "mdi-check-circle"
      if (s === "cancelled") return "mdi-cancel"
      if (s === "noshow") return "mdi-eye-off"
      return "mdi-calendar-clock"
    },
    currentTheme() {
      if (this.localData.color) {
        return {
          color: this.localData.color,
          icon: this.localData.icon || this.getIconByType(),
          title: this.localData.title || "Termin",
        }
      }
      const type = (this.localData.specialType || "").toLowerCase()

      // Bestehende Typen
      if (type === "trial") return { color: "#F57C00", icon: "mdi-account-star", title: "Probetraining" }
      if (type === "intro") return { color: "var(--v-primary-base)", icon: "mdi-dumbbell", title: "Einführung" }
      if (type === "followup") return { color: "var(--v-primary-base)", icon: "mdi-dumbbell", title: "Ergänzung" }
      if (type === "workout") return { color: "var(--v-primary-base)", icon: "mdi-dumbbell", title: this.localData.title || "Training" }
      if (type === "anamnese") return { color: "#00ACC1", icon: "mdi-clipboard-pulse", title: "Anamnese" }

      // Neue Typen
      if (type === "check") return { color: "#009688", icon: "mdi-calendar-check", title: "Kontrolltermin" }
      if (type === "milon") return { color: "#3F51B5", icon: "mdi-sync", title: "Milon Einführung" }
      if (type === "nutrition") return { color: "#8BC34A", icon: "mdi-food-apple", title: "Ernährungsberatung" }
      if (type === "consultation") return { color: "#5C6BC0", icon: "mdi-account-tie", title: "Beratungstermin" }
      if (type === "trainingplan") return { color: "#0288D1", icon: "mdi-clipboard-list", title: "Trainingsplan" }

      if (type === "new_contract") return { color: "#37474F", icon: "mdi-file-document-edit", title: "Vertrag erstellen" }
      if (type === "upgrade") return { color: "#455A64", icon: "mdi-star-circle", title: "Vertrag / Upgrade" }

      return { color: "#00963f", icon: "mdi-calendar", title: "Termin" }
    },

    // Neue Computed Property für die Ansichtssteuerung
    showTrainingFlow() {
      const type = (this.localData.specialType || "").toLowerCase()
      // Alle Typen, die Anamnese + Trainingsplan anzeigen sollen
      const trainingTypes = ["trial", "intro", "followup", "check", "milon", "nutrition", "consultation"]
      return trainingTypes.includes(type)
    },
    hasNoAccountData() {
      if (this.activeCustomerIndex === 1) {
        return !!(this.localData.noAccountData2 && this.localData.noAccountData2.firstName)
      }
      return !!(this.localData.noAccountData && this.localData.noAccountData.firstName)
    },
    activeCustomer() {
      if (this.activeCustomerIndex === 1) {
        return this.localData?.customer2 || null
      }
      return this.localData?.customer || null
    },
    activeCustomerId() {
      const customer = this.activeCustomer
      if (!customer) return ""
      const nestedUser = customer.user
      return String(customer._id || customer.id || (nestedUser && (nestedUser._id || nestedUser.id)) || nestedUser || "")
    },
    activeMainContract() {
      const details = this.customerDetails
      if (!details || !details.mainContract) return null
      const nestedUser = details.user
      const detailsUserId = nestedUser && typeof nestedUser === "object" ? nestedUser._id || nestedUser.id : nestedUser
      if (detailsUserId && this.activeCustomerId && String(detailsUserId) !== this.activeCustomerId) return null
      return details.mainContract
    },
    activeCustomerRating() {
      const activeCustomerId = this.activeCustomer?._id || this.activeCustomer?.id || this.activeCustomer?.user
      if (!activeCustomerId || !Array.isArray(this.localData?.customerRatings)) return null
      return (
        this.localData.customerRatings.find((rating) => {
          const ratingUserId = rating?.user?._id || rating?.user?.id || rating?.user
          return ratingUserId && String(ratingUserId) === String(activeCustomerId)
        }) || null
      )
    },
    activeCustomerRatingTooltip() {
      if (!this.activeCustomerRating) return ""
      const tagLabels = {
        friendly: "Freundlich",
        competent: "Kompetent",
        helpful: "Hilfreich",
        motivating: "Motivierend",
        punctual: "Pünktlich",
        pleasant: "Angenehm",
      }
      const tags = (this.activeCustomerRating.tags || []).map((tag) => tagLabels[tag] || tag).join(", ")
      return [`${this.activeCustomerRating.stars}/5 Sterne`, tags, this.activeCustomerRating.comment || ""].filter(Boolean).join(" · ")
    },
    mergedCustomer() {
      const details = this.customerDetails || {}
      const appointmentCustomer = this.activeCustomer || {}
      const detailsId = details._id || details.id || details.user?._id || details.user?.id || details.user
      const appointmentCustomerId =
        appointmentCustomer._id || appointmentCustomer.id || appointmentCustomer.user?._id || appointmentCustomer.user?.id || appointmentCustomer.user
      const belongsToActiveCustomer = detailsId && appointmentCustomerId && String(detailsId) === String(appointmentCustomerId)
      return {
        ...appointmentCustomer,
        ...(belongsToActiveCustomer ? details : {}),
        bild: (belongsToActiveCustomer ? details.bild : null) || appointmentCustomer.bild || null,
        tac_image: (belongsToActiveCustomer ? details.tac_image : null) || appointmentCustomer.tac_image || null,
      }
    },
    activeNoAccountData() {
      if (this.activeCustomerIndex === 1) {
        return this.localData.noAccountData2 || {}
      }
      return this.localData.noAccountData || {}
    },
    hasTwoCustomers() {
      return !!(this.localData?.customer2 || this.localData?.noAccountData2)
    },
    customer1Label() {
      if (this.localData?.customer) return `${this.localData.customer.vorname} ${this.localData.customer.nachname}`
      if (this.localData.noAccountData && this.localData.noAccountData.firstName)
        return `${this.localData.noAccountData.firstName} ${this.localData.noAccountData.lastName}`
      return "Person 1"
    },
    customer2Label() {
      if (this.localData?.customer2) return `${this.localData.customer2.vorname} ${this.localData.customer2.nachname}`
      if (this.localData.noAccountData2 && this.localData.noAccountData2.firstName)
        return `${this.localData.noAccountData2.firstName} ${this.localData.noAccountData2.lastName}`
      return "Person 2"
    },
    appointmentCustomerSlots() {
      const slots = []
      const addSlot = (index, customer, noAccountData) => {
        if (!customer) return
        const firstName = customer.vorname || customer.firstName || ""
        const lastName = customer.nachname || customer.lastName || ""
        slots.push({
          index,
          customer,
          noAccountData,
          slotLabel: this.hasTwoCustomers ? `Person ${index + 1}` : "Kunde",
          name: `${firstName} ${lastName}`.trim() || `Person ${index + 1}`,
        })
      }
      addSlot(0, this.localData?.customer, this.localData?.noAccountData)
      addSlot(1, this.localData?.customer2, this.localData?.noAccountData2)
      return slots
    },
    customerAssignmentChanged() {
      const referenceId = (value) => {
        if (!value) return null
        if (typeof value !== "object") return value
        const nestedUser = value.user
        return value._id || value.id || (typeof nestedUser === "object" ? nestedUser?._id || nestedUser?.id : nestedUser) || null
      }
      return (
        String(referenceId(this.localData?.customer) || "") !== String(referenceId(this.editData?.customer) || "") ||
        String(referenceId(this.localData?.customer2) || "") !== String(referenceId(this.editData?.customer2) || "")
      )
    },
    customerChangeTitle() {
      return this.hasTwoCustomers ? `Person ${this.activeCustomerIndex + 1} ersetzen` : "Kunde ersetzen"
    },
    customerChangeSearchLabel() {
      return this.hasTwoCustomers ? `Neuen Kunden für Person ${this.activeCustomerIndex + 1} suchen...` : "Neuen Kunden suchen..."
    },
    headerStyle() {
      const color = this.currentTheme.color || "#4CAF50"
      return {
        "--header-accent": color,
        borderBottom: `1px solid ${this.hexToRgba(color, 0.22)}`,
        background: this.isDarkMode
          ? `linear-gradient(90deg, ${this.hexToRgba(color, 0.1)} 0%, rgba(255, 255, 255, 0.015) 34%, rgba(255, 255, 255, 0.015) 100%)`
          : `linear-gradient(90deg, ${this.hexToRgba(color, 0.07)} 0%, rgba(255, 255, 255, 0.96) 34%, rgba(255, 255, 255, 0.98) 100%)`,
      }
    },
    completedChecklistItems() {
      if (!this.localData.checklist) return 0
      return this.localData.checklist.filter((item) => item.isDone).length
    },
    extendedEmployees() {
      const helpers = [
        { id: "ma_info", name: "📋 MA Info (Hilfsspalte)" },
        { id: "no_show", name: "❌ Nicht gekommen (Hilfsspalte)" },
      ]
      const employees = this.isRealAppointment ? [...this.eligibleAppointmentEmployees] : [...(this.employees || [])]
      const originalEmployeeId = String(this.editData?.colId || "")
      const originalEmployee = (this.employees || []).find((employee) => String(employee?.id || "") === originalEmployeeId)
      if (originalEmployee && !employees.some((employee) => String(employee?.id || "") === originalEmployeeId)) {
        employees.push({ ...originalEmployee, name: `${originalEmployee.name || "Mitarbeiter"} (bestehender Termin)` })
      }
      if (this.finderSelection?.colId && !employees.some((employee) => String(employee.id) === String(this.finderSelection.colId))) {
        employees.push({
          id: this.finderSelection.colId,
          name: this.finderSelection.colName || "Mitarbeiter",
          picture: this.finderSelection.colPicture || null,
        })
      }
      return [...employees, { divider: true }, ...helpers]
    },
    checklistProgress() {
      if (!this.localData.checklist || this.localData.checklist.length === 0) return 0
      return (this.completedChecklistItems / this.localData.checklist.length) * 100
    },
    anamneseInfo() {
      const mh = this.customerDetails?.documents?.medicalHistory || []
      const old = this.customerDetails?.documents?.anamnese || []

      if (mh.length > 0) {
        const newest = [...mh].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
        return { exists: true, isNew: true, date: newest.createdAt, obj: newest }
      }
      if (old.length > 0) {
        const newest = [...old].sort((a, b) => b.date - a.date)[0]
        const d = newest.date > 10000000000 ? newest.date : newest.date * 1000
        return { exists: true, isNew: false, date: d, obj: newest }
      }
      return { exists: false }
    },
    sortedMedicalHistory() {
      return (this.customerDetails?.documents?.medicalHistory || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    sortedOldAnamnese() {
      return (this.customerDetails?.documents?.anamnese || []).sort((a, b) => b.date - a.date)
    },

    // --- NEU: Nutzen die gefetchten Workouts ---
    allWorkouts() {
      return this.fetchedWorkouts
    },
    // Der "echte", bearbeitbare aktuelle Plan
    currentActivePlan() {
      return this.fetchedWorkouts.find((w) => w.type === "new") || null
    },

    sortedTimestops() {
      return (this.customerDetails?.documents?.timestops || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    isStandardAppointment() {
      const type = (this.localData.specialType || "").toLowerCase()
      const isPause = this.localData.isPause
      return (
        ![
          "trial",
          "intro",
          "followup",
          "workout",
          "check",
          "milon",
          "nutrition",
          "consultation",
          "trainingplan",
          "anamnese",
          "new_contract",
          "upgrade",
        ].includes(type) && !isPause
      )
    },
    quickActions() {
      const hasAnamnese = Boolean(this.anamneseInfo.exists)
      const activePlan = this.currentActivePlan
      const contractIsActive = this.activeMainContract?.CONTRACTSTATE === "Laufend"
      const successColor = this.isDarkMode ? "#86EFAC" : "#15803D"
      const neutralColor = this.isDarkMode ? "#CBD5E1" : "#475569"
      const warningColor = this.isDarkMode ? "#FDBA74" : "#B45309"
      const contractStatusColor = this.roodyContractsLoading
        ? neutralColor
        : contractIsActive
          ? successColor
          : this.relevantRoodyContract
            ? warningColor
            : neutralColor

      return [
        {
          key: "anamnese",
          type: "anamnese",
          text: "Anamnese",
          eyebrow: "Gesundheit",
          subtitle: hasAnamnese ? `Letzter Stand vom ${this.formatDate(this.anamneseInfo.date)}` : "Noch keine Anamnese hinterlegt",
          cta: hasAnamnese ? "Öffnen & bearbeiten" : "Jetzt erfassen",
          statusLabel: hasAnamnese ? "Vorhanden" : "Nicht erfasst",
          statusColor: hasAnamnese ? successColor : neutralColor,
          icon: "mdi-clipboard-pulse-outline",
          color: this.isDarkMode ? "#67E8F9" : "#0E7490",
          ready: hasAnamnese,
          loading: false,
          disabled: false,
        },
        {
          key: "training-plan",
          type: activePlan ? "workout_edit" : "workout_new",
          text: "Trainingsplan",
          eyebrow: "Training",
          subtitle: this.loadingWorkouts
            ? "Trainingspläne werden geprüft"
            : activePlan
              ? activePlan.displayTitle || activePlan.title
              : "Noch kein aktiver Plan vorhanden",
          cta: activePlan ? "Bestehenden Plan bearbeiten" : "Neuen Plan erstellen",
          statusLabel: this.loadingWorkouts ? "Wird geprüft" : activePlan ? "Aktiver Plan" : "Kein Plan",
          statusColor: this.loadingWorkouts ? neutralColor : activePlan ? successColor : neutralColor,
          icon: "mdi-dumbbell",
          color: this.isDarkMode ? "#93C5FD" : "#1D4ED8",
          ready: Boolean(activePlan),
          loading: this.loadingWorkouts,
          disabled: this.loadingWorkouts,
        },
        {
          key: "contract",
          type: "upgrade",
          text: "Vertrag & Upgrade",
          eyebrow: "Mitgliedschaft",
          subtitle: this.roodyContractsLoading ? "Vertragsstatus wird geprüft" : this.appointmentContractTitle,
          cta: this.activeMainContract ? "Vertrag & Pakete öffnen" : "Vertragsstatus öffnen",
          statusLabel: this.appointmentContractChipLabel,
          statusColor: contractStatusColor,
          icon: "mdi-file-document-edit-outline",
          color: warningColor,
          ready: Boolean(this.activeMainContract || this.relevantRoodyContract),
          loading: this.roodyContractsLoading,
          disabled: this.roodyContractsLoading,
        },
      ]
    },
    relevantRoodyContract() {
      if (!this.roodyContractsLoaded || !this.roodyContracts.length) return null

      const today = moment().startOf("day")
      const relevant = this.roodyContracts.filter((contract) => {
        const status = String(contract?.status || "").toLowerCase()
        if (status === "created" || status === "temporary") return true
        if (status !== "accepted") return false
        if (!contract?.endDate) return true
        const endDate = moment(contract?.endDate)
        return !endDate.isValid() || endDate.isSameOrAfter(today, "day")
      })
      if (!relevant.length) return null

      const activeStudioId = userService.getStudio()?._id
      const sameStudio = activeStudioId
        ? relevant.filter((contract) => String(contract?.studio?._id || contract?.studio || "") === String(activeStudioId))
        : []
      const candidates = sameStudio.length ? sameStudio : relevant
      const statusPriority = { temporary: 3, created: 2, accepted: 1 }

      return [...candidates].sort((left, right) => {
        const statusDiff = (statusPriority[String(right?.status || "").toLowerCase()] || 0) - (statusPriority[String(left?.status || "").toLowerCase()] || 0)
        if (statusDiff) return statusDiff
        return moment(right?.createdAt || 0).valueOf() - moment(left?.createdAt || 0).valueOf()
      })[0]
    },
    roodyContractStatusLabel() {
      const status = String(this.relevantRoodyContract?.status || "").toLowerCase()
      if (status === "temporary") return "Entwurf gespeichert"
      if (status === "created") return "Roody-Vertrag offen"
      if (status === "accepted") return "Roody-Vertrag akzeptiert"
      return ""
    },
    roodyContractTitle() {
      const contract = this.relevantRoodyContract
      return contract?.membership?.selection?.title || contract?.category?.selection?.title || "Roody-Vertrag"
    },
    roodyContractStateTitle() {
      if (this.roodyContractsLoading) return "Roody-Verträge werden geprüft"
      if (this.relevantRoodyContract) return this.roodyContractStatusLabel
      if (this.roodyContractsError === "missing_right") return "Roody-Verträge nicht prüfbar"
      if (this.roodyContractsError) return "Vertragsstatus nicht verfügbar"
      if (this.roodyContractsLoaded) return "Noch kein Vertrag vorhanden"
      return "Vertragsstatus wird vorbereitet"
    },
    roodyContractStateHint() {
      if (this.roodyContractsLoading) return "Offene Verträge und Entwürfe werden geladen."
      if (this.relevantRoodyContract?.status === "temporary") return `${this.roodyContractTitle} kann direkt fortgesetzt werden.`
      if (this.relevantRoodyContract?.status === "created") return `${this.roodyContractTitle} ist im Roody vorhanden, aber im TAC noch nicht aktiv.`
      if (this.relevantRoodyContract?.status === "accepted") return `${this.roodyContractTitle} ist akzeptiert, aber im TAC noch nicht aktiv.`
      if (this.roodyContractsError === "missing_right") return "Für die Prüfung fehlt das Recht zur Kundensuche."
      if (this.roodyContractsError) return "Die Roody-Verträge konnten nicht geladen werden."
      if (this.roodyContractsLoaded) return "Weder im TAC noch als offener Roody-Vertrag gefunden."
      return ""
    },
    relevantRoodyContractActionLabel() {
      return this.relevantRoodyContract?.status === "temporary" ? "Entwurf fortsetzen" : "Vertrag ansehen"
    },
    canCreateCustomerContract() {
      return userService.hasRight("c_create_customer", "c_create")
    },
    newContractAppointmentActionLabel() {
      if (!this.activeCustomerId) return "Zuerst Stammkunden verknüpfen"
      if (this.activeMainContract) return "TAC-Vertrag bereits vorhanden"
      if (this.roodyContractsLoading || (!this.roodyContractsLoaded && !this.roodyContractsError)) return "Vertragsstatus wird geprüft"
      if (this.relevantRoodyContract) return this.relevantRoodyContractActionLabel
      if (this.roodyContractsError === "missing_right") return "Vertragsstatus nicht prüfbar"
      if (this.roodyContractsError) return "Vertragsstatus erneut prüfen"
      if (!this.canCreateCustomerContract) return "Keine Berechtigung zum Erstellen"
      return "Vertrag erstellen"
    },
    newContractAppointmentActionIcon() {
      if (this.activeMainContract) return "mdi-check-circle-outline"
      if (this.relevantRoodyContract?.status === "temporary") return "mdi-pencil-outline"
      if (this.relevantRoodyContract) return "mdi-eye-outline"
      if (this.roodyContractsError && this.roodyContractsError !== "missing_right") return "mdi-refresh"
      return "mdi-file-sign"
    },
    newContractAppointmentActionDisabled() {
      if (!this.activeCustomerId || this.activeMainContract || this.roodyContractsLoading) return true
      if (this.relevantRoodyContract) return false
      if (this.roodyContractsError) return this.roodyContractsError === "missing_right"
      return !this.roodyContractsLoaded || !this.canCreateCustomerContract
    },
    appointmentContractChipColor() {
      if (this.activeMainContract?.CONTRACTSTATE === "Laufend") return "green lighten-4"
      if (this.relevantRoodyContract) return "orange lighten-4"
      return "grey lighten-3"
    },
    appointmentContractChipClass() {
      if (this.activeMainContract?.CONTRACTSTATE === "Laufend") return "green--text text--darken-3"
      if (this.relevantRoodyContract) return "orange--text text--darken-4"
      return "grey--text text--darken-3"
    },
    appointmentContractChipLabel() {
      if (this.activeMainContract) return this.activeMainContract.CONTRACTSTATE || "TAC-Vertrag"
      if (this.relevantRoodyContract) return this.roodyContractStatusLabel
      if (this.roodyContractsLoading) return "Prüfung läuft"
      return "Kein Vertrag"
    },
    appointmentContractTitle() {
      if (this.activeMainContract) return this.activeMainContract.NAME || "TAC-Vertrag"
      if (this.relevantRoodyContract) return this.roodyContractTitle
      return "Kein aktiver Vertrag"
    },
    contractStatusColor() {
      const state = this.activeMainContract?.CONTRACTSTATE
      if (state === "Laufend") return "#0a9a47"
      if (state === "Gekündigt") return "#FF5252"
      if (this.relevantRoodyContract) return "#E88A1A"
      return "grey"
    },
    contractStatusText() {
      const c = this.activeMainContract
      if (!c) {
        if (this.roodyContractsLoading) return "Roody-Vertragsstatus wird geladen"
        if (this.relevantRoodyContract) {
          const startValue = this.relevantRoodyContract.startDate
          const startDate = startValue ? moment(startValue) : null
          return startDate && startDate.isValid()
            ? `${this.roodyContractStatusLabel} · Start ${startDate.format("DD.MM.YYYY")}`
            : this.roodyContractStatusLabel
        }
        if (this.roodyContractsLoaded) return "Kein TAC- und kein offener Roody-Vertrag"
        if (this.roodyContractsError) return "Roody-Vertragsstatus konnte nicht geladen werden"
        return ""
      }
      if (c.CONTRACTSTATE === "Laufend") return `Läuft bis ${this.formatDate(c.TERM_ENDDATE)}`
      return c.CONTRACTSTATE
    },
    activeTimestop() {
      if (!this.customerDetails?.documents?.timestops) return null
      return this.customerDetails.documents.timestops.find(
        (t) => t.status === "accepted" && moment().isBetween(t.timestopInfos.start, t.timestopInfos.end, "day", "[]"),
      )
    },
    processedAttendance() {
      let rawEvents = []
      const src = this.customerDetails?.attendance || this.asynchronousAttendance
      if (!src) return []

      if (typeof src === "object" && !Array.isArray(src)) {
        // Keys are dates like "2023-10-16", values are arrays of events
        Object.entries(src).forEach(([dateKey, dayEvents]) => {
          if (Array.isArray(dayEvents)) {
            dayEvents.forEach((event) => rawEvents.push({ ...event, date: event.date || dateKey }))
          }
        })
      } else if (Array.isArray(src)) {
        rawEvents = src
      }
      rawEvents.sort((a, b) => new Date(a.date + " " + a.time) - new Date(b.date + " " + b.time))
      const visits = []
      let currentVisit = null
      rawEvents.forEach((entry) => {
        if (entry.type === "checkin") {
          const checkinMoment = moment(`${entry.date} ${entry.time}`, "YYYY-MM-DD HH:mm:ss")
          currentVisit = {
            date: entry.date,
            start: checkinMoment.isValid() ? checkinMoment.format("HH:mm") : entry.time || "--",
            end: null,
            duration: null,
            fullStartDate: checkinMoment,
          }
          visits.push(currentVisit)
        } else if (entry.type === "checkout" && currentVisit && !currentVisit.end) {
          const checkoutMoment = moment(`${entry.date} ${entry.time}`, "YYYY-MM-DD HH:mm:ss")
          currentVisit.end = checkoutMoment.isValid() ? checkoutMoment.format("HH:mm") : null
          const diff =
            checkoutMoment.isValid() && currentVisit.fullStartDate.isValid() ? checkoutMoment.diff(currentVisit.fullStartDate, "minutes") : NaN
          if (!isNaN(diff) && diff >= 0) {
            const h = Math.floor(diff / 60)
            const m = diff % 60
            currentVisit.duration = h > 0 ? `${h}h ${m}min` : `${m}min`
          } else {
            currentVisit.duration = null
          }
          currentVisit = null
        }
      })
      return visits.reverse()
    },
    localConflicts() {
      if (!this.localData || !this.localData.colId || !this.localData.date || !this.localData.start) return []

      const emp = (this.employees || []).find((e) => String(e.id || e._id) === String(this.localData.colId))
      if (!emp) return []

      const timeToMinutes = (t) => {
        if (!t) return 0
        const [h, m] = t.split(":").map(Number)
        return h * 60 + m
      }

      const startMin = timeToMinutes(this.localData.start)
      const duration = parseInt(this.localData.duration) || 30
      const endMin = startMin + duration

      const conflicts = []
      const targetDate = this.localData.date

      // 1. Appointments check
      if (emp.appointments) {
        emp.appointments.forEach((appt) => {
          if (appt.date === targetDate) {
            // Skip the same appointment we are currently editing
            if (String(appt._id || appt.id) === String(this.localData._id || this.localData.id)) {
              return
            }
            if (appt.status === "cancelled" || appt.status === "noshow") {
              return
            }
            const apptStart = timeToMinutes(appt.start || appt.time)
            const apptEnd = timeToMinutes(appt.end) || apptStart + (appt.duration || 30)

            if (apptStart < endMin && apptEnd > startMin) {
              conflicts.push({
                type: "error",
                message: `Überschneidet sich mit Termin "${appt.title || "Termin"}" (${appt.start} - ${appt.end})`,
              })
            }
          }
        })
      }

      // 2. Shifts & Pauses check
      if (emp.shifts) {
        let hasShift = false
        let inWorkHours = false

        emp.shifts.forEach((shift) => {
          if (shift.isSick) {
            conflicts.push({
              type: "error",
              message: "Mitarbeiter ist an diesem Tag krankgemeldet.",
            })
            hasShift = true
            return
          }

          const times = shift.times || []
          times.forEach((t) => {
            const tStart = timeToMinutes(t.start)
            const tEnd = timeToMinutes(t.end)

            if (t.type === "Pause") {
              if (tStart < endMin && tEnd > startMin) {
                conflicts.push({
                  type: "warning",
                  message: `Fällt in die geplante Pause des Trainers (${t.start} - ${t.end}).`,
                })
              }
            } else {
              hasShift = true
              if (startMin >= tStart && endMin <= tEnd) {
                inWorkHours = true
              }
            }
          })
        })

        if (hasShift && !inWorkHours) {
          conflicts.push({
            type: "warning",
            message: "Der gewählte Zeitraum liegt ausserhalb der Arbeitszeit des Trainers.",
          })
        } else if (!hasShift) {
          conflicts.push({
            type: "warning",
            message: "Trainer hat an diesem Tag keinen eingetragenen Dienst.",
          })
        }
      }

      // 3. Tasks check
      if (emp.tasks) {
        emp.tasks.forEach((task) => {
          const taskStart = timeToMinutes(task.time)
          const taskEnd = taskStart + (task.duration || 30)

          if (taskStart < endMin && taskEnd > startMin) {
            conflicts.push({
              type: "info",
              message: `Überschneidet sich mit Aufgabe "${task.title || "Aufgabe"}" (${task.time})`,
            })
          }
        })
      }

      return conflicts
    },
  },
  methods: {
    isEmployeeEligibleForAppointment(employeeOrId) {
      if (!this.isRealAppointment) return true
      const employeeId = String(employeeOrId?.id || employeeOrId?._id || employeeOrId || "")
      if (!employeeId || ["ma_info", "no_show", "unassigned", "none"].includes(employeeId)) return true

      const restriction = this.selectedAppointmentTemplate?.appointmentRestriction || {}
      const excludedEmployeeIds = (restriction.excludedEmployees || []).map((employee) => String(employee?._id || employee || ""))
      if (excludedEmployeeIds.includes(employeeId)) return false
      if (restriction.excludeApprentices !== true) return true

      const employee =
        typeof employeeOrId === "object"
          ? employeeOrId
          : [...(this.employees || []), ...(this.finderEmployees || [])].find(
              (candidate) => String(candidate?.id || candidate?._id || "") === employeeId,
            )
      return employee?.isApprentice !== true
    },
    appointmentRestrictionMessage(employeeOrId) {
      if (this.isEmployeeEligibleForAppointment(employeeOrId)) return ""
      const employeeId = String(employeeOrId?.id || employeeOrId?._id || employeeOrId || "")
      const restriction = this.selectedAppointmentTemplate?.appointmentRestriction || {}
      const explicitlyExcluded = (restriction.excludedEmployees || []).some((employee) => String(employee?._id || employee || "") === employeeId)
      return explicitlyExcluded ? "Diese Person ist für diese Terminart gesperrt." : "Diese Terminart ist für Lernende gesperrt."
    },
    appointmentBookingChanged() {
      if (!this.isRealAppointment) return false
      const comparable = (value) => String(value?._id || value || "")
      const assignmentOrStartChanged = ["colId", "templateId", "date", "start"].some(
        (field) => comparable(this.localData?.[field]) !== comparable(this.editData?.[field]),
      )
      if (assignmentOrStartChanged) return true

      const effectiveEnd = (entry) => {
        if (entry?.start && Number(entry?.duration) > 0) {
          return moment(entry.start, "HH:mm").add(Number(entry.duration), "minutes").format("HH:mm")
        }
        return String(entry?.end || "")
      }
      return effectiveEnd(this.localData) !== effectiveEnd(this.editData)
    },
    hasVisibleAppointmentAssignment(employeeId = this.localData?.colId) {
      const status = String(this.localData?.status || "")
      if (["cancelled", "noshow"].includes(status)) return true
      const normalizedId = String(employeeId || "")
      return Boolean(normalizedId && !["none", "unassigned"].includes(normalizedId))
    },
    getTemplateCategory(template = {}) {
      if (template.isTask || template.type === "task") return "ÄMTLI & AUFGABE"
      if (template.isPause || template.type === "pause" || template.specialType === "pause") return "PAUSE & SCHICHT"

      const type = String(template.specialType || "").toLowerCase()
      if (["trial", "intro", "followup", "workout", "trainingplan", "milon"].includes(type)) return "TRAINING & COACHING"
      if (["anamnese", "nutrition", "check", "abschlusstermin_11d"].includes(type)) return "GESUNDHEIT & CHECK"
      if (["consultation", "new_contract", "upgrade"].includes(type)) return "BERATUNG & VERTRAG"
      if (template.requiresCustomer || this.activeCustomer || this.hasNoAccountData) return "KUNDENTERMIN"
      return "STUDIO-TERMIN"
    },
    onTemplateChange(templateId) {
      const template = this.appointmentTemplates.find((t) => String(t.id || t._id) === String(templateId))
      if (template) {
        this.localData.templateId = template.id || template._id
        this.localData.title = template.title
        this.localData.color = template.color
        this.localData.icon = template.icon
        this.localData.specialType = template.specialType || null
      }
    },
    openAppointmentDateFinder() {
      if (!this.isRealAppointment) return
      this.showFinder = true
    },
    ...mapActions("studioStore", ["selectEmployee"]),
    ...mapActions("panelStore", ["selectCustomer"]),
    ...mapActions("runWorkoutStore", ["setPlanForEditing"]),
    syncActiveTrainerToAppointment() {
      if (!this.localData || !this.localData.colId) return
      const empId = this.localData.colId
      const emp = (this.employees || []).find((e) => String(e.id) === String(empId))
      if (emp) {
        const names = (emp.name || "").split(" ")
        const formattedEmp = {
          user: emp.id,
          fullName: emp.name,
          vorname: names[0] || "",
          nachname: names.slice(1).join(" ") || "",
          picture: emp.picture,
        }
        this.selectEmployee(formattedEmp)
        if (this.$toast) {
          this.$toast.success(`Aktiver Trainer geändert zu ${formattedEmp.fullName}`)
        }
      }
    },
    getIconByType() {
      return "mdi-calendar"
    },
    switchCustomer(index) {
      this.activeCustomerIndex = index
      this.showCustomerImagePreview = false

      // Reset loaded lists to prevent showing old details before new ones load
      this.appointmentHistory = []
      this.fetchedWorkouts = []
      this.asynchronousAttendance = []

      // Emit to parent to load the correct customer's details
      const customer = index === 1 ? this.localData?.customer2 : this.localData?.customer
      const customerId = customer?._id || customer?.id || customer?.user?._id || customer?.user?.id || customer?.user
      if (customerId) {
        this.$emit("load-customer-details", customerId)

        // Load data for the newly selected active customer
        this.loadWorkouts()
        this.loadAttendance()
        if (this.activeTab === 2) {
          this.loadHistory()
        }
      }
    },
    openCustomerChange() {
      if (!this.activeCustomer || !this.isRealAppointment) return
      this.showFinder = false
      this.showMobileDetails = true
      this.showCustomerChange = true
    },
    prepareCustomerChange(index) {
      this.switchCustomer(index)
      this.showFinder = false
      this.showMobileDetails = true
      this.showCustomerChange = true
    },
    selectReplacementCustomer(newUser) {
      if (!newUser || !newUser._id) return

      const referenceId = (value) => {
        if (!value) return null
        if (typeof value !== "object") return value
        const nestedUser = value.user
        return value._id || value.id || (typeof nestedUser === "object" ? nestedUser?._id || nestedUser?.id : nestedUser) || null
      }
      const currentCustomer = this.activeCustomer
      const otherCustomer = this.activeCustomerIndex === 1 ? this.localData?.customer : this.localData?.customer2

      if (String(referenceId(currentCustomer) || "") === String(newUser._id)) {
        this.$toast?.info("Dieser Kunde ist dem Termin bereits zugewiesen.")
        this.showCustomerChange = false
        return
      }
      if (String(referenceId(otherCustomer) || "") === String(newUser._id)) {
        this.$toast?.error("Dieser Kunde ist bereits als zweite Person im selben Termin eingetragen.")
        return
      }

      if (this.activeCustomerIndex === 1) {
        this.$set(this.localData, "customer2", newUser)
        this.$set(this.localData, "noAccountData2", null)
      } else {
        this.$set(this.localData, "customer", newUser)
        this.$set(this.localData, "noAccountData", null)
      }

      this.switchCustomer(this.activeCustomerIndex)
      this.showCustomerChange = false
      this.$toast?.success(`${newUser.vorname || ""} ${newUser.nachname || ""} ist ausgewählt. Bitte den Termin speichern.`.trim())
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(0,150,63, ${alpha})`
      if (hex.startsWith("var(")) {
        return `color-mix(in srgb, ${hex} ${alpha * 100}%, transparent)`
      }
      if (hex.startsWith("#")) {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      return hex
    },
    openCustomerImagePreview() {
      if (!this.mergedCustomer || !this.hasRealCustomerPicture(this.mergedCustomer)) return
      this.showCustomerImagePreview = true
    },
    resolveCustomerPicture(customer) {
      if (!customer) return null

      const nestedUser = customer.user && typeof customer.user === "object" ? customer.user : {}
      const rawPicture = customer.bild || nestedUser.bild || null
      const rawTacPicture = customer.tac_image || nestedUser.tac_image || null
      const customerId = customer._id || customer.id || nestedUser._id || nestedUser.id || null

      if (rawPicture && typeof rawPicture !== "boolean") {
        const picture = String(rawPicture).trim()
        const normalized = picture.toLowerCase()
        const isPlaceholder = normalized.includes("placeholder") || normalized.includes("default") || normalized.includes("150x150")

        if (!isPlaceholder) {
          if (/^(data:image|https?:\/\/|blob:)/i.test(picture)) return picture
          if (picture.length > 200) return `data:image/jpeg;base64,${picture}`
          if (picture.startsWith("/img/") || picture.startsWith("/api/")) return picture
          return `/img/profilbilder/${picture.replace(/^\/+/, "")}`
        }
      }

      if (rawTacPicture) {
        if (typeof rawTacPicture === "string") {
          const tacPicture = rawTacPicture.trim()
          const normalized = tacPicture.toLowerCase()
          const isPlaceholder = normalized.includes("placeholder") || normalized.includes("default") || normalized.includes("150x150")

          if (!isPlaceholder) {
            if (/^(data:image|https?:\/\/|blob:)/i.test(tacPicture)) return tacPicture
            if (tacPicture.length > 200) return `data:image/jpeg;base64,${tacPicture}`
          }
        }

        if (customerId) {
          return `https://app.wellcomefit.ch/api/checkin/pool/images/${encodeURIComponent(customerId)}`
        }
      }

      return null
    },
    hasRealCustomerPicture(customer) {
      return Boolean(this.resolveCustomerPicture(customer))
    },
    getCustomerInitials(customer, noAccountData) {
      let first = ""
      let last = ""
      if (customer) {
        first = customer.vorname || ""
        last = customer.nachname || ""
      } else if (noAccountData) {
        first = noAccountData.firstName || ""
        last = noAccountData.lastName || ""
      }
      const fChar = first.trim().charAt(0).toUpperCase()
      const lChar = last.trim().charAt(0).toUpperCase()
      return `${fChar}${lChar}` || "?"
    },
    membershipCardStyle(contract) {
      const dark = this.isDarkMode
      if (!contract) {
        const roodyAccent = this.relevantRoodyContract ? "#E88A1A" : dark ? "#555" : "#bbb"
        return {
          background: dark ? "rgba(255, 255, 255, 0.02) !important" : "#ffffff !important",
          color: "var(--text-main) !important",
          border: `1px ${this.relevantRoodyContract ? "solid" : "dashed"} ${dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"} !important`,
          borderLeft: `4px solid ${roodyAccent} !important`,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04) !important",
        }
      }
      const isActive = contract.CONTRACTSTATE === "Laufend"
      const statusColor = isActive ? "#4CAF50" : "#F44336"

      return {
        background: dark ? "rgba(255, 255, 255, 0.02) !important" : "#ffffff !important",
        color: "var(--text-main) !important",
        border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"} !important`,
        borderLeft: `4px solid ${statusColor} !important`,
        boxShadow: "0 6px 18px rgba(0, 0, 0, 0.04) !important",
      }
    },
    getPicture(customer) {
      return this.resolveCustomerPicture(customer) || "/img/icons/user_placeholder.png"
    },

    // --- NEU: Trainingspläne laden & mappen ---
    async loadWorkouts() {
      if (!this.activeCustomer?._id) return
      this.loadingWorkouts = true
      try {
        const [newPlansResponse, oldPlansResponse] = await Promise.all([
          api.workoutApi.fetchCustomerWorkouts(this.activeCustomer._id).catch(() => []),
          api.getTrainingPlansForUser(this.activeCustomer._id).catch(() => []),
        ])

        // Neue V2 Pläne
        const mappedNew = (Array.isArray(newPlansResponse) ? newPlansResponse : []).map((plan) => {
          let displayTitle = plan.title
          let splitDays = []
          if (plan.splits && Array.isArray(plan.splits.workouts) && plan.splits.workouts.length > 0) {
            splitDays = plan.splits.workouts.map((w) => w.title)
            displayTitle = splitDays.join(", ")
          }

          const populatedInstructorName = [plan.instructor_id?.vorname, plan.instructor_id?.nachname].filter(Boolean).join(" ")
          const contributorNames = (Array.isArray(plan.allContributors) ? plan.allContributors : [])
            .map((contributor) => [contributor?.vorname, contributor?.nachname].filter(Boolean).join(" "))
            .filter(Boolean)
            .join(", ")
          const instructorName =
            populatedInstructorName ||
            (typeof plan.instructor === "string" ? plan.instructor.trim() : "") ||
            (typeof plan.form?.instructor === "string" ? plan.form.instructor.trim() : "") ||
            contributorNames ||
            "Unbekannt"

          return {
            id: plan._id,
            title: plan.title || plan.splits?.text || "Trainingsplan",
            displayTitle: displayTitle,
            createdDate: plan.createdAt,
            type: "new",
            originalObject: plan,
            instructor: instructorName,
            splitDays: splitDays,
          }
        })

        // Alte V1 Pläne
        const oldPlansArray = Array.isArray(oldPlansResponse) ? oldPlansResponse : []
        const mappedOld = oldPlansArray.map((plan) => {
          let instructorName = "Unbekannt"
          const legacyTimestamp = Number(plan.form?.ts)
          const createdDate =
            Number.isFinite(legacyTimestamp) && legacyTimestamp > 0
              ? new Date(legacyTimestamp * 1000).toISOString()
              : plan.createdAt || plan.updatedAt || null

          if (plan.creatorInfo?.vorname) {
            instructorName = `${plan.creatorInfo.vorname} ${plan.creatorInfo.nachname}`
          } else if (plan.form?.instructor) {
            instructorName = plan.form.instructor
          } else if (plan.instructor) {
            instructorName = plan.instructor
          }

          return {
            id: plan._id,
            title: plan.title || plan.form?.title || "Ohne Titel (Alt)",
            createdDate,
            type: "old",
            instructor: instructorName,
            originalObject: plan,
          }
        })

        this.fetchedWorkouts = [...mappedNew, ...mappedOld].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
      } catch (error) {
        console.error("Fehler beim Laden der Trainingspläne:", error)
      } finally {
        this.loadingWorkouts = false
      }
    },

    viewDocument(type, item) {
      if (type === "workout") {
        if (item.type === "old") {
          this.$toast.info("Dieser Plan ist archiviert und kann nicht mehr bearbeitet werden.")
          return
        }
        this.setPlanForEditing({ form: item.originalObject })
        const appointmentId = this.editData.id
        const date = this.currentDate
        const query = { userId: this.activeCustomer._id }
        if (appointmentId && date) {
          query.appointmentId = appointmentId
          query.date = date
        }
        this.$router.push({ name: "WorkoutCreateNew", params: { id: item.id }, query })
      } else if (type === "medicalHistory") {
        this.$refs.anamneseBogenRef.setVisible(item._id || item.id || item, false)
      }
    },
    async loadAppointmentChanges(force = false) {
      const appointmentId = this.localData?.id || this.editData?.id
      if (!appointmentId || !this.isRealAppointment) return
      const normalizedId = String(appointmentId)
      if (!force && this.appointmentChangesLoadedId === normalizedId) return

      this.appointmentChangesLoadedId = normalizedId
      const requestId = ++this.appointmentChangesRequestId
      this.loadingAppointmentChanges = true
      this.appointmentChangesError = ""

      try {
        const result = await api.shiftManagement.fetchAppointmentAuditHistory(normalizedId, 50)
        if (requestId !== this.appointmentChangesRequestId || String(this.localData?.id || "") !== normalizedId) return
        this.appointmentChangeEntries = Array.isArray(result?.entries) ? result.entries : []
        this.appointmentChangeMeta = result || null
      } catch (error) {
        if (requestId !== this.appointmentChangesRequestId || String(this.localData?.id || "") !== normalizedId) return
        this.appointmentChangeEntries = []
        this.appointmentChangeMeta = null
        this.appointmentChangesError = error?.response?.data?.message || "Termin-Verlauf konnte nicht geladen werden."
      } finally {
        if (requestId === this.appointmentChangesRequestId) this.loadingAppointmentChanges = false
      }
    },
    toggleAppointmentChanges() {
      this.appointmentChangesExpanded = !this.appointmentChangesExpanded
      if (this.appointmentChangesExpanded) this.loadAppointmentChanges()
    },
    appointmentChangeHeadline(entry) {
      if (entry?.summary) return entry.summary
      const labels = {
        created: "Termin erstellt",
        copied: "Termin kopiert",
        moved: "Termin verschoben",
        updated: "Termindetails geändert",
        deleted: "Termin gelöscht",
        hard_deleted: "Termin dauerhaft gelöscht",
        series_created: "Als Teil einer Terminserie erstellt",
        series_deleted: "Terminserie gelöscht",
        series_deleted_future: "Künftige Serientermine gelöscht",
      }
      return labels[entry?.action] || "Termin geändert"
    },
    appointmentChangeIcon(entry) {
      if (entry?.changes?.some((change) => change.field === "description")) return "mdi-note-edit-outline"
      if (entry?.changes?.some((change) => change.field === "status")) return "mdi-clipboard-list-outline"
      const icons = {
        created: "mdi-calendar-plus",
        copied: "mdi-content-copy",
        moved: "mdi-calendar-arrow-right",
        updated: "mdi-pencil-outline",
        deleted: "mdi-trash-can-outline",
        hard_deleted: "mdi-delete-forever-outline",
        series_created: "mdi-calendar-multiple",
        series_deleted: "mdi-calendar-remove",
        series_deleted_future: "mdi-calendar-remove",
      }
      return icons[entry?.action] || "mdi-pencil-outline"
    },
    appointmentChangeColor(entry) {
      const movedToNoShow = entry?.changes?.some(
        (change) => change.field === "assignment" && String(change.newValue || "").startsWith("helper:no_show:"),
      )
      if (movedToNoShow || ["deleted", "hard_deleted", "series_deleted", "series_deleted_future"].includes(entry?.action)) return "#E53935"
      if (entry?.changes?.some((change) => change.field === "description")) return "#FB8C00"
      if (entry?.changes?.some((change) => change.field === "status")) return "#7E57C2"
      if (entry?.action === "moved") return "#1E88E5"
      if (["created", "series_created"].includes(entry?.action)) return "#43A047"
      return this.currentTheme.color || "#607D8B"
    },
    formatAppointmentChangeDate(value) {
      if (!value) return ""
      const parsed = moment(value)
      return parsed.isValid() ? parsed.format("DD.MM.YYYY, HH:mm") : String(value)
    },
    formatAppointmentHistoryDay(value) {
      if (!value) return ""
      const strictDate = moment(value, "YYYY-MM-DD", true)
      if (strictDate.isValid()) return strictDate.format("DD.MM.YYYY")
      const parsed = moment(value)
      return parsed.isValid() ? parsed.format("DD.MM.YYYY") : String(value)
    },
    compactAppointmentHistoryText(value, maxLength = 160) {
      const compact = String(value || "")
        .replace(/\s+/g, " ")
        .trim()
      if (compact.length <= maxLength) return compact
      return `${compact.slice(0, maxLength - 1).trim()}…`
    },
    formatAppointmentFieldChange(change) {
      const oldValue = this.compactAppointmentHistoryText(change?.oldLabel ?? change?.oldValue)
      const newValue = this.compactAppointmentHistoryText(change?.newLabel ?? change?.newValue)
      const label = change?.label || "Angabe"

      if (change?.field === "description") {
        if (!oldValue && newValue) return `Notiz hinzugefügt: „${newValue}“`
        if (oldValue && !newValue) return `Notiz entfernt: „${oldValue}“`
        return `Notiz: „${oldValue}“ → „${newValue}“`
      }
      if (!oldValue && newValue) return `${label}: ${newValue}`
      if (oldValue && !newValue) return `${label}: ${oldValue} → entfernt`
      if (oldValue === newValue) return `${label} geändert`
      return `${label}: ${oldValue || "–"} → ${newValue || "–"}`
    },
    appointmentChangeDetails(entry) {
      const changes = Array.isArray(entry?.changes) ? entry.changes : []
      if (changes.length) return changes.map((change) => this.formatAppointmentFieldChange(change))

      const details = []
      const sourcePosition = [this.formatAppointmentHistoryDay(entry?.sourceDate), entry?.sourceStart].filter(Boolean).join(" · ")
      const targetPosition = [this.formatAppointmentHistoryDay(entry?.targetDate), entry?.targetStart].filter(Boolean).join(" · ")

      if (["moved", "copied"].includes(entry?.action) && sourcePosition && targetPosition && sourcePosition !== targetPosition) {
        details.push(`${sourcePosition} → ${targetPosition}`)
      } else if (entry?.action === "created" && targetPosition) {
        details.push(targetPosition)
      } else if (["deleted", "hard_deleted"].includes(entry?.action) && sourcePosition) {
        details.push(sourcePosition)
      }

      const sourceAssignment = entry?.sourceAssignmentLabel || ""
      const targetAssignment = entry?.targetAssignmentLabel || ""
      if (sourceAssignment && targetAssignment && sourceAssignment !== targetAssignment) {
        details.push(`Zuständigkeit: ${sourceAssignment} → ${targetAssignment}`)
      } else if (entry?.action === "created" && targetAssignment) {
        details.push(`Zuständig: ${targetAssignment}`)
      }

      return details
    },
    async loadHistory() {
      if (!this.activeCustomer?._id || this.appointmentHistory.length > 0) return
      this.loadingHistory = true
      try {
        const response = await api.shiftManagement.fetchHistory(this.activeCustomer._id)
        this.appointmentHistory = response.data
      } catch (e) {
        this.$toast.error("Historie Fehler")
      } finally {
        this.loadingHistory = false
      }
    },
    openHistoryDetails(appt) {
      this.selectedHistoryEntry = appt
      this.showHistoryDialog = true
    },
    async loadAttendance() {
      if (!this.activeCustomer?._id) return
      this.loadingAttendance = true
      this.asynchronousAttendance = []
      try {
        const data = await api.fetchRecentAttendance(this.activeCustomer._id)
        this.asynchronousAttendance = data
      } catch (e) {
        console.error("Attendence fetch error", e)
      } finally {
        this.loadingAttendance = false
      }
    },
    async refreshCustomer() {
      if (!this.activeCustomer?._id) return
      this.refreshingCustomer = true
      try {
        this.$emit("load-customer-details", this.activeCustomer._id)

        // Set appointment as adjusted if we have an active appointment ID (FIRST)
        if (this.editData && this.editData.id && !this.editData.isTask) {
          try {
            const completedActions = this.editData.completedActions || (this.localData && this.localData.completedActions) || []
            const hasAnamnese = completedActions.some((a) => a.actionType === "anamnese")

            if (!hasAnamnese) {
              await api.shiftManagement.updateAppointment(this.editData.id, {
                isAdjusted: true,
                $push: {
                  completedActions: {
                    actionType: "anamnese",
                    title: "Anamnese ausgefüllt",
                    createdAt: new Date(),
                  },
                },
              })
              this.$emit("appointment-updated")
            }
          } catch (err) {
            console.error("Failed to mark appointment as adjusted:", err)
          }
        }

        // Load workouts, attendance, and timeline history afterwards
        await this.loadWorkouts()
        await this.loadAttendance()

        // Refresh history
        this.appointmentHistory = []
        await this.loadHistory()
      } finally {
        this.refreshingCustomer = false
      }
    },
    openLogInteraction() {
      if (this.editData && this.activeCustomer && this.activeCustomer._id) {
        this.$refs.customerInteraction.setVisible(this.activeCustomer._id)
      }
    },
    async onInteractionCreated() {
      this.appointmentHistory = []
      await this.loadHistory()
      this.$emit("appointment-updated")
    },
    async loadRoodyContractStatus(customerId) {
      const targetCustomerId = String(customerId || "")
      const requestId = ++this.roodyContractRequestId

      this.roodyContracts = []
      this.roodyContractsLoaded = false
      this.roodyContractsError = ""
      this.roodyContractsCustomerId = targetCustomerId || null

      if (!targetCustomerId) {
        this.roodyContractsLoading = false
        return
      }
      if (!userService.hasRight("u_search")) {
        this.roodyContractsLoading = false
        this.roodyContractsError = "missing_right"
        return
      }

      this.roodyContractsLoading = true
      try {
        const contracts = await api.getCustomerContracts(targetCustomerId)
        if (requestId !== this.roodyContractRequestId || this.activeCustomerId !== targetCustomerId) return
        this.roodyContracts = (Array.isArray(contracts) ? contracts : []).map((contract, arrayIndex) => ({
          ...contract,
          _contractIndex: contract?.index !== undefined && contract?.index !== null ? contract.index : arrayIndex,
        }))
        this.roodyContractsLoaded = true
      } catch (error) {
        if (requestId !== this.roodyContractRequestId || this.activeCustomerId !== targetCustomerId) return
        this.roodyContractsError = "load_failed"
      } finally {
        if (requestId === this.roodyContractRequestId) this.roodyContractsLoading = false
      }
    },
    appointmentContractRouteQuery(includeCustomer = false) {
      const query = {}
      if (includeCustomer && this.activeCustomerId) query.userId = this.activeCustomerId
      if (this.editData?.id && this.currentDate) {
        query.appointmentId = this.editData.id
        query.date = this.currentDate
      }
      return query
    },
    openRelevantRoodyContract() {
      const contract = this.relevantRoodyContract
      if (!contract || !this.activeCustomerId) return
      const index = contract._contractIndex
      if (index === undefined || index === null) return
      const mode = String(contract.status || "").toLowerCase() === "temporary" ? "edit" : "view"
      this.$router.push({
        path: `/dashboard/contract/${mode}/${this.activeCustomerId}/${index}`,
        query: this.appointmentContractRouteQuery(),
      })
    },
    startCustomerContract() {
      if (!this.activeCustomerId || !this.canCreateCustomerContract) return
      this.$router.push({
        path: "/dashboard/contract",
        query: this.appointmentContractRouteQuery(true),
      })
    },
    handleNewContractAppointmentAction() {
      if (!this.activeCustomerId || this.activeMainContract || this.roodyContractsLoading) return
      if (this.relevantRoodyContract) {
        this.openRelevantRoodyContract()
        return
      }
      if (this.roodyContractsError) {
        if (this.roodyContractsError !== "missing_right") this.loadRoodyContractStatus(this.activeCustomerId)
        return
      }
      if (this.roodyContractsLoaded) this.startCustomerContract()
    },
    executeAction(type) {
      if (this.activeCustomer) {
        this.selectCustomer(this.activeCustomer)
      }
      if (type === "workout_edit") {
        if (this.currentActivePlan) {
          this.setPlanForEditing({ form: this.currentActivePlan.originalObject })
          const appointmentId = this.editData.id
          const date = this.currentDate
          const query = {}
          if (this.activeCustomerId) query.userId = this.activeCustomerId
          if (appointmentId && date) {
            query.appointmentId = appointmentId
            query.date = date
          }
          this.$router.push({ name: "WorkoutCreateNew", params: { id: this.currentActivePlan.id }, query })
        }
      } else if (type === "workout_new") {
        this.triggerNewWorkout()
      } else if (type === "anamnese") {
        if (this.anamneseInfo.exists && this.anamneseInfo.obj) {
          const docId = this.anamneseInfo.obj._id || this.anamneseInfo.obj.id
          this.$refs.anamneseBogenRef.setVisible(docId, true)
        } else {
          this.$refs.anamneseBogenRef.setVisible(null, false, false, this.activeCustomer?._id)
        }
      } else if (type === "anamnese_new") {
        this.$refs.anamneseBogenRef.setVisible(null, false, false, this.activeCustomer?._id)
      } else if (type === "upgrade") {
        this.showContractDialog = true
      } else if (type === "new_contract") {
        this.handleNewContractAppointmentAction()
      }
    },
    triggerNewWorkout() {
      if (this.currentActivePlan) {
        this.showWorkoutWarningDialog = true
      } else {
        this.proceedNewWorkout()
      }
    },
    proceedNewWorkout() {
      this.showWorkoutWarningDialog = false
      const appointmentId = this.editData.id
      const date = this.currentDate
      if (appointmentId && date) {
        this.$router.push(`/workouts/createnew?userId=${this.activeCustomer._id}&appointmentId=${appointmentId}&date=${date}`)
      } else {
        this.$router.push(`/workouts/createnew?userId=${this.activeCustomer._id}`)
      }
    },
    copyToClipboard(text, label) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$toast.success(`${label} kopiert`)
        })
        .catch(() => {
          // Fallback
          const el = document.createElement("textarea")
          el.value = text
          document.body.appendChild(el)
          el.select()
          document.execCommand("copy")
          document.body.removeChild(el)
          this.$toast.success(`${label} kopiert`)
        })
    },
    openCustomerDashboard() {
      const targetCustomer = this.activeCustomer || this.editData.customer
      if (!targetCustomer || !targetCustomer._id) {
        this.$toast?.error("Kein gültiger Kunde ausgewählt.")
        return
      }
      this.$router.push(`/dashboard/user/search/${targetCustomer._id}`)
    },
    save() {
      if (this.isRealAppointment && !this.hasVisibleAppointmentAssignment()) {
        this.$toast.error("Bitte zuerst einen Mitarbeiter für den Termin auswählen.")
        this.openAppointmentDateFinder()
        return
      }
      if (this.appointmentBookingChanged() && !this.isEmployeeEligibleForAppointment(this.localData?.colId)) {
        this.$toast.error(this.appointmentRestrictionMessage(this.localData?.colId))
        return
      }
      this.$emit("save", {
        ...this.localData,
        finderSelection: this.finderSelection ? { ...this.finderSelection } : null,
      })
    },
    openDeleteDialog() {
      this.deleteScope = "single"
      this.showDeleteReasonDialog = true
    },
    cancelDelete() {
      this.showDeleteReasonDialog = false
      this.deleteReason = ""
    },
    confirmDelete() {
      this.$emit("delete", {
        ...this.localData,
        deleteReason: this.deleteReason.trim(),
        deleteScope: this.deleteScope,
      })
      this.showDeleteReasonDialog = false
      this.deleteReason = ""
    },
    formatDate(d) {
      if (!d) return ""
      const date = typeof d === "number" && d < 2000000000 ? new Date(d * 1000) : new Date(d)
      return moment(date).format("DD.MM.YY")
    },
    formatTime(d) {
      if (!d) return ""
      const date = new Date(d)
      return moment(date).format("HH:mm")
    },
    calculateAge(d) {
      return d ? moment().diff(moment(d), "years") : ""
    },
    addChecklistItem() {
      if (this.newChecklistItem.trim() === "") return
      if (!this.localData.checklist) this.$set(this.localData, "checklist", [])
      this.localData.checklist.push({ text: this.newChecklistItem.trim(), isDone: false })
      this.newChecklistItem = ""
    },
    async assignExistingCustomer(newUser) {
      if (!newUser || !newUser._id) return

      try {
        const updatePayload = {}
        if (this.activeCustomerIndex === 1) {
          updatePayload.customer2 = newUser._id
          updatePayload.noAccountData2 = null
        } else {
          updatePayload.customer = newUser._id
          updatePayload.noAccountData = null
        }

        // Auch rawData/guestData zurücksetzen, falls vorhanden
        updatePayload.rawData = { guestData: null }

        await api.shiftManagement.updateAppointment(this.editData.id, updatePayload)

        this.$toast?.success(`${newUser.vorname} ${newUser.nachname} wurde dem Termin zugewiesen!`)

        this.bypassWarning = true
        this.$emit("customer-assigned", { appointmentId: this.editData.id, newUser, index: this.activeCustomerIndex })
        this.$emit("input", false) // Dialog schliessen
      } catch (e) {
        console.error("Fehler beim Zuweisen des Kunden:", e)
        this.$toast?.error("Fehler beim Zuweisen.")
      }
    },
    async newAccountRegistered(newUser) {
      this.showRegisterDialog = false

      if (!newUser || !newUser._id) {
        this.$toast?.success("Account erstellt – bitte Termin manuell zuweisen.")
        return
      }

      try {
        // Bestimme welches Feld aktualisiert wird (Person 1 oder 2)
        const updatePayload = {}
        if (this.activeCustomerIndex === 1) {
          updatePayload.customer2 = newUser._id
          updatePayload.noAccountData2 = null
        } else {
          updatePayload.customer = newUser._id
          updatePayload.noAccountData = null
        }

        // Appointment in DB aktualisieren
        await api.shiftManagement.updateAppointment(this.editData.id, updatePayload)

        this.$toast?.success(`${newUser.vorname} ${newUser.nachname} wurde dem Termin zugewiesen!`)

        // Emit to parent: close dialog + reload data
        this.bypassWarning = true
        this.$emit("customer-assigned", { appointmentId: this.editData.id, newUser, index: this.activeCustomerIndex })
        this.$emit("input", false) // Dialog schliessen
      } catch (e) {
        console.error("Fehler beim Zuweisen des neuen Kunden:", e)
        this.$toast?.error("Fehler beim Zuweisen – bitte manuell verknüpfen.")
      }
    },
    onFinderUpdate(field, val) {
      if (!this.isRealAppointment) return
      if (this.localData[field] !== val) {
        this.localData[field] = val
        this.finderChangedData = true
      }
    },
    onFinderSelectSlot(selection) {
      if (!selection || !this.isRealAppointment) return
      if (!this.isEmployeeEligibleForAppointment(selection.colId)) {
        this.$toast.warning(this.appointmentRestrictionMessage(selection.colId))
        return
      }
      this.finderSelection = { ...selection }
      this.onFinderUpdate("date", selection.date)
      this.onFinderUpdate("start", selection.start)
      this.onFinderUpdate("colId", selection.colId)
    },
    onFinderClearSelection() {
      this.finderSelection = null
      this.finderChangedData = false
    },
    onFinderSelectAndClose(selection) {
      if (!this.isRealAppointment) {
        this.showFinder = false
        return
      }
      if (selection) this.onFinderSelectSlot(selection)
      if (!this.finderSelection || this.finderSaving) return

      this.finderSaving = true
      const finishFinderSave = () => {
        this.finderSaving = false
      }
      const failFinderSave = (error) => {
        finishFinderSave()
        if (error && error.response && error.response.status === 409) {
          this.$nextTick(() => {
            const finder = this.$refs.appointmentFinder
            if (finder && typeof finder.refreshResults === "function") finder.refreshResults()
          })
        }
      }
      this.$emit(
        "save",
        {
          ...this.localData,
          finderSelection: { ...this.finderSelection },
        },
        {
          source: "finder",
          onSuccess: finishFinderSave,
          onError: failFinderSave,
          onCancel: finishFinderSave,
        },
      )
    },
    handleInput(val) {
      if (val === false) {
        this.closeDialog()
      } else {
        this.$emit("input", val)
      }
    },
    closeDialog() {
      if (this.finderSaving) {
        this.$emit("input", true)
        return
      }
      if (this.hasUnsavedChanges && !this.bypassWarning) {
        if (!confirm("Ungespeicherte Änderungen gehen verloren. Trotzdem schliessen?")) {
          this.$emit("input", true)
          return
        }
      }
      this.bypassWarning = false
      this.$emit("input", false)
    },
  },
}
</script>

<style scoped>
/* Glass UI - Dynamic Text Integration */
.dynamic-text {
  color: var(--text-color) !important;
}

.glass-dialog-wrapper {
  box-shadow: none;
}
.glass-card-theme {
  border-radius: 14px !important;
  border: 1px solid var(--border-color) !important;
  transition: height 0.28s cubic-bezier(0.22, 1, 0.36, 1), border-radius 0.22s ease;
}
.glass-panel-light {
  background: rgba(var(--rgb-color), 0.04);
  border: 1px solid rgba(var(--rgb-color), 0.06);
  transition: all 0.2s;
}
.border-light {
  border-color: rgba(var(--rgb-color), 0.06) !important;
}
.appointment-header {
  isolation: isolate;
  overflow: hidden;
}

.appointment-header::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  z-index: 3;
  height: 2px;
  content: "";
  pointer-events: none;
  background: linear-gradient(90deg, var(--header-accent) 0%, var(--header-accent) 18%, transparent 64%);
  opacity: 0.82;
}

.appointment-header-main {
  min-height: 88px;
  padding: 12px 24px;
}

.appointment-header--customer .appointment-header-main {
  min-height: 82px;
  padding-top: 10px;
  padding-bottom: 9px;
}

.appointment-header-icon {
  border: 1px solid;
  border-radius: 14px !important;
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.09) !important;
}

.appointment-header-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.appointment-header-kicker {
  overflow: hidden;
  font-size: 0.62rem;
  font-weight: 900;
  line-height: 1.2;
  letter-spacing: 1.35px;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.appointment-header-title-row {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 2px;
}

.customer-avatar-trigger {
  position: relative;
  display: inline-flex;
  width: 58px;
  height: 58px;
  padding: 2px;
  color: inherit;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 18px;
  outline: none;
  transition: transform 0.16s ease;
}

.customer-avatar-trigger:not(.customer-avatar-trigger--with-picture) {
  cursor: default;
}

.customer-avatar-trigger:hover,
.customer-avatar-trigger:focus-visible,
.customer-avatar-trigger[aria-expanded="true"] {
  transform: translateY(-1px) scale(1.035);
}

.customer-avatar-trigger:focus-visible::before {
  position: absolute;
  inset: -3px;
  content: "";
  border: 2px solid var(--header-accent);
  border-radius: 20px;
}

.customer-header-avatar {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: rgba(var(--rgb-color), 0.05);
  border: 2px solid var(--bg-color);
  box-shadow: 0 0 0 1px rgba(var(--rgb-color), 0.12), 0 7px 18px rgba(0, 0, 0, 0.16);
}

.customer-header-avatar-image,
.customer-header-initials {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.customer-header-initials {
  font-size: 0.9rem;
  letter-spacing: 0.4px;
}

.customer-avatar-type-badge,
.customer-avatar-zoom-badge {
  position: absolute;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--bg-color);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.customer-avatar-type-badge {
  right: -1px;
  bottom: -1px;
  width: 22px;
  height: 22px;
  border-radius: 8px;
}

.customer-avatar-zoom-badge {
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: rgba(17, 24, 39, 0.82);
  border-radius: 50%;
  opacity: 0;
  transform: scale(0.72);
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.customer-avatar-trigger--with-picture:hover .customer-avatar-zoom-badge,
.customer-avatar-trigger--with-picture:focus-visible .customer-avatar-zoom-badge,
.customer-avatar-trigger--with-picture[aria-expanded="true"] .customer-avatar-zoom-badge {
  opacity: 1;
  transform: scale(1);
}

.customer-image-preview-card {
  width: 286px;
  border: 1px solid;
  border-radius: 17px !important;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.28) !important;
}

.customer-image-preview-empty {
  width: 284px;
  height: 230px;
  text-align: center;
}

.customer-image-preview-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 78px;
  height: 78px;
  color: #fff;
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: 0.6px;
  border: 3px solid rgba(255, 255, 255, 0.72);
  border-radius: 24px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
}

.customer-image-preview-caption {
  padding: 11px 14px 12px;
  border-top: 1px solid rgba(var(--rgb-color), 0.08);
}

.customer-image-preview-hint {
  margin-top: 2px;
  color: rgba(var(--rgb-color), 0.48);
  font-size: 0.66rem;
  font-weight: 750;
  letter-spacing: 0.65px;
  text-transform: uppercase;
}

.appointment-header-title {
  min-width: 0;
  margin: 2px 0 0;
  font-size: 1.48rem;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.35px;
}

.appointment-header-badge {
  flex: 0 0 auto;
  padding: 2px 7px;
  margin-left: 8px;
  color: rgba(var(--rgb-color), 0.52);
  font-size: 0.58rem;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: 0.45px;
  text-transform: uppercase;
  background: rgba(var(--rgb-color), 0.05);
  border: 1px solid rgba(var(--rgb-color), 0.08);
  border-radius: 999px;
}

.appointment-header-meta {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 5px;
  overflow: hidden;
  gap: 4px 11px;
  color: rgba(var(--rgb-color), 0.53);
}

.appointment-header-meta-item {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  font-size: 0.68rem;
  font-weight: 650;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.appointment-header-meta-item .v-icon {
  margin-right: 4px;
  color: currentColor !important;
}

.appointment-rating-meta {
  color: #d89b00;
}

.appointment-header-actions {
  gap: 2px;
}

.appointment-header-quickfacts {
  min-width: 0;
  gap: 6px;
}

.appointment-header-quickfacts + .appointment-header-actions {
  padding-left: 8px;
  border-left: 1px solid rgba(var(--rgb-color), 0.08);
}

.customer-quickfact {
  display: inline-flex;
  align-items: center;
  min-width: 36px;
  height: 34px;
  padding: 0 9px;
  overflow: hidden;
  color: rgba(var(--rgb-color), 0.67);
  font: inherit;
  cursor: pointer;
  background: rgba(var(--rgb-color), 0.035);
  border: 1px solid rgba(var(--rgb-color), 0.1);
  border-radius: 10px;
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

button.customer-quickfact:hover,
button.customer-quickfact:focus-visible {
  background: rgba(var(--rgb-color), 0.075);
  border-color: rgba(var(--rgb-color), 0.18);
  transform: translateY(-1px);
}

button.customer-quickfact:focus-visible {
  box-shadow: 0 0 0 2px var(--bg-color), 0 0 0 4px var(--header-accent);
}

.customer-quickfact--status {
  color: var(--quickfact-color);
  cursor: default;
  background: color-mix(in srgb, var(--quickfact-color) 9%, transparent);
  border-color: color-mix(in srgb, var(--quickfact-color) 25%, transparent);
}

.customer-quickfact .v-icon {
  flex: 0 0 auto;
  color: currentColor !important;
}

.customer-quickfact-value {
  margin-left: 6px;
  overflow: hidden;
  font-size: 0.68rem;
  font-weight: 750;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-quickfact-value--phone {
  max-width: 112px;
}

.customer-quickfact-value--email {
  max-width: 148px;
}

.appointment-header-action {
  width: 38px !important;
  min-width: 38px !important;
  height: 38px !important;
}

.appointment-header-tabs,
.appointment-customer-switch {
  padding: 0 24px;
}

.appointment-header-tabs {
  border-top: 1px solid rgba(var(--rgb-color), 0.045);
}

.appointment-header-tabs >>> .v-tab {
  min-width: 116px;
  min-height: 40px;
  padding: 0 12px;
  font-size: 0.76rem !important;
  letter-spacing: 0.1px;
  text-transform: none;
}

.appointment-customer-switch {
  padding-top: 5px;
  padding-bottom: 6px;
}
.hover-glow {
  transition: all 0.15s ease;
}
.hover-glow:hover {
  background: rgba(var(--rgb-color), 0.07);
  border-color: rgba(var(--rgb-color), 0.12);
  transform: translateX(2px);
}
.border-left-indicator {
  border-left: 3px solid;
}
.border-left-active {
  border-left: 3px solid #4caf50;
  background: linear-gradient(90deg, rgba(76, 175, 80, 0.08), transparent);
}
.border-dashed {
  border: 1px dashed rgba(var(--rgb-color), 0.06) !important;
}
.border-bottom-light {
  border-bottom: 1px solid rgba(var(--rgb-color), 0.06);
}
.border-left-light {
  border-left: 1px solid rgba(var(--rgb-color), 0.06) !important;
}
.border-top-light {
  border-top: 1px solid rgba(var(--rgb-color), 0.06) !important;
}

.appointment-contract-state {
  padding: 11px;
  border: 1px solid rgba(232, 138, 26, 0.2);
  border-radius: 10px;
  background: rgba(232, 138, 26, 0.075);
}

.appointment-contract-state__row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.appointment-contract-state__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.appointment-contract-state__copy strong {
  color: var(--text-main);
  font-size: 0.78rem;
  line-height: 1.25;
}

.appointment-contract-state__copy span {
  color: var(--text-muted);
  font-size: 0.7rem;
  line-height: 1.35;
}

/* Glass Button */
.glass-btn {
  background: rgba(var(--rgb-color), 0.06) !important;
  border: 1px solid rgba(var(--rgb-color), 0.08) !important;
  border-radius: 8px !important;
}

.glass-action-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(var(--rgb-color), 0.03);
}
.glass-action-card:hover {
  background: rgba(var(--rgb-color), 0.08);
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}
.hover-scale:active {
  transform: scale(0.96);
}
.absolute-full {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}
.z-index-2 {
  z-index: 2;
}

.text-strike {
  text-decoration: line-through;
}
.text-muted {
  opacity: 0.5;
}
.checklist-input >>> input {
  color: var(--text-color) !important;
  font-weight: 500;
}

/* Box Shadow Glow */
.box-shadow-glow {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

/* Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(var(--rgb-color), 0.08);
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--rgb-color), 0.15);
}

/* Timeline */
.timeline-item {
  position: relative;
}
.timeline-line {
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(var(--rgb-color), 0.06);
}
.timeline-dot {
  position: absolute;
  left: 6px;
  top: 20px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 8px currentColor;
}

/* Forms */
.centered-input >>> input {
  text-align: center;
  font-weight: 700;
}

/* Custom Textarea */
.custom-textarea >>> .v-input__slot {
  min-height: 80px !important;
}
.custom-textarea >>> textarea {
  font-size: 0.85rem;
  color: var(--text-color) !important;
}

/* Notepad */
.notepad-wrapper {
  background: rgba(var(--rgb-color), 0.02);
}

/* Typography */
.text-shadow {
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.line-height-1 {
  line-height: 1;
}
.letter-spacing-2 {
  letter-spacing: 2px;
}
.opacity-70 {
  opacity: 0.7;
}
.opacity-80 {
  opacity: 0.8;
}

/* Gaps */
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}

/* Checkin Pill */
.checkin-pill {
  transition: all 0.15s;
}
.checkin-pill:hover {
  background: rgba(var(--rgb-color), 0.12) !important;
}

/* Z-Index */
.z-index-2 {
  z-index: 2;
}
.z-index-20 {
  z-index: 20;
}

/* Individueller Kundentermin */
.standard-appointment-hub {
  position: relative;
  padding: 22px;
  overflow: hidden;
  background: radial-gradient(circle at 100% 0%, var(--appointment-accent-soft), transparent 36%),
    linear-gradient(145deg, rgba(var(--rgb-color), 0.052), rgba(var(--rgb-color), 0.014));
  border: 1px solid rgba(var(--rgb-color), 0.09);
  border-radius: 22px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.12);
}

.standard-appointment-hub::before {
  position: absolute;
  top: 0;
  right: 22px;
  left: 22px;
  height: 3px;
  content: "";
  background: linear-gradient(90deg, transparent, var(--appointment-accent), transparent);
  border-radius: 0 0 999px 999px;
  opacity: 0.82;
}

.standard-appointment-hub::after {
  position: absolute;
  top: -94px;
  right: -72px;
  width: 210px;
  height: 210px;
  pointer-events: none;
  content: "";
  background: radial-gradient(circle, var(--appointment-accent-soft), transparent 68%);
  border-radius: 50%;
  opacity: 0.72;
}

.standard-appointment-hub__header,
.standard-appointment-actions,
.standard-appointment-hub__context {
  position: relative;
  z-index: 1;
}

.standard-appointment-hub__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.standard-appointment-hub__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 13px;
}

.standard-appointment-hub__icon {
  display: inline-flex;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  align-items: center;
  justify-content: center;
  background: var(--appointment-accent-soft);
  border: 1px solid var(--appointment-accent-border);
  border-radius: 15px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
}

.standard-appointment-hub__copy {
  min-width: 0;
}

.standard-appointment-hub__eyebrow {
  margin-bottom: 3px;
  color: var(--appointment-accent);
  font-size: 0.61rem;
  font-weight: 900;
  letter-spacing: 1.25px;
  line-height: 1.2;
  text-transform: uppercase;
}

.standard-appointment-hub__copy h2 {
  margin: 0;
  font-size: clamp(1.18rem, 2.1vw, 1.55rem);
  font-weight: 900;
  line-height: 1.12;
  letter-spacing: -0.35px;
}

.standard-appointment-hub__copy p {
  margin: 5px 0 0;
  color: rgba(var(--rgb-color), 0.52);
  font-size: 0.74rem;
  font-weight: 550;
  line-height: 1.45;
}

.standard-appointment-hub__badge {
  display: inline-flex;
  min-height: 30px;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  padding: 0 10px;
  color: var(--appointment-accent);
  font-size: 0.66rem;
  font-weight: 850;
  letter-spacing: 0.25px;
  white-space: nowrap;
  background: var(--appointment-accent-soft);
  border: 1px solid var(--appointment-accent-border);
  border-radius: 999px;
}

.standard-appointment-hub__badge .v-icon {
  color: currentColor !important;
}

.standard-appointment-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 11px;
}

.standard-appointment-action {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 202px;
  padding: 15px;
  overflow: hidden;
  color: var(--text-color);
  font: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  background: linear-gradient(155deg, rgba(var(--rgb-color), 0.048), rgba(var(--rgb-color), 0.018));
  border: 1px solid rgba(var(--rgb-color), 0.085);
  border-radius: 17px;
  outline: none;
  flex-direction: column;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.standard-appointment-action::before {
  position: absolute;
  top: 0;
  right: 14px;
  left: 14px;
  height: 2px;
  content: "";
  background: var(--quick-action-color);
  border-radius: 0 0 999px 999px;
  opacity: 0.68;
}

.standard-appointment-action:not(:disabled):hover,
.standard-appointment-action:not(:disabled):focus-visible {
  background: linear-gradient(155deg, var(--quick-action-soft), rgba(var(--rgb-color), 0.025));
  border-color: var(--quick-action-border);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.14);
  transform: translateY(-3px);
}

.standard-appointment-action:not(:disabled):focus-visible {
  box-shadow: 0 0 0 2px var(--bg-color), 0 0 0 4px var(--quick-action-color), 0 14px 28px rgba(0, 0, 0, 0.14);
}

.standard-appointment-action:disabled {
  cursor: wait;
  opacity: 0.68;
}

.standard-appointment-action__topline,
.standard-appointment-action__footer {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.standard-appointment-action__icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  background: var(--quick-action-soft);
  border: 1px solid var(--quick-action-border);
  border-radius: 13px;
}

.standard-appointment-action__status {
  display: inline-flex;
  min-width: 0;
  min-height: 24px;
  align-items: center;
  gap: 5px;
  padding: 0 8px;
  overflow: hidden;
  color: var(--quick-action-status);
  font-size: 0.59rem;
  font-weight: 850;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: var(--quick-action-status-soft);
  border: 1px solid var(--quick-action-status-soft);
  border-radius: 999px;
}

.standard-appointment-action__status-dot {
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  background: currentColor;
  border-radius: 50%;
  box-shadow: 0 0 0 3px var(--quick-action-status-soft);
}

.standard-appointment-action__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  padding: 17px 0 14px;
}

.standard-appointment-action__copy small {
  margin-bottom: 4px;
  color: var(--quick-action-color);
  font-size: 0.58rem;
  font-weight: 900;
  letter-spacing: 1px;
  line-height: 1.2;
  text-transform: uppercase;
}

.standard-appointment-action__copy strong {
  overflow: hidden;
  font-size: 0.98rem;
  font-weight: 850;
  line-height: 1.22;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.standard-appointment-action__copy > span {
  display: -webkit-box;
  margin-top: 6px;
  overflow: hidden;
  color: rgba(var(--rgb-color), 0.52);
  font-size: 0.68rem;
  font-weight: 550;
  line-height: 1.42;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.standard-appointment-action__footer {
  min-height: 30px;
  padding-top: 10px;
  color: var(--quick-action-color);
  font-size: 0.65rem;
  font-weight: 850;
  line-height: 1.25;
  border-top: 1px solid rgba(var(--rgb-color), 0.07);
}

.standard-appointment-action__footer > span {
  min-width: 0;
}

.standard-appointment-hub__context {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 13px;
  color: rgba(var(--rgb-color), 0.42);
  font-size: 0.65rem;
  font-weight: 650;
  line-height: 1.35;
}

.standard-appointment-hub__context .v-icon {
  flex: 0 0 auto;
}

.cursor-pointer {
  cursor: pointer;
}
.font-weight-semibold {
  font-weight: 600;
}

.task-editor,
.generic-entry-editor {
  min-height: 0;
}

.left-editor-heading {
  min-height: 40px;
}

.left-editor-heading-icon {
  display: inline-flex;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-right: 10px;
  border: 1px solid rgba(var(--rgb-color), 0.06);
  border-radius: 11px;
}

.left-editor-heading-title {
  margin-top: 1px;
  font-size: 1rem;
  font-weight: 850;
  line-height: 1.2;
}

.editor-notes-panel,
.task-checklist-panel {
  background: rgba(var(--rgb-color), 0.018);
  border: 1px solid rgba(var(--rgb-color), 0.075);
  border-radius: 13px;
}

.editor-notes-panel {
  border-top-color: var(--editor-accent);
}

.editor-panel-heading {
  min-height: 38px;
  padding: 8px 12px;
  background: rgba(var(--rgb-color), 0.022);
  border-bottom: 1px solid rgba(var(--rgb-color), 0.055);
}

.editor-notes-input {
  padding: 0 4px !important;
}

.editor-notes-input >>> .v-input__slot {
  min-height: 92px !important;
  padding: 8px 10px !important;
}

.task-checklist-panel {
  min-height: 132px;
  padding: 10px;
}

.task-empty-state {
  display: flex;
  min-height: 112px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.task-empty-icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  background: rgba(var(--rgb-color), 0.035);
  border-radius: 14px;
}

.checklist-add-control >>> .v-input__slot {
  background: rgba(var(--rgb-color), 0.025) !important;
}

/* Termin-Cockpit */
.cockpit-fullscreen-card,
.cockpit-finder-expanded-card {
  height: 100vh !important;
  height: 100dvh !important;
  border-radius: 0 !important;
}

.cockpit-finder-body {
  min-height: 0;
  padding: 0 !important;
  overflow: hidden !important;
}

.cockpit-finder-row,
.cockpit-finder-column {
  min-height: 0;
}

.cockpit-finder-column {
  width: 100%;
  max-width: none;
  padding: 0 !important;
  overflow: hidden !important;
}

.finder-fullscreen-view {
  min-height: 0;
  background: var(--bg-color);
  animation: finder-view-enter 0.22s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes finder-view-enter {
  from {
    opacity: 0;
    transform: translateY(7px) scale(0.995);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.finder-fullscreen-toolbar {
  min-height: 58px;
  padding: 8px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid rgba(var(--rgb-color), 0.08);
  background: var(--actions-bg);
}

.finder-fullscreen-title,
.finder-fullscreen-actions {
  display: flex;
  align-items: center;
}

.finder-fullscreen-title {
  min-width: 0;
  gap: 10px;
}

.finder-fullscreen-title > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.finder-fullscreen-title strong {
  font-size: 0.96rem;
  line-height: 1.2;
}

.finder-fullscreen-title small {
  margin-top: 2px;
  overflow: hidden;
  color: rgba(var(--rgb-color), 0.52);
  font-size: 0.66rem;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.finder-fullscreen-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: linear-gradient(145deg, var(--v-primary-base), #14743b);
  box-shadow: 0 7px 18px rgba(0, 150, 63, 0.18);
}

.finder-fullscreen-actions {
  flex: 0 0 auto;
  gap: 4px;
}

.cockpit-details-shell {
  min-height: 0;
}

.cockpit-details-scroll {
  min-height: 0;
  padding-bottom: 10px;
}

.detail-section-eyebrow {
  font-size: 0.64rem;
  font-weight: 900;
  line-height: 1.2;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.detail-section-card {
  position: relative;
  padding: 12px;
  overflow: hidden;
  background: rgba(var(--rgb-color), 0.018);
  border: 1px solid rgba(var(--rgb-color), 0.075);
  border-radius: 13px;
}

.detail-section-card::before {
  position: absolute;
  top: 14px;
  left: 0;
  width: 3px;
  height: 22px;
  content: "";
  background: var(--section-accent);
  border-radius: 0 4px 4px 0;
}

.detail-section-heading {
  display: flex;
  align-items: center;
  gap: 9px;
}

.detail-section-icon {
  display: inline-flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
}

.detail-section-eyebrow {
  color: rgba(var(--rgb-color), 0.42);
}

.detail-section-title {
  margin-top: 1px;
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1.2;
}

.customer-assignment-list {
  display: grid;
  gap: 7px;
}

.customer-assignment-card {
  overflow: visible;
}

.customer-assignment-row {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 8px 9px;
  color: var(--text-color);
  text-align: left;
  cursor: pointer;
  background: rgba(var(--rgb-color), 0.025);
  border: 1px solid rgba(var(--rgb-color), 0.08);
  border-radius: 10px;
  transition: background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
}

.customer-assignment-row:hover {
  background: rgba(var(--rgb-color), 0.055);
  border-color: rgba(var(--rgb-color), 0.16);
  transform: translateY(-1px);
}

.customer-assignment-avatar {
  flex: 0 0 auto;
  margin-right: 9px;
  overflow: hidden;
  color: var(--text-color);
  background: rgba(var(--rgb-color), 0.08);
  border: 1px solid rgba(var(--rgb-color), 0.11);
}

.customer-assignment-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.customer-assignment-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.customer-assignment-copy small {
  margin-bottom: 1px;
  color: rgba(var(--rgb-color), 0.48);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
}

.customer-assignment-copy strong {
  overflow: hidden;
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-assignment-action {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  margin-left: 8px;
  color: var(--section-accent);
  font-size: 12px;
  font-weight: 800;
}

.customer-change-search {
  position: relative;
  z-index: 12;
  padding: 10px;
  background: rgba(var(--rgb-color), 0.035);
  border: 1px solid rgba(var(--rgb-color), 0.09);
  border-radius: 10px;
}

.customer-change-hint {
  color: rgba(var(--rgb-color), 0.56);
  font-size: 12px;
  line-height: 1.4;
}

.customer-change-pending-chip {
  flex: 0 0 auto;
  height: 22px !important;
  font-size: 12px !important;
  font-weight: 800;
}

.cockpit-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
}

.cockpit-field-cell {
  width: 100%;
  min-width: 0;
}

.cockpit-field-cell > * {
  width: 100%;
  min-width: 0;
}

.cockpit-control {
  width: 100%;
  min-width: 0;
}

.cockpit-control >>> .v-input__slot {
  min-height: 50px !important;
  margin-bottom: 0 !important;
  border-radius: 10px !important;
}

.cockpit-control >>> .v-text-field__details {
  display: none !important;
}

.cockpit-control >>> .v-label {
  font-size: 0.8rem;
}

.cockpit-control >>> input,
.cockpit-control >>> .v-select__selection {
  font-size: 0.84rem;
}

.template-select-control >>> .v-input__slot {
  min-height: 50px !important;
}

.template-select-control >>> .v-select__selections {
  min-width: 0;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

.template-option {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 5px 0;
}

.template-option-icon {
  display: inline-flex;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-right: 11px;
  border: 1px solid;
  border-radius: 11px;
}

.template-option--selection {
  padding: 0;
}

.template-option--selection .template-option-icon {
  flex-basis: 32px;
  width: 32px;
  height: 32px;
  margin-right: 9px;
  border-radius: 9px;
}

.template-option-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.template-option-title {
  overflow: hidden;
  color: var(--text-color);
  font-size: 0.83rem;
  font-weight: 800;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-option-meta {
  margin-top: 2px;
  overflow: hidden;
  color: rgba(var(--rgb-color), 0.48);
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.35px;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.finder-launch-button {
  min-height: 44px !important;
  border: 1px solid;
}

.finder-launch-button >>> .v-btn__content {
  min-width: 0;
}

.finder-launch-button span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trainer-warning {
  background: rgba(255, 152, 0, 0.12);
  border: 1px solid rgba(255, 152, 0, 0.25);
}

.trainer-warning-action {
  min-height: 44px !important;
}

.cockpit-notes-input >>> .v-input__slot {
  min-height: 88px !important;
  margin-bottom: 0 !important;
  background: rgba(var(--rgb-color), 0.02);
  border-radius: 10px !important;
}

.cockpit-notes-input >>> textarea {
  color: var(--text-color) !important;
  font-size: 0.84rem;
  line-height: 1.45;
}

.appointment-change-history {
  overflow: hidden;
  background: rgba(var(--rgb-color), 0.018);
  border: 1px solid rgba(var(--rgb-color), 0.075);
  border-top-color: var(--history-accent);
  border-radius: 13px;
}

.appointment-change-history__toggle {
  display: flex;
  width: 100%;
  min-height: 58px;
  align-items: center;
  padding: 9px 12px;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: background 0.15s ease;
}

.appointment-change-history__toggle:hover,
.appointment-change-history__toggle:focus-visible {
  background: rgba(var(--rgb-color), 0.045);
  outline: none;
}

.appointment-change-history__icon {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  margin-right: 9px;
  background: rgba(var(--rgb-color), 0.045);
  border-radius: 10px;
}

.appointment-change-history__copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  margin-right: 8px;
}

.appointment-change-history__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.appointment-change-history__title-row strong {
  font-size: 0.8rem;
  line-height: 1.2;
}

.appointment-change-history__count {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  color: white;
  font-size: 0.62rem;
  font-weight: 800;
  background: var(--history-accent);
  border-radius: 9px;
}

.appointment-change-history__latest {
  margin-top: 3px;
  overflow: hidden;
  color: rgba(var(--rgb-color), 0.5);
  font-size: 0.65rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.appointment-change-history__body {
  max-height: 330px;
  padding: 0 10px 10px;
  overflow-y: auto;
  border-top: 1px solid rgba(var(--rgb-color), 0.06);
}

.appointment-change-history__state {
  display: flex;
  min-height: 58px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(var(--rgb-color), 0.5);
  font-size: 0.7rem;
  text-align: center;
}

.appointment-change-history__state--error {
  color: var(--v-error-base);
}

.appointment-change-history__no-show {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 9px 0 4px;
  padding: 8px 9px;
  color: #d32f2f;
  font-size: 0.68rem;
  line-height: 1.35;
  background: rgba(229, 57, 53, 0.08);
  border: 1px solid rgba(229, 57, 53, 0.18);
  border-radius: 9px;
}

.appointment-change-history__list {
  padding-top: 4px;
}

.appointment-change-entry {
  display: flex;
  gap: 9px;
  padding: 9px 2px;
}

.appointment-change-entry + .appointment-change-entry {
  border-top: 1px solid rgba(var(--rgb-color), 0.055);
}

.appointment-change-entry__marker {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
}

.appointment-change-entry__content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.appointment-change-entry__headline {
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.3;
}

.appointment-change-entry__meta {
  margin-top: 2px;
  color: rgba(var(--rgb-color), 0.46);
  font-size: 0.61rem;
  line-height: 1.3;
}

.appointment-change-entry__detail {
  margin-top: 5px;
  padding-left: 8px;
  color: rgba(var(--rgb-color), 0.68);
  font-size: 0.66rem;
  line-height: 1.38;
  white-space: pre-wrap;
  border-left: 2px solid rgba(var(--rgb-color), 0.1);
}

.color-palette-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 46px;
  padding: 6px 10px;
  color: var(--text-color);
  cursor: pointer;
  background: rgba(var(--rgb-color), 0.035);
  border: 1px solid rgba(var(--rgb-color), 0.09);
  border-radius: 10px;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.color-palette-toggle:hover,
.color-palette-toggle:focus-visible {
  background: rgba(var(--rgb-color), 0.065);
  border-color: rgba(var(--rgb-color), 0.18);
  outline: none;
}

.color-palette-current {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  margin-right: 10px;
  border: 3px solid var(--bg-color);
  border-radius: 10px;
  box-shadow: 0 0 0 1px rgba(var(--rgb-color), 0.22), 0 4px 10px rgba(0, 0, 0, 0.15);
}

.color-palette-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  text-align: left;
}

.color-palette-copy strong {
  font-size: 0.78rem;
  line-height: 1.2;
}

.color-palette-copy small {
  margin-top: 2px;
  color: rgba(var(--rgb-color), 0.48);
  font-size: 0.66rem;
  font-weight: 600;
}

.color-palette-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
  gap: 2px;
  padding: 8px;
  background: rgba(var(--rgb-color), 0.025);
  border: 1px solid rgba(var(--rgb-color), 0.07);
  border-radius: 12px;
}

.color-swatch {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  min-width: 44px;
  height: 44px;
  padding: 5px;
  margin: auto;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 12px;
  transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.color-swatch:hover,
.color-swatch:focus-visible {
  background: rgba(var(--rgb-color), 0.07);
  outline: none;
  transform: translateY(-1px);
}

.color-swatch.active {
  background: rgba(var(--rgb-color), 0.07);
  border-color: rgba(var(--rgb-color), 0.22);
}

.color-swatch-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 9px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.18);
}

.cockpit-inline-notes {
  flex: 0 0 auto;
}

.cockpit-footer {
  min-height: 62px;
  border-top: 1px solid rgba(var(--rgb-color), 0.07);
  box-shadow: 0 -8px 22px rgba(0, 0, 0, 0.055);
  backdrop-filter: blur(18px);
}

.cockpit-delete-button,
.cockpit-cancel-button {
  min-height: 40px !important;
}

.cockpit-save-button {
  min-width: 168px !important;
  min-height: 44px !important;
  letter-spacing: 0.4px;
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.18) !important;
}

@media (max-width: 959px) {
  .standard-appointment-hub {
    padding: 18px;
  }

  .standard-appointment-action {
    min-height: 188px;
  }

  .appointment-header-main {
    min-height: 84px;
    padding-right: 16px;
    padding-left: 16px;
  }

  .appointment-header-tabs,
  .appointment-customer-switch {
    padding-right: 16px;
    padding-left: 16px;
  }

  .cockpit-details-column {
    min-height: 0;
  }

  .cockpit-details-shell {
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }

  .detail-section-card {
    padding: 12px;
  }

  .customer-quickfact {
    justify-content: center;
    width: 36px;
    padding: 0;
  }

  .customer-quickfact-value {
    display: none;
  }
}

@media (max-width: 760px) {
  .standard-appointment-actions {
    grid-template-columns: minmax(0, 1fr);
  }

  .standard-appointment-action {
    min-height: 170px;
  }
}

@media (max-width: 599px) {
  .standard-appointment-hub {
    padding: 16px;
    border-radius: 18px;
  }

  .standard-appointment-hub::before {
    right: 16px;
    left: 16px;
  }

  .standard-appointment-hub__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .standard-appointment-hub__badge {
    margin-left: 59px;
  }

  .standard-appointment-action {
    min-height: 164px;
    padding: 14px;
  }

  .standard-appointment-action__copy {
    padding-top: 13px;
    padding-bottom: 12px;
  }

  .finder-fullscreen-toolbar {
    min-height: 54px;
    padding: 7px 10px;
  }

  .finder-fullscreen-title small {
    max-width: 48vw;
  }

  .finder-fullscreen-actions .v-btn:first-child {
    min-width: 38px !important;
    padding: 0 7px !important;
    font-size: 0;
  }

  .finder-fullscreen-actions .v-btn:first-child .v-icon {
    margin-right: 0 !important;
  }

  .appointment-header-main,
  .appointment-header--customer .appointment-header-main {
    min-height: 78px;
    padding: 9px 12px;
  }

  .appointment-header-icon {
    margin-right: 9px !important;
    border-radius: 12px !important;
  }

  .customer-avatar-trigger {
    width: 50px;
    height: 50px;
    margin-right: 9px !important;
  }

  .customer-avatar-type-badge {
    width: 20px;
    height: 20px;
  }

  .customer-image-preview-card {
    width: 246px;
    max-width: 86vw;
  }

  .customer-image-preview-empty {
    width: 244px;
    max-width: calc(86vw - 2px);
    height: 210px;
  }

  .appointment-header-title {
    font-size: 1.14rem;
    letter-spacing: -0.15px;
  }

  .appointment-header-kicker {
    font-size: 0.56rem;
    letter-spacing: 1px;
  }

  .appointment-header-meta {
    gap: 4px 8px;
    margin-top: 4px;
  }

  .appointment-header-meta-item {
    font-size: 0.62rem;
  }

  .appointment-header--customer .appointment-header-meta-item:nth-child(n + 2) {
    display: none !important;
  }

  .appointment-header-quickfacts {
    display: none !important;
  }

  .appointment-header-quickfacts + .appointment-header-actions {
    padding-left: 0;
    border-left: 0;
  }

  .appointment-header-action {
    width: 34px !important;
    min-width: 34px !important;
    height: 34px !important;
  }

  .appointment-header-action--desktop {
    display: none !important;
  }

  .appointment-header-tabs,
  .appointment-customer-switch {
    padding-right: 12px;
    padding-left: 12px;
  }

  .appointment-header-tabs >>> .v-tab {
    min-width: 102px;
    padding: 0 8px;
    font-size: 0.7rem !important;
  }

  .cockpit-field-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .cockpit-footer {
    min-height: 60px;
  }

  .cockpit-save-button {
    min-width: 132px !important;
  }

  .finder-launch-button span {
    max-width: 230px;
  }

  .trainer-warning {
    align-items: flex-start !important;
    flex-wrap: wrap;
  }

  .trainer-warning-action {
    width: 100%;
    margin-top: 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .standard-appointment-action {
    transition: none;
  }

  .standard-appointment-action:not(:disabled):hover,
  .standard-appointment-action:not(:disabled):focus-visible {
    transform: none;
  }
}
</style>

<style>
/* Der Finder bleibt Bestandteil desselben Edit-Dialogs. Auf Desktop wird nur
   dessen vorhandene Dialogfläche weich bis an den Viewport erweitert. */
.v-dialog.work-calendar-edit-dialog,
.work-calendar-edit-dialog > .v-dialog {
  height: 90vh;
  max-height: 90vh;
  overflow: hidden;
  transition: width 0.28s cubic-bezier(0.22, 1, 0.36, 1), max-width 0.28s cubic-bezier(0.22, 1, 0.36, 1), height 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    max-height 0.28s cubic-bezier(0.22, 1, 0.36, 1), margin 0.28s cubic-bezier(0.22, 1, 0.36, 1), border-radius 0.22s ease;
  will-change: width, max-width, height, max-height, margin;
}

.v-dialog.work-calendar-edit-dialog.work-calendar-edit-dialog--finder,
.v-dialog.work-calendar-edit-dialog.v-dialog--fullscreen,
.work-calendar-edit-dialog--finder > .v-dialog,
.work-calendar-edit-dialog > .v-dialog.v-dialog--fullscreen {
  width: 100vw !important;
  max-width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

@media (prefers-reduced-motion: reduce) {
  .v-dialog.work-calendar-edit-dialog,
  .work-calendar-edit-dialog > .v-dialog,
  .work-calendar-edit-dialog .glass-card-theme,
  .work-calendar-edit-dialog .finder-fullscreen-view {
    transition: none !important;
    animation: none !important;
  }
}
</style>
